/**
 * @license
 * @qwik.dev/core 2.0.0-alpha.8-dev+2d23222
 * Copyright QwikDev. All Rights Reserved.
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/QwikDev/qwik/blob/main/LICENSE
 */
import { isDev, isServer } from "@qwik.dev/core/build";

export { isBrowser, isDev, isServer } from "@qwik.dev/core/build";

const qDev = !1;

const qInspector = !1;

const qSerialize = !0;

const qDynamicPlatform = !0;

const qTest = !1;

const qRuntimeQrl = !1;

const seal = () => {
    qDev;
};

const STYLE = "";

const logError = (message, ...optionalParams) => createAndLogError(!1, message, ...optionalParams);

const throwErrorAndStop = (message, ...optionalParams) => {
    throw createAndLogError(!1, message, ...optionalParams);
};

const logErrorAndStop = (message, ...optionalParams) => createAndLogError(!0, message, ...optionalParams);

const _printed = /*#__PURE__*/ new Set;

const logOnceWarn = () => {
    qDev;
};

const logWarn = () => {
    qDev;
};

const createAndLogError = (asyncThrow, message, ...optionalParams) => {
    const err = message instanceof Error ? message : new Error(message);
    return console.error("%cQWIK ERROR", "", err.message, ...optionalParams, err.stack), 
    asyncThrow && setTimeout((() => {
        throw err;
    }), 0), err;
};

const ASSERT_DISCLAIMER = "Internal assert, this is likely caused by a bug in Qwik: ";

function assertDefined() {
    qDev;
}

function assertEqual() {
    qDev;
}

function assertTrue() {
    qDev;
}

function assertFalse() {
    qDev;
}

function assertNumber() {
    qDev;
}

const codeToText = code => `Code(Q${code}) https://github.com/QwikDev/qwik/blob/main/packages/qwik/src/core/error/error.ts#L${8 + code}`;

const qError = (code, errorMessageArgs = []) => {
    const text = codeToText(code, ...errorMessageArgs);
    return logErrorAndStop(text, ...errorMessageArgs);
};

const SYNC_QRL = "<sync>";

const isSyncQrl = value => isQrl$1(value) && "<sync>" == value.$symbol$;

const isQrl$1 = value => "function" == typeof value && "function" == typeof value.getSymbol;

function assertQrl(qrl) {
    if (isDev && !isQrl$1(qrl)) {
        throw new Error("Not a QRL");
    }
}

const getSymbolHash = symbolName => {
    const index = symbolName.lastIndexOf("_");
    return index > -1 ? symbolName.slice(index + 1) : symbolName;
};

const OnRenderProp = "q:renderFn";

const ComponentStylesPrefixContent = "⚡️";

const QSlot = "q:slot";

const QSlotParent = "q:sparent";

const QSlotS = "q:s";

const QStyle = "q:style";

const QStyleSelector = "style[q\\:style]";

const QStyleSSelector = "style[q\\:sstyle]";

const QStylesAllSelector = "style[q\\:style],style[q\\:sstyle]";

const QScopedStyle = "q:sstyle";

const QCtxAttr = "q:ctx";

const QBackRefs = "q:brefs";

const QFuncsPrefix = "qFuncs_";

const getQFuncs = (document, hash) => document["qFuncs_" + hash] || [];

const QBaseAttr = "q:base";

const QLocaleAttr = "q:locale";

const QManifestHashAttr = "q:manifest-hash";

const QInstanceAttr = "q:instance";

const QContainerIsland = "q:container-island";

const QContainerIslandEnd = "/" + QContainerIsland;

const QIgnore = "q:ignore";

const QIgnoreEnd = "/q:ignore";

const QContainerAttr = "q:container";

const QContainerAttrEnd = "/q:container";

const QTemplate = "q:template";

const QContainerSelector = "[q\\:container]:not([q\\:container=html]):not([q\\:container=text])";

const HTML_NS = "http://www.w3.org/1999/xhtml";

const SVG_NS = "http://www.w3.org/2000/svg";

const MATH_NS = "http://www.w3.org/1998/Math/MathML";

const ResourceEvent = "qResource";

const RenderEvent = "qRender";

const TaskEvent = "qTask";

const QDefaultSlot = "";

const ELEMENT_ID = "q:id";

const ELEMENT_KEY = "q:key";

const ELEMENT_PROPS = "q:props";

const ELEMENT_SEQ = "q:seq";

const ELEMENT_SEQ_IDX = "q:seqIdx";

const Q_PREFIX = "q:";

const NON_SERIALIZABLE_MARKER_PREFIX = ":";

const USE_ON_LOCAL = ":on";

const USE_ON_LOCAL_SEQ_IDX = ":onIdx";

const USE_ON_LOCAL_FLAGS = ":onFlags";

const FLUSH_COMMENT = "qkssr-f";

const STREAM_BLOCK_START_COMMENT = "qkssr-pu";

const STREAM_BLOCK_END_COMMENT = "qkssr-po";

const Q_PROPS_SEPARATOR = ":";

const dangerouslySetInnerHTML = "dangerouslySetInnerHTML";

const qwikInspectorAttr = "data-qwik-inspector";

const createPlatform = () => ({
    isServer,
    importSymbol(containerEl, url, symbolName) {
        if (isServer) {
            const hash = getSymbolHash(symbolName);
            const regSym = globalThis.__qwik_reg_symbols?.get(hash);
            if (regSym) {
                return regSym;
            }
        }
        if (!url) {
            throw qError(31, [ symbolName ]);
        }
        if (!containerEl) {
            throw qError(30, [ url, symbolName ]);
        }
        const urlDoc = toUrl(containerEl.ownerDocument, containerEl, url).toString();
        const urlCopy = new URL(urlDoc);
        urlCopy.hash = "";
        return import(urlCopy.href).then((mod => mod[symbolName]));
    },
    raf: fn => new Promise((resolve => {
        requestAnimationFrame((() => {
            resolve(fn());
        }));
    })),
    nextTick: fn => new Promise((resolve => {
        setTimeout((() => {
            resolve(fn());
        }));
    })),
    chunkForSymbol: (symbolName, chunk) => [ symbolName, chunk ?? "_" ]
});

const toUrl = (doc, containerEl, url) => {
    const baseURI = doc.baseURI;
    const base = new URL(containerEl.getAttribute("q:base") ?? baseURI, baseURI);
    return new URL(url, base);
};

let _platform = /*#__PURE__ */ createPlatform();

const setPlatform = plt => _platform = plt;

const getPlatform = () => _platform;

const isServerPlatform = () => _platform.isServer;

const isNode = value => value && "number" == typeof value.nodeType;

const isDocument = value => 9 === value.nodeType;

const isElement$1 = value => 1 === value.nodeType;

const MAX_RETRY_ON_PROMISE_COUNT = 100;

const isPromise = value => !!value && "object" == typeof value && "function" == typeof value.then;

const safeCall = (call, thenFn, rejectFn) => {
    try {
        const result = call();
        return isPromise(result) ? result.then(thenFn, rejectFn) : thenFn(result);
    } catch (e) {
        return rejectFn(e);
    }
};

const maybeThen = (valueOrPromise, thenFn) => isPromise(valueOrPromise) ? valueOrPromise.then(thenFn, shouldNotError) : thenFn(valueOrPromise);

const shouldNotError = reason => {
    throwErrorAndStop(reason);
};

const delay = timeout => new Promise((resolve => {
    setTimeout(resolve, timeout);
}));

function retryOnPromise(fn, retryCount = 0) {
    const retryOrThrow = e => {
        if (isPromise(e) && retryCount < 100) {
            return e.then(retryOnPromise.bind(null, fn, retryCount++));
        }
        throw e;
    };
    try {
        const result = fn();
        return isPromise(result) ? result.catch((e => retryOrThrow(e))) : result;
    } catch (e) {
        return retryOrThrow(e);
    }
}

const isSerializableObject = v => {
    const proto = Object.getPrototypeOf(v);
    return proto === Object.prototype || proto === Array.prototype || null === proto;
};

const isObject = v => !!v && "object" == typeof v;

const isArray = v => Array.isArray(v);

const isString = v => "string" == typeof v;

const isFunction = v => "function" == typeof v;

const isQrl = value => "function" == typeof value && "function" == typeof value.getSymbol;

const EMPTY_ARRAY = [];

const EMPTY_OBJ = {};

Object.freeze(EMPTY_ARRAY), Object.freeze(EMPTY_OBJ);

const EXTRACT_IMPORT_PATH = /\(\s*(['"])([^\1]+)\1\s*\)/;

const EXTRACT_SELF_IMPORT = /Promise\s*\.\s*resolve/;

const EXTRACT_FILE_NAME = /[\\/(]([\w\d.\-_]+\.(js|ts)x?):/;

const announcedQRL = /*#__PURE__*/ new Set;

const qrl = (chunkOrFn, symbol, lexicalScopeCapture = EMPTY_ARRAY, stackOffset = 0) => {
    let chunk = null;
    let symbolFn = null;
    if (isFunction(chunkOrFn)) {
        symbolFn = chunkOrFn;
        {
            let match;
            const srcCode = String(chunkOrFn);
            if ((match = srcCode.match(EXTRACT_IMPORT_PATH)) && match[2]) {
                chunk = match[2];
            } else {
                if (!(match = srcCode.match(EXTRACT_SELF_IMPORT))) {
                    throw qError(11, [ srcCode ]);
                }
                {
                    const ref = "QWIK-SELF";
                    const frames = new Error(ref).stack.split("\n");
                    const start = frames.findIndex((f => f.includes(ref)));
                    match = frames[start + 2 + stackOffset].match(EXTRACT_FILE_NAME), chunk = match ? match[1] : "main";
                }
            }
        }
    } else {
        if (!isString(chunkOrFn)) {
            throw qError(12, [ chunkOrFn ]);
        }
        chunk = chunkOrFn;
    }
    return announcedQRL.has(symbol) || (announcedQRL.add(symbol), emitEvent("qprefetch", {
        symbols: [ symbol ],
        bundles: chunk && [ chunk ]
    })), createQRL(chunk, symbol, null, symbolFn, null, lexicalScopeCapture);
};

const inlinedQrl = (symbol, symbolName, lexicalScopeCapture = EMPTY_ARRAY) => createQRL(null, symbolName, symbol, null, null, lexicalScopeCapture);

const _noopQrl = (symbolName, lexicalScopeCapture = EMPTY_ARRAY) => createQRL(null, symbolName, null, null, null, lexicalScopeCapture);

const _noopQrlDEV = (symbolName, opts, lexicalScopeCapture = EMPTY_ARRAY) => {
    const newQrl = _noopQrl(symbolName, lexicalScopeCapture);
    return newQrl.dev = opts, newQrl;
};

const qrlDEV = (chunkOrFn, symbol, opts, lexicalScopeCapture = EMPTY_ARRAY) => {
    const newQrl = qrl(chunkOrFn, symbol, lexicalScopeCapture, 1);
    return newQrl.dev = opts, newQrl;
};

const inlinedQrlDEV = (symbol, symbolName, opts, lexicalScopeCapture = EMPTY_ARRAY) => {
    const qrl = inlinedQrl(symbol, symbolName, lexicalScopeCapture);
    return qrl.dev = opts, qrl;
};

const _regSymbol = (symbol, hash) => (void 0 === globalThis.__qwik_reg_symbols && (globalThis.__qwik_reg_symbols = new Map), 
globalThis.__qwik_reg_symbols.set(hash, symbol), symbol);

let _locale;

function getLocale(defaultLocale) {
    if (void 0 === _locale) {
        const ctx = tryGetInvokeContext();
        if (ctx && ctx.$locale$) {
            return ctx.$locale$;
        }
        if (void 0 !== defaultLocale) {
            return defaultLocale;
        }
        throw new Error("Reading `locale` outside of context.");
    }
    return _locale;
}

function withLocale(locale, fn) {
    const previousLang = _locale;
    try {
        return _locale = locale, fn();
    } finally {
        _locale = previousLang;
    }
}

function setLocale(locale) {
    _locale = locale;
}

const useSequentialScope = () => {
    const iCtx = useInvokeContext();
    const host = iCtx.$hostElement$;
    let seq = iCtx.$container$.getHostProp(host, "q:seq");
    null === seq && (seq = [], iCtx.$container$.setHostProp(host, "q:seq", seq));
    let seqIdx = iCtx.$container$.getHostProp(host, "q:seqIdx");
    for (null === seqIdx && (seqIdx = 0), iCtx.$container$.setHostProp(host, "q:seqIdx", seqIdx + 1); seq.length <= seqIdx; ) {
        seq.push(void 0);
    }
    return {
        val: seq[seqIdx],
        set: value => seq[seqIdx] = value,
        i: seqIdx,
        iCtx
    };
};

const EVENT_SUFFIX = "$";

const DOMContentLoadedEvent = "DOMContentLoaded";

const isJsxPropertyAnEventName = name => (name.startsWith("on") || name.startsWith("window:on") || name.startsWith("document:on")) && name.endsWith("$");

const isHtmlAttributeAnEventName = name => name.startsWith("on:") || name.startsWith("on-window:") || name.startsWith("on-document:");

function jsxEventToHtmlAttribute(jsxEvent) {
    if (jsxEvent.endsWith("$")) {
        const [prefix, idx] = getEventScopeDataFromJsxEvent(jsxEvent);
        if (-1 !== idx) {
            const eventName = getEventNameFromJsxEvent(jsxEvent);
            return prefix + fromCamelToKebabCase(eventName);
        }
    }
    return null;
}

function eventNameToJsxEvent(eventName, prefix) {
    return prefix + (eventName = eventName.charAt(0).toUpperCase() + eventName.substring(1)) + "$";
}

function getEventNameFromJsxEvent(jsxEvent) {
    if (jsxEvent.endsWith("$")) {
        const [, idx] = getEventScopeDataFromJsxEvent(jsxEvent);
        if (-1 != idx) {
            return jsxEventToEventName(jsxEvent, idx);
        }
    }
    return null;
}

function jsxEventToEventName(jsxEvent, startIdx = 0) {
    let lastIdx = startIdx;
    const isCaseSensitive = isDash(jsxEvent.charCodeAt(startIdx));
    isCaseSensitive && lastIdx++;
    let eventName = "";
    const chunk = jsxEvent.substring(lastIdx, jsxEvent.length - 1);
    return "DOMContentLoaded" === chunk ? "DOMContentLoaded" : (eventName += isCaseSensitive ? chunk : chunk.toLowerCase(), 
    eventName);
}

function getEventScopeDataFromJsxEvent(eventName) {
    let prefix = null;
    let idx = -1;
    return eventName.startsWith("on") ? (prefix = "on:", idx = 2) : eventName.startsWith("window:on") ? (prefix = "on-window:", 
    idx = 9) : eventName.startsWith("document:on") && (prefix = "on-document:", idx = 11), 
    [ prefix, idx ];
}

const isDash = charCode => 45 === charCode;

const getEventNameScopeFromJsxEvent = name => {
    const index = name.indexOf(":");
    return -1 !== index ? name.substring(0, index) : "";
};

function isPreventDefault(key) {
    return key.startsWith("preventdefault:");
}

const fromCamelToKebabCase = text => text.replace(/([A-Z-])/g, "-$1").toLowerCase();

const createContextId = name => (assertTrue(/^[\w/.-]+$/.test(name), "Context name must only contain A-Z,a-z,0-9, _", name), 
/*#__PURE__*/ Object.freeze({
    id: fromCamelToKebabCase(name)
}));

const useContextProvider = (context, newValue) => {
    const {val, set, iCtx} = useSequentialScope();
    void 0 === val && (iCtx.$container$.setContext(iCtx.$hostElement$, context, newValue), 
    set(1));
};

const useContext = (context, defaultValue) => {
    const {val, set, iCtx} = useSequentialScope();
    if (void 0 !== val) {
        return val;
    }
    const value = iCtx.$container$.resolveContext(iCtx.$hostElement$, context);
    if ("function" == typeof defaultValue) {
        return set(invoke(void 0, defaultValue, value));
    }
    if (void 0 !== value) {
        return set(value);
    }
    if (void 0 !== defaultValue) {
        return set(defaultValue);
    }
    throw qError(13, [ context.id ]);
};

const validateContext = context => {
    if (!isObject(context) || "string" != typeof context.id || 0 === context.id.length) {
        throw qError(28, [ context ]);
    }
};

const ERROR_CONTEXT = /*#__PURE__*/ createContextId("qk-error");

const isRecoverable = err => !(err && err instanceof Error && "plugin" in err);

const version = "2.0.0-alpha.8-dev+2d23222";

const Slot = props => _jsxSorted(Virtual, null, {
    [QSlotS]: ""
}, props.children, 0, props.name ?? "");

const SkipRender = Symbol("skip render");

const SSRRaw = () => null;

const SSRComment = () => null;

const SSRStreamBlock = props => [ jsx(SSRComment, {
    data: "qkssr-pu"
}), props.children, jsx(SSRComment, {
    data: "qkssr-po"
}) ];

const SSRStream = (props, key) => jsx(RenderOnce, {
    children: jsx(InternalSSRStream, props)
}, key);

const InternalSSRStream = () => null;

const NEEDS_COMPUTATION = Symbol("invalid");

const _EFFECT_BACK_REF = Symbol("backRef");

function getSubscriber(effect, prop, data) {
    effect[_EFFECT_BACK_REF] || (isServer && isSsrNode(effect) ? effect.setProp("q:brefs", new Map) : effect[_EFFECT_BACK_REF] = new Map);
    const subMap = effect[_EFFECT_BACK_REF];
    let sub = subMap.get(prop);
    return sub || (sub = [ effect, prop ], subMap.set(prop, sub)), data && (sub[3] = data), 
    sub;
}

function isSsrNode(value) {
    return "__brand__" in value && "currentComponentNode" in value;
}

const DEBUG$1 = !1;

const log = (...args) => console.log("SIGNAL", ...args.map(qwikDebugToString));

const throwIfQRLNotResolved = qrl => {
    if (!qrl.resolved) {
        throw qrl.resolve();
    }
};

const isSignal = value => value instanceof SignalImpl;

class SubscriptionData {
    data;
    constructor(data) {
        this.data = data;
    }
}

class SignalImpl {
    $untrackedValue$;
    $effects$=null;
    $container$=null;
    constructor(container, value) {
        this.$container$ = container, this.$untrackedValue$ = value;
    }
    get untrackedValue() {
        return this.$untrackedValue$;
    }
    set untrackedValue(value) {
        this.$untrackedValue$ = value;
    }
    get value() {
        const ctx = tryGetInvokeContext();
        if (ctx) {
            if (null === this.$container$) {
                if (!ctx.$container$) {
                    return this.untrackedValue;
                }
                this.$container$ = ctx.$container$;
            } else {
                assertTrue(!ctx.$container$ || ctx.$container$ === this.$container$, "Do not use signals across containers");
            }
            const effectSubscriber = ctx.$effectSubscriber$;
            if (effectSubscriber) {
                const effects = this.$effects$ ||= new Set;
                ensureContainsSubscription(effects, effectSubscriber), ensureContainsBackRef(effectSubscriber, this), 
                addQrlToSerializationCtx(effectSubscriber, this.$container$);
            }
        }
        return this.untrackedValue;
    }
    set value(value) {
        value !== this.$untrackedValue$ && (this.$untrackedValue$ = value, triggerEffects(this.$container$, this, this.$effects$));
    }
    valueOf() {
        qDev;
    }
    toString() {
        return isDev ? `[${this.constructor.name}${1 & this.$flags$ ? " INVALID" : ""} ${String(this.$untrackedValue$)}]` + (Array.from(this.$effects$ || []).map((e => "\n -> " + pad(qwikDebugToString(e[0]), "    "))).join("\n") || "") : this.constructor.name;
    }
    toJSON() {
        return {
            value: this.$untrackedValue$
        };
    }
}

const ensureContainsSubscription = (array, effectSubscription) => {
    array.add(effectSubscription);
};

const ensureContainsBackRef = (array, value) => {
    array[2] ||= new Set, array[2].add(value);
};

const addQrlToSerializationCtx = (effectSubscriber, container) => {
    if (container && !isDomContainer(container)) {
        const effect = effectSubscriber[0];
        const property = effectSubscriber[1];
        let qrl = null;
        isTask(effect) ? qrl = effect.$qrl$ : effect instanceof ComputedSignalImpl ? qrl = effect.$computeQrl$ : ":" === property && (qrl = container.getHostProp(effect, "q:renderFn")), 
        qrl && container.serializationCtx.$eventQrls$.add(qrl);
    }
};

const triggerEffects = (container, signal, effects) => {
    const isBrowser = isDomContainer(container);
    if (effects) {
        const scheduleEffect = effectSubscription => {
            const consumer = effectSubscription[0];
            const property = effectSubscription[1];
            if (assertDefined(container, "Container must be defined."), isTask(consumer)) {
                consumer.$flags$ |= 8;
                let choreType = 3;
                1 & consumer.$flags$ && (choreType = 32), container.$scheduler$(choreType, consumer);
            } else if (consumer instanceof SignalImpl) {
                consumer instanceof ComputedSignalImpl && (consumer.$computeQrl$.resolved || container.$scheduler$(1, null, consumer.$computeQrl$)), 
                consumer.$invalidate$();
            } else if (":" === property) {
                const host = consumer;
                const qrl = container.getHostProp(host, "q:renderFn");
                assertDefined(qrl, "Component must have QRL");
                const props = container.getHostProp(host, "q:props");
                container.$scheduler$(6, host, qrl, props);
            } else if (isBrowser) {
                if ("." === property) {
                    container.$scheduler$(4, consumer, consumer, signal);
                } else {
                    const effectData = effectSubscription[3];
                    if (effectData instanceof SubscriptionData) {
                        const payload = {
                            ...effectData.data,
                            $value$: signal
                        };
                        container.$scheduler$(5, consumer, property, payload);
                    }
                }
            }
        };
        for (const effect of effects) {
            scheduleEffect(effect);
        }
    }
};

class ComputedSignalImpl extends SignalImpl {
    $computeQrl$;
    $flags$;
    $forceRunEffects$=!1;
    [_EFFECT_BACK_REF]=null;
    constructor(container, fn, flags = 1) {
        super(container, NEEDS_COMPUTATION), this.$computeQrl$ = fn, this.$flags$ = flags;
    }
    $invalidate$() {
        this.$flags$ |= 1, this.$forceRunEffects$ = !1, this.$container$?.$scheduler$(7, null, this);
    }
    force() {
        this.$forceRunEffects$ = !0, this.$container$?.$scheduler$(7, null, this);
    }
    get untrackedValue() {
        const didChange = this.$computeIfNeeded$();
        return didChange && (this.$forceRunEffects$ = didChange), assertFalse(this.$untrackedValue$ === NEEDS_COMPUTATION, "Invalid state"), 
        this.$untrackedValue$;
    }
    $computeIfNeeded$() {
        if (!(1 & this.$flags$)) {
            return !1;
        }
        const computeQrl = this.$computeQrl$;
        throwIfQRLNotResolved(computeQrl);
        const ctx = tryGetInvokeContext();
        const previousEffectSubscription = ctx?.$effectSubscriber$;
        ctx && (ctx.$effectSubscriber$ = getSubscriber(this, "."));
        try {
            const untrackedValue = computeQrl.getFn(ctx)();
            if (isPromise(untrackedValue)) {
                throw qError(46, [ computeQrl.dev ? computeQrl.dev.file : "", computeQrl.$hash$ ]);
            }
            this.$flags$ &= -2;
            const didChange = untrackedValue !== this.$untrackedValue$;
            return didChange && (this.$untrackedValue$ = untrackedValue), didChange;
        } finally {
            ctx && (ctx.$effectSubscriber$ = previousEffectSubscription);
        }
    }
    set value(_) {
        throw qError(47);
    }
    get value() {
        return super.value;
    }
}

class WrappedSignal extends SignalImpl {
    $args$;
    $func$;
    $funcStr$;
    $flags$;
    $hostElement$=null;
    $forceRunEffects$=!1;
    [_EFFECT_BACK_REF]=null;
    constructor(container, fn, args, fnStr, flags = 3) {
        super(container, NEEDS_COMPUTATION), this.$args$ = args, this.$func$ = fn, this.$funcStr$ = fnStr, 
        this.$flags$ = flags;
    }
    $invalidate$() {
        this.$flags$ |= 1, this.$forceRunEffects$ = !1, this.$container$?.$scheduler$(7, this.$hostElement$, this);
    }
    force() {
        this.$flags$ |= 1, this.$forceRunEffects$ = !1, triggerEffects(this.$container$, this, this.$effects$);
    }
    get untrackedValue() {
        const didChange = this.$computeIfNeeded$();
        return didChange && (this.$forceRunEffects$ = didChange), assertFalse(this.$untrackedValue$ === NEEDS_COMPUTATION, "Invalid state"), 
        this.$untrackedValue$;
    }
    $computeIfNeeded$() {
        if (!(1 & this.$flags$)) {
            return !1;
        }
        const untrackedValue = trackSignal((() => this.$func$(...this.$args$)), this, ".", this.$container$);
        const didChange = untrackedValue !== this.$untrackedValue$;
        return didChange && (this.$untrackedValue$ = untrackedValue), didChange;
    }
    set value(_) {
        throw qError(48);
    }
    get value() {
        return super.value;
    }
}

class SerializerSignalImpl extends ComputedSignalImpl {
    constructor(container, argQrl) {
        super(container, argQrl);
    }
    $didInitialize$=!1;
    $computeIfNeeded$() {
        if (!(1 & this.$flags$)) {
            return !1;
        }
        throwIfQRLNotResolved(this.$computeQrl$);
        let arg = this.$computeQrl$.resolved;
        "function" == typeof arg && (arg = arg());
        const {deserialize, initial} = arg;
        const update = arg.update;
        const currentValue = this.$untrackedValue$ === NEEDS_COMPUTATION ? initial : this.$untrackedValue$;
        const untrackedValue = trackSignal((() => this.$didInitialize$ ? update?.(currentValue) : deserialize(currentValue)), this, ".", this.$container$);
        const didChange = this.$didInitialize$ && "undefined" !== untrackedValue || untrackedValue !== this.$untrackedValue$;
        return this.$flags$ &= -2, this.$didInitialize$ = !0, didChange && (this.$untrackedValue$ = untrackedValue), 
        didChange;
    }
}

const isSerializerObj = obj => "object" == typeof obj && null !== obj && "function" == typeof obj[SerializerSymbol];

const STORE_TARGET = Symbol("store.target");

const STORE_HANDLER = Symbol("store.handler");

const STORE_ALL_PROPS = Symbol("store.all");

const getStoreHandler = value => value[STORE_HANDLER];

const getStoreTarget = value => value?.[STORE_TARGET] || null;

const unwrapStore = value => getStoreTarget(value) || value;

const isStore = value => STORE_TARGET in value;

function createStore(container, obj, flags) {
    return new Proxy(obj, new StoreHandler(flags, container || null));
}

const getOrCreateStore = (obj, flags, container) => {
    if (isSerializableObject(obj) && container) {
        let store = container.$storeProxyMap$.get(obj);
        return store || (store = createStore(container, obj, flags), container.$storeProxyMap$.set(obj, store)), 
        store;
    }
    return obj;
};

class StoreHandler {
    $flags$;
    $container$;
    $effects$=null;
    constructor($flags$, $container$) {
        this.$flags$ = $flags$, this.$container$ = $container$;
    }
    toString() {
        return "[Store]";
    }
    get(target, prop) {
        if ("symbol" == typeof prop) {
            return prop === STORE_TARGET ? target : prop === STORE_HANDLER ? this : target[prop];
        }
        const ctx = tryGetInvokeContext();
        const value = target[prop];
        if (ctx) {
            if (null === this.$container$) {
                if (!ctx.$container$) {
                    return value;
                }
                this.$container$ = ctx.$container$;
            } else {
                assertTrue(!ctx.$container$ || ctx.$container$ === this.$container$, "Do not use signals across containers");
            }
            const effectSubscriber = ctx.$effectSubscriber$;
            effectSubscriber && addStoreEffect(target, Array.isArray(target) ? STORE_ALL_PROPS : prop, this, effectSubscriber);
        }
        if ("toString" === prop && value === Object.prototype.toString) {
            return this.toString;
        }
        return 1 & this.$flags$ && "object" == typeof value && null !== value && !Object.isFrozen(value) && !isStore(value) && !Object.isFrozen(target) ? getOrCreateStore(value, this.$flags$, this.$container$) : value;
    }
    set(target, prop, value) {
        if ("symbol" == typeof prop) {
            return target[prop] = value, !0;
        }
        const newValue = 1 & this.$flags$ ? unwrapStore(value) : value;
        if (prop in target) {
            newValue !== target[prop] && setNewValueAndTriggerEffects(prop, newValue, target, this);
        } else {
            setNewValueAndTriggerEffects(prop, newValue, target, this);
        }
        return !0;
    }
    deleteProperty(target, prop) {
        return "string" == typeof prop && delete target[prop] && (triggerEffects(this.$container$, this, getEffects(target, prop, this.$effects$)), 
        !0);
    }
    has(target, prop) {
        if (prop === STORE_TARGET) {
            return !0;
        }
        if ("string" == typeof prop) {
            const ctx = tryGetInvokeContext();
            if (ctx) {
                const effectSubscriber = ctx.$effectSubscriber$;
                effectSubscriber && addStoreEffect(target, Array.isArray(target) ? STORE_ALL_PROPS : prop, this, effectSubscriber);
            }
        }
        return Object.prototype.hasOwnProperty.call(target, prop);
    }
    ownKeys(target) {
        const ctx = tryGetInvokeContext();
        const effectSubscriber = ctx?.$effectSubscriber$;
        return effectSubscriber && addStoreEffect(target, STORE_ALL_PROPS, this, effectSubscriber), 
        Reflect.ownKeys(target);
    }
    getOwnPropertyDescriptor(target, prop) {
        const descriptor = Reflect.getOwnPropertyDescriptor(target, prop);
        return Array.isArray(target) || "symbol" == typeof prop || descriptor && !descriptor.configurable ? descriptor : {
            enumerable: !0,
            configurable: !0
        };
    }
}

function addStoreEffect(target, prop, store, effectSubscription) {
    const effectsMap = store.$effects$ ||= new Map;
    let effects = effectsMap.get(prop);
    effects || (effects = new Set, effectsMap.set(prop, effects)), ensureContainsSubscription(effects, effectSubscription), 
    ensureContainsBackRef(effectSubscription, target), addQrlToSerializationCtx(effectSubscription, store.$container$);
}

function setNewValueAndTriggerEffects(prop, value, target, currentStore) {
    target[prop] = value, triggerEffects(currentStore.$container$, currentStore, getEffects(target, prop, currentStore.$effects$));
}

function getEffects(target, prop, storeEffects) {
    let effectsToTrigger;
    if (storeEffects) {
        if (Array.isArray(target)) {
            for (const effects of storeEffects.values()) {
                effectsToTrigger ||= new Set;
                for (const effect of effects) {
                    effectsToTrigger.add(effect);
                }
            }
        } else {
            effectsToTrigger = storeEffects.get(prop);
        }
    }
    const storeArrayValue = storeEffects?.get(STORE_ALL_PROPS);
    if (storeArrayValue) {
        effectsToTrigger ||= new Set;
        for (const effect of storeArrayValue) {
            effectsToTrigger.add(effect);
        }
    }
    return effectsToTrigger || null;
}

class BackRef {
    [_EFFECT_BACK_REF]=null;
}

function clearAllEffects(container, consumer) {
    vnode_isVNode(consumer) && vnode_isElementVNode(consumer) && ensureMaterialized(consumer);
    const effects = consumer[_EFFECT_BACK_REF];
    if (effects) {
        for (const [, effect] of effects) {
            const backRefs = effect[2];
            if (!backRefs) {
                return;
            }
            for (const producer of backRefs) {
                if (producer instanceof SignalImpl) {
                    clearSignal(container, producer, effect);
                } else if (container.$storeProxyMap$.has(producer)) {
                    const target = container.$storeProxyMap$.get(producer);
                    clearStore(getStoreHandler(target), effect);
                }
            }
        }
    }
}

function clearSignal(container, producer, effect) {
    const effects = producer.$effects$;
    effects && effects.delete(effect), producer instanceof WrappedSignal && (producer.$hostElement$ = null, 
    clearAllEffects(container, producer));
}

function clearStore(producer, effect) {
    const effects = producer?.$effects$;
    if (effects) {
        for (const propEffects of effects.values()) {
            propEffects.delete(effect);
        }
    }
}

const useLexicalScope = () => {
    const context = getInvokeContext();
    let qrl = context.$qrl$;
    if (qrl) {
        assertQrl(qrl), assertDefined(qrl.$captureRef$, "invoke: qrl $captureRef$ must be defined inside useLexicalScope()", qrl);
    } else {
        const el = context.$element$;
        assertDefined(el, "invoke: element must be defined inside useLexicalScope()", context);
        const containerElement = _getQContainerElement(el);
        assertDefined(containerElement, "invoke: cant find parent q:container of", el);
        qrl = getDomContainer(containerElement).parseQRL(decodeURIComponent(String(context.$url$)));
    }
    return qrl.$captureRef$;
};

const useTaskQrl = qrl => {
    const {val, set, iCtx, i} = useSequentialScope();
    if (val) {
        return;
    }
    assertQrl(qrl), set(1);
    const task = new Task(10, i, iCtx.$hostElement$, qrl, void 0, null);
    set(task);
    const promise = iCtx.$container$.$scheduler$(3, task);
    isPromise(promise) && promise.catch((() => {}));
};

const runTask = (task, container, host) => {
    task.$flags$ &= -9, cleanupTask(task);
    const iCtx = newInvokeContext(container.$locale$, host, void 0, "qTask");
    iCtx.$container$ = container;
    const taskFn = task.$qrl$.getFn(iCtx, (() => clearAllEffects(container, task)));
    let cleanupFns = null;
    const cleanup = fn => {
        "function" == typeof fn && (cleanupFns || (cleanupFns = [], task.$destroy$ = noSerialize((() => {
            task.$destroy$ = null, cleanupFns.forEach((fn => {
                try {
                    fn();
                } catch (err) {
                    container.handleError(err, host);
                }
            }));
        }))), cleanupFns.push(fn));
    };
    const taskApi = {
        track: (obj, prop) => {
            const ctx = newInvokeContext();
            return ctx.$effectSubscriber$ = getSubscriber(task, ":"), ctx.$container$ = container, 
            invoke(ctx, (() => {
                if (isFunction(obj)) {
                    return obj();
                }
                if (prop) {
                    return obj[prop];
                }
                if (isSignal(obj)) {
                    return obj.value;
                }
                if (isStore(obj)) {
                    return addStoreEffect(getStoreTarget(obj), STORE_ALL_PROPS, getStoreHandler(obj), ctx.$effectSubscriber$), 
                    obj;
                }
                throw qError(2);
            }));
        },
        cleanup
    };
    return safeCall((() => taskFn(taskApi)), cleanup, (err => {
        if (isPromise(err)) {
            return err.then((() => runTask(task, container, host)));
        }
        throw err;
    }));
};

const cleanupTask = task => {
    const destroy = task.$destroy$;
    if (destroy) {
        task.$destroy$ = null;
        try {
            destroy();
        } catch (err) {
            logError(err);
        }
    }
};

class Task extends BackRef {
    $flags$;
    $index$;
    $el$;
    $qrl$;
    $state$;
    $destroy$;
    constructor($flags$, $index$, $el$, $qrl$, $state$, $destroy$) {
        super(), this.$flags$ = $flags$, this.$index$ = $index$, this.$el$ = $el$, this.$qrl$ = $qrl$, 
        this.$state$ = $state$, this.$destroy$ = $destroy$;
    }
}

const isTask = value => value instanceof Task;

const scheduleTask = (_event, element) => {
    const [task] = useLexicalScope();
    const type = 1 & task.$flags$ ? 32 : 3;
    getDomContainer(element).$scheduler$(type, task);
};

const styleContent = styleId => "⚡️" + styleId;

function hasClassAttr(props) {
    for (const key in props) {
        if (Object.prototype.hasOwnProperty.call(props, key) && isClassAttr(key)) {
            return !0;
        }
    }
    return !1;
}

function isClassAttr(key) {
    return "class" === key || "className" === key;
}

function convertScopedStyleIdsToArray(scopedStyleIds) {
    return scopedStyleIds?.split(" ") ?? null;
}

function convertStyleIdsToString(scopedStyleIds) {
    return Array.from(scopedStyleIds).join(" ");
}

const addComponentStylePrefix = styleId => {
    if (styleId) {
        let idx = 0;
        do {
            styleId = styleId.substring(0, idx) + styleContent(styleId.substring(idx));
        } while (0 !== (idx = styleId.indexOf(" ", idx) + 1));
    }
    return styleId || null;
};

const DEBUG_TYPE = "q:type";

const START = "[34m";

const END = "[0m";

const VirtualTypeName = {
    V: "[34mVirtual[0m",
    F: "[34mFragment[0m",
    S: "[34mSignal[0m",
    A: "[34mAwaited[0m",
    C: "[34mComponent[0m",
    I: "[34mInlineComponent[0m",
    P: "[34mProjection[0m"
};

const mapApp_findIndx = (array, key, start) => {
    assertTrue(start % 2 == 0, "Expecting even number.");
    let bottom = start >> 1;
    let top = array.length - 2 >> 1;
    for (;bottom <= top; ) {
        const mid = bottom + (top - bottom >> 1);
        const midKey = array[mid << 1];
        if (midKey === key) {
            return mid << 1;
        }
        midKey < key ? bottom = mid + 1 : top = mid - 1;
    }
    return ~(bottom << 1);
};

const mapArray_set = (array, key, value, start) => {
    const indx = mapApp_findIndx(array, key, start);
    indx >= 0 ? null == value ? array.splice(indx, 2) : array[indx + 1] = value : null != value && array.splice(~indx, 0, key, value);
};

const mapArray_get = (array, key, start) => {
    const indx = mapApp_findIndx(array, key, start);
    return indx >= 0 ? array[indx + 1] : null;
};

const isForeignObjectElement = elementName => isDev ? "foreignobject" === elementName.toLowerCase() : "foreignObject" === elementName;

const isSvgElement = elementName => "svg" === elementName || isForeignObjectElement(elementName);

const isMathElement = elementName => "math" === elementName;

const vnode_isDefaultNamespace = vnode => !(192 & vnode[0]);

const vnode_getElementNamespaceFlags = element => {
    switch (fastNamespaceURI(element)) {
      case SVG_NS:
        return 64;

      case MATH_NS:
        return 128;

      default:
        return 0;
    }
};

function vnode_getDomChildrenWithCorrectNamespacesToInsert(journal, domParentVNode, newChild) {
    const {elementNamespace, elementNamespaceFlag} = getNewElementNamespaceData(domParentVNode, newChild);
    let domChildren = [];
    if (elementNamespace === HTML_NS) {
        domChildren = vnode_getDOMChildNodes(journal, newChild);
    } else {
        const children = vnode_getDOMChildNodes(journal, newChild, !0);
        for (let i = 0; i < children.length; i++) {
            const childVNode = children[i];
            if (vnode_isTextVNode(childVNode)) {
                domChildren.push(childVNode[4]);
                continue;
            }
            if ((192 & childVNode[0]) == (192 & domParentVNode[0])) {
                domChildren.push(childVNode[6]);
                continue;
            }
            const newChildElement = vnode_cloneElementWithNamespace(childVNode, domParentVNode, elementNamespace, elementNamespaceFlag);
            newChildElement && domChildren.push(newChildElement);
        }
    }
    return domChildren;
}

function cloneElementWithNamespace(element, elementName, namespace) {
    const newElement = element.ownerDocument.createElementNS(namespace, elementName);
    const attributes = element.attributes;
    for (const attribute of attributes) {
        const name = attribute.name;
        name && ":" !== name && newElement.setAttribute(name, attribute.value);
    }
    return newElement;
}

function vnode_cloneElementWithNamespace(elementVNode, parentVNode, namespace, namespaceFlag) {
    ensureElementVNode(elementVNode);
    let vCursor = elementVNode;
    let vParent = null;
    let rootElement = null;
    let parentElement = null;
    for (;vCursor; ) {
        let childElement = null;
        let newChildElement = null;
        if (vnode_isElementVNode(vCursor)) {
            childElement = vCursor[6];
            const childElementTag = vnode_getElementName(vCursor);
            const vCursorParent = vnode_getParent(vCursor);
            const vCursorDomParent = null == rootElement ? parentVNode : vCursorParent && vnode_getDomParentVNode(vCursorParent);
            if (vCursorDomParent) {
                const namespaceData = getNewElementNamespaceData(vCursorDomParent, vnode_getElementName(vCursor));
                namespace = namespaceData.elementNamespace, namespaceFlag = namespaceData.elementNamespaceFlag;
            }
            newChildElement = cloneElementWithNamespace(childElement, childElementTag, namespace), 
            childElement.remove(), null == rootElement && (rootElement = newChildElement), parentElement && parentElement.appendChild(newChildElement);
            const vFirstChild = vnode_getFirstChild(vCursor);
            if (vCursor[6] = newChildElement, vCursor[0] &= -193, vCursor[0] |= namespaceFlag, 
            vFirstChild) {
                vCursor = vFirstChild, parentElement = newChildElement;
                continue;
            }
            if (shouldIgnoreChildren(childElement)) {
                const container = getDomContainerFromQContainerElement(childElement);
                if (container) {
                    const innerContainerFirstVNode = vnode_getFirstChild(container.rootVNode);
                    if (innerContainerFirstVNode) {
                        vCursor = innerContainerFirstVNode, parentElement = newChildElement;
                        continue;
                    }
                }
            }
        }
        if (vCursor === elementVNode) {
            return rootElement;
        }
        const vNextSibling = vnode_getNextSibling(vCursor);
        if (vNextSibling) {
            vCursor = vNextSibling;
        } else {
            for (vParent = vnode_getParent(vCursor); vParent; ) {
                if (vParent === elementVNode) {
                    return rootElement;
                }
                const vNextParentSibling = vnode_getNextSibling(vParent);
                if (vNextParentSibling) {
                    return vCursor = vNextParentSibling, rootElement;
                }
                vParent = vnode_getParent(vParent);
            }
            if (null == vParent) {
                return rootElement;
            }
        }
    }
    return rootElement;
}

function isSvg(tagOrVNode) {
    return "string" == typeof tagOrVNode ? isSvgElement(tagOrVNode) : !!(64 & tagOrVNode[0]);
}

function isMath(tagOrVNode) {
    return "string" == typeof tagOrVNode ? isMathElement(tagOrVNode) : !!(128 & tagOrVNode[0]);
}

function getNewElementNamespaceData(domParentVNode, tagOrVNode) {
    const parentIsDefaultNamespace = !domParentVNode || !!vnode_getElementName(domParentVNode) && vnode_isDefaultNamespace(domParentVNode);
    const parentIsForeignObject = !parentIsDefaultNamespace && isForeignObjectElement(vnode_getElementName(domParentVNode));
    let elementNamespace = HTML_NS;
    let elementNamespaceFlag = 0;
    const isElementVNodeOrString = "string" == typeof tagOrVNode || vnode_isElementVNode(tagOrVNode);
    if (isElementVNodeOrString && isSvg(tagOrVNode)) {
        elementNamespace = SVG_NS, elementNamespaceFlag = 64;
    } else if (isElementVNodeOrString && isMath(tagOrVNode)) {
        elementNamespace = MATH_NS, elementNamespaceFlag = 128;
    } else if (domParentVNode && !parentIsForeignObject && !parentIsDefaultNamespace) {
        elementNamespace = !!(64 & domParentVNode[0]) ? SVG_NS : !!(128 & domParentVNode[0]) ? MATH_NS : HTML_NS, 
        elementNamespaceFlag = 192 & domParentVNode[0];
    }
    return {
        elementNamespace,
        elementNamespaceFlag
    };
}

const executeComponent = (container, renderHost, subscriptionHost, componentQRL, props) => {
    const iCtx = newInvokeContext(container.$locale$, subscriptionHost || void 0, void 0, "qRender");
    let componentFn;
    subscriptionHost && (iCtx.$effectSubscriber$ = getSubscriber(subscriptionHost, ":"), 
    iCtx.$container$ = container), container.ensureProjectionResolved(renderHost);
    let isInlineComponent = !1;
    if (null === componentQRL && assertDefined(componentQRL = container.getHostProp(renderHost, "q:renderFn"), "No Component found at this location"), 
    isQrl$1(componentQRL)) {
        (props = props || container.getHostProp(renderHost, "q:props") || EMPTY_OBJ).children && delete props.children, 
        componentFn = componentQRL.getFn(iCtx);
    } else if (isQwikComponent(componentQRL)) {
        const qComponentFn = componentQRL;
        componentFn = () => invokeApply(iCtx, qComponentFn, [ props || EMPTY_OBJ, null, 0 ]);
    } else {
        isInlineComponent = !0;
        const inlineComponent = componentQRL;
        componentFn = () => invokeApply(iCtx, inlineComponent, [ props || EMPTY_OBJ ]);
    }
    const executeComponentWithPromiseExceptionRetry = (retryCount = 0) => safeCall((() => (isInlineComponent || (container.setHostProp(renderHost, "q:seqIdx", null), 
    container.setHostProp(renderHost, ":onIdx", null), container.setHostProp(renderHost, "q:props", props)), 
    vnode_isVNode(renderHost) && clearAllEffects(container, renderHost), componentFn(props))), (jsx => {
        const useOnEvents = container.getHostProp(renderHost, ":on");
        return useOnEvents ? addUseOnEvents(jsx, useOnEvents) : jsx;
    }), (err => {
        if (isPromise(err) && retryCount < 100) {
            return err.then((() => executeComponentWithPromiseExceptionRetry(retryCount++)));
        }
        throw err;
    }));
    return executeComponentWithPromiseExceptionRetry();
};

function addUseOnEvents(jsx, useOnEvents) {
    const jsxElement = findFirstStringJSX(jsx);
    let jsxResult = jsx;
    return maybeThen(jsxElement, (jsxElement => {
        let isInvisibleComponent = !1;
        jsxElement || (isInvisibleComponent = !0);
        for (const key in useOnEvents) {
            if (Object.prototype.hasOwnProperty.call(useOnEvents, key)) {
                if (isInvisibleComponent) {
                    if ("onQvisible$" === key) {
                        const [jsxElement, jsx] = addScriptNodeForInvisibleComponents(jsxResult);
                        jsxResult = jsx, jsxElement && addUseOnEvent(jsxElement, "document:onQinit$", useOnEvents[key]);
                    } else if (key.startsWith("document:") || key.startsWith("window:")) {
                        const [jsxElement, jsx] = addScriptNodeForInvisibleComponents(jsxResult);
                        jsxResult = jsx, jsxElement && addUseOnEvent(jsxElement, key, useOnEvents[key]);
                    } else {
                        isDev && logWarn('You are trying to add an event "' + key + '" using `useOn` hook, but a node to which you can add an event is not found. Please make sure that the component has a valid element node. ');
                    }
                } else {
                    jsxElement && addUseOnEvent(jsxElement, key, useOnEvents[key]);
                }
            }
        }
        return jsxResult;
    }));
}

function addUseOnEvent(jsxElement, key, value) {
    let props = jsxElement.props;
    props === EMPTY_OBJ && (props = jsxElement.props = {});
    let propValue = props[key];
    void 0 === propValue ? propValue = [] : Array.isArray(propValue) || (propValue = [ propValue ]), 
    propValue.push(...value), props[key] = propValue;
}

function findFirstStringJSX(jsx) {
    const queue = [ jsx ];
    for (;queue.length; ) {
        const jsx = queue.shift();
        if (isJSXNode(jsx)) {
            if ("string" == typeof jsx.type) {
                return jsx;
            }
            queue.push(jsx.children);
        } else if (Array.isArray(jsx)) {
            queue.push(...jsx);
        } else {
            if (isPromise(jsx)) {
                return maybeThen(jsx, (jsx => findFirstStringJSX(jsx)));
            }
            if (isSignal(jsx)) {
                return findFirstStringJSX(untrack((() => jsx.value)));
            }
        }
    }
    return null;
}

function addScriptNodeForInvisibleComponents(jsx) {
    if (isJSXNode(jsx)) {
        const jsxElement = new JSXNodeImpl("script", {}, {
            type: "placeholder",
            hidden: ""
        }, null, 3);
        return jsx.type === Slot ? [ jsxElement, _jsxSorted(Fragment, null, null, [ jsx, jsxElement ], 0, null) ] : (null == jsx.children ? jsx.children = jsxElement : Array.isArray(jsx.children) ? jsx.children.push(jsxElement) : jsx.children = [ jsx.children, jsxElement ], 
        [ jsxElement, jsx ]);
    }
    if (Array.isArray(jsx) && jsx.length) {
        const [jsxElement, _] = addScriptNodeForInvisibleComponents(jsx[0]);
        return [ jsxElement, jsx ];
    }
    return [ null, null ];
}

const _CONST_PROPS = Symbol("CONST");

const _VAR_PROPS = Symbol("VAR");

const _IMMUTABLE = Symbol("IMMUTABLE");

function isSlotProp(prop) {
    return !prop.startsWith("q:") && !prop.startsWith(":");
}

const _restProps = (props, omit, target = {}) => {
    let constPropsTarget = null;
    const constProps = props[_CONST_PROPS];
    if (constProps) {
        for (const key in constProps) {
            omit.includes(key) || (constPropsTarget ||= {}, constPropsTarget[key] = constProps[key]);
        }
    }
    const varPropsTarget = target;
    const varProps = props[_VAR_PROPS];
    for (const key in varProps) {
        omit.includes(key) || (varPropsTarget[key] = varProps[key]);
    }
    return createPropsProxy(varPropsTarget, constPropsTarget);
};

function escapeHTML(html) {
    let escapedHTML = "";
    const length = html.length;
    let idx = 0;
    let lastIdx = idx;
    for (;idx < length; idx++) {
        const ch = html.charCodeAt(idx);
        if (60 === ch) {
            escapedHTML += html.substring(lastIdx, idx) + "&lt;";
        } else if (62 === ch) {
            escapedHTML += html.substring(lastIdx, idx) + "&gt;";
        } else if (38 === ch) {
            escapedHTML += html.substring(lastIdx, idx) + "&amp;";
        } else if (34 === ch) {
            escapedHTML += html.substring(lastIdx, idx) + "&quot;";
        } else {
            if (39 !== ch) {
                continue;
            }
            escapedHTML += html.substring(lastIdx, idx) + "&#39;";
        }
        lastIdx = idx + 1;
    }
    return 0 === lastIdx ? html : escapedHTML + html.substring(lastIdx);
}

const unitlessNumbers = new Set([ "animationIterationCount", "aspectRatio", "borderImageOutset", "borderImageSlice", "borderImageWidth", "boxFlex", "boxFlexGroup", "boxOrdinalGroup", "columnCount", "columns", "flex", "flexGrow", "flexShrink", "gridArea", "gridRow", "gridRowEnd", "gridRowStart", "gridColumn", "gridColumnEnd", "gridColumnStart", "fontWeight", "lineClamp", "lineHeight", "opacity", "order", "orphans", "scale", "tabSize", "widows", "zIndex", "zoom", "MozAnimationIterationCount", "MozBoxFlex", "msFlex", "msFlexPositive", "WebkitAnimationIterationCount", "WebkitBoxFlex", "WebkitBoxOrdinalGroup", "WebkitColumnCount", "WebkitColumns", "WebkitFlex", "WebkitFlexGrow", "WebkitFlexShrink", "WebkitLineClamp" ]);

const isUnitlessNumber = name => unitlessNumbers.has(name);

const hashCode = (text, hash = 0) => {
    for (let i = 0; i < text.length; i++) {
        hash = (hash << 5) - hash + text.charCodeAt(i), hash |= 0;
    }
    return Number(Math.abs(hash)).toString(36);
};

const serializeClass = obj => {
    if (!obj) {
        return "";
    }
    if (isString(obj)) {
        return obj.trim();
    }
    const classes = [];
    if (isArray(obj)) {
        for (const o of obj) {
            const classList = serializeClass(o);
            classList && classes.push(classList);
        }
    } else {
        for (const [key, value] of Object.entries(obj)) {
            value && classes.push(key.trim());
        }
    }
    return classes.join(" ");
};

const fromCamelToKebabCaseWithDash = text => text.replace(/([A-Z])/g, "-$1").toLowerCase();

const stringifyStyle = obj => {
    if (null == obj) {
        return "";
    }
    if ("object" == typeof obj) {
        if (isArray(obj)) {
            throw qError(0, [ obj, "style" ]);
        }
        {
            const chunks = [];
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    const value = obj[key];
                    null != value && "function" != typeof value && (key.startsWith("--") ? chunks.push(key + ":" + value) : chunks.push(fromCamelToKebabCaseWithDash(key) + ":" + setValueForStyle(key, value)));
                }
            }
            return chunks.join(";");
        }
    }
    return String(obj);
};

const serializeBooleanOrNumberAttribute = value => null != value ? String(value) : null;

function serializeAttribute(key, value, styleScopedId) {
    if (isClassAttr(key)) {
        const serializedClass = serializeClass(value);
        value = styleScopedId ? styleScopedId + (serializedClass.length ? " " + serializedClass : serializedClass) : serializedClass;
    } else {
        "style" === key ? value = stringifyStyle(value) : isEnumeratedBooleanAttribute(key) || "number" == typeof value ? value = serializeBooleanOrNumberAttribute(value) : !1 === value || null == value ? value = null : !0 === value && isPreventDefault(key) && (value = "");
    }
    return value;
}

function isEnumeratedBooleanAttribute(key) {
    return isAriaAttribute(key) || [ "spellcheck", "draggable", "contenteditable" ].includes(key);
}

const setValueForStyle = (styleName, value) => "number" != typeof value || 0 === value || isUnitlessNumber(styleName) ? value : value + "px";

function isAriaAttribute(prop) {
    return prop.startsWith("aria-");
}

const styleKey = (qStyles, index) => (assertQrl(qStyles), `${hashCode(qStyles.$hash$)}-${index}`);

function getFileLocationFromJsx(jsxDev) {
    if (!jsxDev) {
        return null;
    }
    const sanitizedFileName = jsxDev.fileName?.replace(/\\/g, "/");
    return sanitizedFileName ? `${sanitizedFileName}:${jsxDev.lineNumber}:${jsxDev.columnNumber}` : null;
}

const vnode_diff = (container, jsxNode, vStartNode, scopedStyleIdPrefix) => {
    let journal = container.$journal$;
    const stack = [];
    const asyncQueue = [];
    let vParent = null;
    let vCurrent = null;
    let vNewNode = null;
    let vSiblings = null;
    let vSiblingsArray = null;
    let jsxChildren = null;
    let jsxValue = null;
    let jsxIdx = 0;
    let jsxCount = 0;
    let shouldAdvance = !0;
    return diff(jsxNode, vStartNode), function drainAsyncQueue() {
        for (;asyncQueue.length; ) {
            const jsxNode = asyncQueue.shift();
            const vHostNode = asyncQueue.shift();
            if (isPromise(jsxNode)) {
                return jsxNode.then((jsxNode => (diff(jsxNode, vHostNode), drainAsyncQueue())));
            }
            diff(jsxNode, vHostNode);
        }
    }();
    function diff(jsxNode, vStartNode) {
        for (assertFalse(vnode_isVNode(jsxNode), "JSXNode should not be a VNode"), assertTrue(vnode_isVNode(vStartNode), "vStartNode should be a VNode"), 
        vParent = vStartNode, vNewNode = null, vCurrent = vnode_getFirstChild(vStartNode), 
        stackPush(jsxNode, !0); stack.length; ) {
            for (;jsxIdx < jsxCount; ) {
                if (assertFalse(vParent === vCurrent, "Parent and current can't be the same"), "string" == typeof jsxValue) {
                    expectText(jsxValue);
                } else if ("number" == typeof jsxValue) {
                    expectText(String(jsxValue));
                } else if (jsxValue && "object" == typeof jsxValue) {
                    if (Array.isArray(jsxValue)) {
                        descend(jsxValue, !1);
                    } else if (isSignal(jsxValue)) {
                        vCurrent && clearAllEffects(container, vCurrent), expectVirtual("S", null), descend(trackSignalAndAssignHost(jsxValue, vNewNode || vCurrent, ".", container), !0);
                    } else if (isPromise(jsxValue)) {
                        expectVirtual("A", null), asyncQueue.push(jsxValue, vNewNode || vCurrent);
                    } else if (isJSXNode(jsxValue)) {
                        const type = jsxValue.type;
                        "string" == typeof type ? (expectNoMoreTextNodes(), expectElement(jsxValue, type), 
                        descend(jsxValue.children, !0)) : "function" == typeof type && (type === Fragment ? (expectNoMoreTextNodes(), 
                        expectVirtual("F", jsxValue.key), descend(jsxValue.children, !0)) : type === Slot ? (expectNoMoreTextNodes(), 
                        expectSlot() || descend(jsxValue.children, !0)) : type === Projection ? (expectProjection(), 
                        descend(jsxValue.children, !0)) : type === SSRComment || type === SSRRaw ? expectNoMore() : (expectNoMoreTextNodes(), 
                        expectComponent(type)));
                    }
                } else {
                    jsxValue === SkipRender ? journal = [] : expectText("");
                }
                advance();
            }
            expectNoMore(), ascend();
        }
    }
    function advance() {
        if (shouldAdvance) {
            if (jsxIdx++, jsxIdx < jsxCount) {
                jsxValue = jsxChildren[jsxIdx];
            } else if (!1 === stack[stack.length - 1]) {
                return ascend();
            }
            null !== vNewNode ? vNewNode = null : advanceToNextSibling();
        } else {
            shouldAdvance = !0;
        }
    }
    function peekNextSibling() {
        return vCurrent ? vnode_getNextSibling(vCurrent) : null;
    }
    function advanceToNextSibling() {
        vCurrent = peekNextSibling();
    }
    function descend(children, descendVNode) {
        null != children ? (stackPush(children, descendVNode), descendVNode && (assertDefined(vCurrent || vNewNode, "Expecting vCurrent to be defined."), 
        vSiblings = null, vSiblingsArray = null, vParent = vNewNode || vCurrent, vCurrent = vnode_getFirstChild(vParent), 
        vNewNode = null), shouldAdvance = !1) : function() {
            const vFirstChild = vCurrent && vnode_getFirstChild(vCurrent);
            if (null !== vFirstChild) {
                let vChild = vFirstChild;
                for (;vChild; ) {
                    cleanup(container, vChild), vChild = vnode_getNextSibling(vChild);
                }
                vnode_truncate(journal, vCurrent, vFirstChild);
            }
        }();
    }
    function ascend() {
        stack.pop() && (vSiblings = stack.pop(), vSiblingsArray = stack.pop(), vNewNode = stack.pop(), 
        vCurrent = stack.pop(), vParent = stack.pop()), jsxValue = stack.pop(), jsxCount = stack.pop(), 
        jsxIdx = stack.pop(), jsxChildren = stack.pop(), advance();
    }
    function stackPush(children, descendVNode) {
        stack.push(jsxChildren, jsxIdx, jsxCount, jsxValue), descendVNode && stack.push(vParent, vCurrent, vNewNode, vSiblingsArray, vSiblings), 
        stack.push(descendVNode), Array.isArray(children) ? (jsxIdx = 0, jsxCount = children.length, 
        jsxChildren = children, jsxValue = jsxCount > 0 ? children[0] : null) : void 0 === children ? (jsxIdx = 0, 
        jsxValue = null, jsxChildren = null, jsxCount = 0) : (jsxIdx = 0, jsxValue = children, 
        jsxChildren = null, jsxCount = 1);
    }
    function getInsertBefore() {
        return vNewNode ? vCurrent : peekNextSibling();
    }
    function expectProjection() {
        const slotName = jsxValue.key;
        vCurrent = vnode_getProp(vParent, slotName, (id => vnode_locate(container.rootVNode, id))), 
        vCurrent = vCurrent && 32 & vCurrent[0] ? null : vCurrent, null == vCurrent && (vNewNode = vnode_newVirtual(), 
        isDev && vnode_setProp(vNewNode, DEBUG_TYPE, "P"), isDev && vnode_setProp(vNewNode, "q:code", "expectProjection"), 
        vnode_setProp(vNewNode, QSlot, slotName), vnode_setProp(vNewNode, "q:sparent", vParent), 
        vnode_setProp(vParent, slotName, vNewNode));
    }
    function expectSlot() {
        const vHost = vnode_getProjectionParentComponent(vParent, container.rootVNode);
        const slotNameKey = function(vHost) {
            const jsxNode = jsxValue;
            const constProps = jsxNode.constProps;
            if (constProps && "object" == typeof constProps && "name" in constProps) {
                const constValue = constProps.name;
                if (vHost && constValue instanceof WrappedSignal) {
                    return trackSignalAndAssignHost(constValue, vHost, ":", container);
                }
            }
            return directGetPropsProxyProp(jsxNode, "name") || "";
        }(vHost);
        const vProjectedNode = vHost ? vnode_getProp(vHost, slotNameKey, null) : null;
        return null == vProjectedNode ? (vnode_insertBefore(journal, vParent, vNewNode = vnode_newVirtual(), vCurrent && getInsertBefore()), 
        vnode_setProp(vNewNode, QSlot, slotNameKey), vHost && vnode_setProp(vHost, slotNameKey, vNewNode), 
        isDev && vnode_setProp(vNewNode, DEBUG_TYPE, "P"), isDev && vnode_setProp(vNewNode, "q:code", "expectSlot" + count++), 
        !1) : (vProjectedNode === vCurrent || (vnode_insertBefore(journal, vParent, vNewNode = vProjectedNode, vCurrent && getInsertBefore()), 
        vnode_setProp(vNewNode, QSlot, slotNameKey), vHost && vnode_setProp(vHost, slotNameKey, vNewNode), 
        isDev && vnode_setProp(vNewNode, DEBUG_TYPE, "P"), isDev && vnode_setProp(vNewNode, "q:code", "expectSlot" + count++)), 
        !0);
    }
    function expectNoMore() {
        if (assertFalse(vParent === vCurrent, "Parent and current can't be the same"), null !== vCurrent) {
            for (;vCurrent; ) {
                const toRemove = vCurrent;
                advanceToNextSibling(), vParent === vnode_getParent(toRemove) && (cleanup(container, toRemove), 
                vnode_remove(journal, vParent, toRemove, !0));
            }
        }
    }
    function expectNoMoreTextNodes() {
        for (;null !== vCurrent && vnode_isTextVNode(vCurrent); ) {
            cleanup(container, vCurrent);
            const toRemove = vCurrent;
            advanceToNextSibling(), vnode_remove(journal, vParent, toRemove, !0);
        }
    }
    function createNewElement(jsx, elementName, currentFile) {
        const element = function(elementName) {
            const domParentVNode = vnode_getDomParentVNode(vParent);
            const {elementNamespace, elementNamespaceFlag} = getNewElementNamespaceData(domParentVNode, elementName);
            const element = container.document.createElementNS(elementNamespace, elementName);
            return vNewNode = vnode_newElement(element, elementName), vNewNode[0] |= elementNamespaceFlag, 
            element;
        }(elementName);
        const {constProps} = jsx;
        let needsQDispatchEventPatch = !1;
        if (constProps) {
            for (const key in constProps) {
                let value = constProps[key];
                if (isJsxPropertyAnEventName(key)) {
                    const eventName = getEventNameFromJsxEvent(key);
                    const scope = getEventNameScopeFromJsxEvent(key);
                    if (eventName && (vnode_setProp(vNewNode, HANDLER_PREFIX + ":" + scope + ":" + eventName, value), 
                    registerQwikLoaderEvent(eventName)), scope) {
                        const htmlEvent = jsxEventToHtmlAttribute(key);
                        htmlEvent && vnode_setAttr(journal, vNewNode, htmlEvent, "");
                    }
                    needsQDispatchEventPatch = !0;
                } else {
                    if ("ref" === key) {
                        if (isSignal(value)) {
                            value.value = element;
                            continue;
                        }
                        if ("function" == typeof value) {
                            value(element);
                            continue;
                        }
                        if (null == value) {
                            continue;
                        }
                        throw qError(32, [ currentFile ]);
                    }
                    if (isSignal(value)) {
                        const signalData = new SubscriptionData({
                            $scopedStyleIdPrefix$: scopedStyleIdPrefix,
                            $isConst$: !0
                        });
                        value = trackSignalAndAssignHost(value, vNewNode, key, container, signalData);
                    }
                    if (key !== dangerouslySetInnerHTML) {
                        if ("textarea" !== elementName || "value" !== key) {
                            value = serializeAttribute(key, value, scopedStyleIdPrefix), null != value && element.setAttribute(key, String(value));
                        } else {
                            if (value && "string" != typeof value) {
                                if (isDev) {
                                    throw qError(40, [ currentFile, value ]);
                                }
                                continue;
                            }
                            element.value = escapeHTML(value || "");
                        }
                    } else {
                        element.innerHTML = value, element.setAttribute("q:container", "html");
                    }
                }
            }
        }
        const key = jsx.key;
        key && (element.setAttribute("q:key", key), vnode_setProp(vNewNode, "q:key", key));
        return !(hasClassAttr(jsx.varProps) || jsx.constProps && hasClassAttr(jsx.constProps)) && scopedStyleIdPrefix && element.setAttribute("class", scopedStyleIdPrefix), 
        vnode_insertBefore(journal, vParent, vNewNode, vCurrent), needsQDispatchEventPatch;
    }
    function expectElement(jsx, elementName) {
        const isSameElementName = vCurrent && vnode_isElementVNode(vCurrent) && elementName === vnode_getElementName(vCurrent);
        const jsxKey = jsx.key;
        let needsQDispatchEventPatch = !1;
        const currentFile = getFileLocationFromJsx(jsx.dev);
        isSameElementName && jsxKey === getKey(vCurrent) || (vNewNode = retrieveChildWithKey(elementName, jsxKey), 
        null === vNewNode ? needsQDispatchEventPatch = createNewElement(jsx, elementName) : (vnode_insertBefore(journal, vParent, vNewNode, vCurrent), 
        vCurrent = vNewNode, vNewNode = null));
        const jsxAttrs = [];
        const props = jsx.varProps;
        for (const key in props) {
            const value = props[key];
            null != value && mapArray_set(jsxAttrs, key, value, 0);
        }
        null !== jsxKey && mapArray_set(jsxAttrs, "q:key", jsxKey, 0);
        const vNode = vNewNode || vCurrent;
        const element = vNode[6];
        element.vNode = new WeakRef(vNode), needsQDispatchEventPatch = function(vnode, srcAttrs, currentFile) {
            vnode_ensureElementInflated(vnode);
            const dstAttrs = vnode_getProps(vnode);
            let srcIdx = 0;
            const srcLength = srcAttrs.length;
            let dstIdx = 0;
            let dstLength = dstAttrs.length;
            let srcKey = srcIdx < srcLength ? srcAttrs[srcIdx++] : null;
            let dstKey = dstIdx < dstLength ? dstAttrs[dstIdx++] : null;
            let patchEventDispatch = !1;
            const record = (key, value) => {
                if (key.startsWith(":")) {
                    vnode_setProp(vnode, key, value);
                } else {
                    if ("ref" === key) {
                        const element = vnode_getNode(vnode);
                        if (isSignal(value)) {
                            return void (value.value = element);
                        }
                        if ("function" == typeof value) {
                            return void value(element);
                        }
                        throw qError(32, [ currentFile ]);
                    }
                    if (isSignal(value)) {
                        const signalData = new SubscriptionData({
                            $scopedStyleIdPrefix$: scopedStyleIdPrefix,
                            $isConst$: !1
                        });
                        value = trackSignalAndAssignHost(value, vnode, key, container, signalData);
                    }
                    vnode_setAttr(journal, vnode, key, null !== value ? serializeAttribute(key, value, scopedStyleIdPrefix) : null), 
                    null === value && (dstLength = dstAttrs.length);
                }
            };
            const recordJsxEvent = (key, value) => {
                const eventName = getEventNameFromJsxEvent(key);
                const scope = getEventNameScopeFromJsxEvent(key);
                if (eventName && (record(":" + scope + ":" + eventName, value), registerQwikLoaderEvent(eventName)), 
                scope) {
                    const htmlEvent = jsxEventToHtmlAttribute(key);
                    htmlEvent && record(htmlEvent, "");
                }
            };
            for (;null !== srcKey || null !== dstKey; ) {
                if (dstKey?.startsWith(HANDLER_PREFIX) || dstKey?.startsWith("q:")) {
                    dstIdx++, dstKey = dstIdx < dstLength ? dstAttrs[dstIdx++] : null;
                } else if (null == srcKey) {
                    dstKey && isHtmlAttributeAnEventName(dstKey) ? dstIdx++ : (record(dstKey, null), 
                    dstIdx--), dstKey = dstIdx < dstLength ? dstAttrs[dstIdx++] : null;
                } else if (null == dstKey) {
                    isJsxPropertyAnEventName(srcKey) ? (patchEventDispatch = !0, recordJsxEvent(srcKey, srcAttrs[srcIdx])) : record(srcKey, srcAttrs[srcIdx]), 
                    srcIdx++, srcKey = srcIdx < srcLength ? srcAttrs[srcIdx++] : null, dstIdx++, dstKey = dstIdx < dstLength ? dstAttrs[dstIdx++] : null;
                } else if (srcKey == dstKey) {
                    const srcValue = srcAttrs[srcIdx++];
                    srcValue !== dstAttrs[dstIdx++] && record(dstKey, srcValue), srcKey = srcIdx < srcLength ? srcAttrs[srcIdx++] : null, 
                    dstKey = dstIdx < dstLength ? dstAttrs[dstIdx++] : null;
                } else {
                    srcKey < dstKey ? (isJsxPropertyAnEventName(srcKey) ? (patchEventDispatch = !0, 
                    recordJsxEvent(srcKey, srcAttrs[srcIdx])) : record(srcKey, srcAttrs[srcIdx]), srcIdx++, 
                    srcKey = srcIdx < srcLength ? srcAttrs[srcIdx++] : null, dstIdx++, dstLength = dstAttrs.length, 
                    dstKey = dstIdx < dstLength ? dstAttrs[dstIdx++] : null) : (isHtmlAttributeAnEventName(dstKey) ? (patchEventDispatch = !0, 
                    dstIdx++) : (record(dstKey, null), dstIdx--), dstKey = dstIdx < dstLength ? dstAttrs[dstIdx++] : null);
                }
            }
            return patchEventDispatch;
        }(vNode, jsxAttrs, currentFile) || needsQDispatchEventPatch, needsQDispatchEventPatch && (element.qDispatchEvent || (element.qDispatchEvent = (event, scope) => {
            const eventName = event.type;
            const eventProp = ":" + scope.substring(1) + ":" + eventName;
            const qrls = [ vnode_getProp(vNode, eventProp, null), vnode_getProp(vNode, HANDLER_PREFIX + eventProp, null) ];
            let returnValue = !1;
            return qrls.flat(2).forEach((qrl => {
                if (qrl) {
                    const value = container.$scheduler$(2, vNode, qrl, [ event, element ]);
                    returnValue = returnValue || !0 === value;
                }
            })), returnValue;
        }));
    }
    function registerQwikLoaderEvent(eventName) {
        const window = container.document.defaultView;
        window && (window.qwikevents ||= []).push(eventName);
    }
    function retrieveChildWithKey(nodeName, key) {
        let vNodeWithKey = null;
        if (null === vSiblings) {
            vSiblings = new Map, vSiblingsArray = [];
            let vNode = vCurrent;
            for (;vNode; ) {
                const name = vnode_isElementVNode(vNode) ? vnode_getElementName(vNode) : null;
                const vKey = getKey(vNode) || getComponentHash(vNode, container.$getObjectById$);
                null === vNodeWithKey && vKey == key && name == nodeName ? vNodeWithKey = vNode : null === vKey ? vSiblingsArray.push(name, vNode) : vSiblings.set(name + ":" + vKey, vNode), 
                vNode = vnode_getNextSibling(vNode);
            }
        } else if (null === key) {
            for (let i = 0; i < vSiblingsArray.length; i += 2) {
                if (vSiblingsArray[i] === nodeName) {
                    vNodeWithKey = vSiblingsArray[i + 1], vSiblingsArray.splice(i, 2);
                    break;
                }
            }
        } else {
            const vSibling = vSiblings.get(nodeName + ":" + key);
            vSibling && (vNodeWithKey = vSibling, vSiblings.delete(nodeName + ":" + key));
        }
        return vNodeWithKey;
    }
    function expectVirtual(type, jsxKey) {
        vCurrent && vnode_isVirtualVNode(vCurrent) && getKey(vCurrent) === jsxKey && jsxKey || (null === jsxKey || (vNewNode = retrieveChildWithKey(null, jsxKey), 
        null == vNewNode) ? (vnode_insertBefore(journal, vParent, vNewNode = vnode_newVirtual(), vCurrent && getInsertBefore()), 
        vnode_setProp(vNewNode, "q:key", jsxKey), isDev && vnode_setProp(vNewNode || vCurrent, DEBUG_TYPE, type)) : vnode_insertBefore(journal, vParent, vNewNode, vCurrent && getInsertBefore()));
    }
    function expectComponent(component) {
        const componentMeta = component[SERIALIZABLE_STATE];
        let host = vNewNode || vCurrent;
        const jsxNode = jsxValue;
        if (componentMeta) {
            const jsxProps = jsxNode.props;
            let shouldRender = !1;
            const [componentQRL] = componentMeta;
            const componentHash = componentQRL.$hash$;
            const vNodeComponentHash = getComponentHash(host, container.$getObjectById$);
            const lookupKey = jsxNode.key || componentHash;
            if (lookupKey === (getKey(host) || vNodeComponentHash) ? componentHash === vNodeComponentHash && jsxNode.key || (insertNewComponent(host, componentQRL, jsxProps), 
            host = vNewNode, shouldRender = !0) : (vNewNode = retrieveChildWithKey(null, lookupKey), 
            vNewNode ? vnode_insertBefore(journal, vParent, vNewNode, vCurrent) : insertNewComponent(host, componentQRL, jsxProps), 
            host = vNewNode, shouldRender = !0), host) {
                const vNodeProps = vnode_getProp(host, "q:props", container.$getObjectById$);
                shouldRender = shouldRender || propsDiffer(jsxProps, vNodeProps), shouldRender && (host[0] &= -33, 
                container.$scheduler$(6, host, componentQRL, jsxProps));
            }
            !function(children, host) {
                const projectionChildren = Array.isArray(children) ? children : [ children ];
                const createProjectionJSXNode = slotName => new JSXNodeImpl(Projection, EMPTY_OBJ, null, [], 0, slotName);
                const projections = [];
                if (host) {
                    const props = vnode_getProps(host);
                    for (let i = 0; i < props.length; i += 2) {
                        const prop = props[i];
                        if (isSlotProp(prop)) {
                            const slotName = prop;
                            projections.push(slotName), projections.push(createProjectionJSXNode(slotName));
                        }
                    }
                }
                if (0 !== projections.length || null != children) {
                    for (let i = 0; i < projectionChildren.length; i++) {
                        const child = projectionChildren[i];
                        const slotName = String(isJSXNode(child) && directGetPropsProxyProp(child, QSlot) || "");
                        const idx = mapApp_findIndx(projections, slotName, 0);
                        let jsxBucket;
                        idx >= 0 ? jsxBucket = projections[idx + 1] : projections.splice(~idx, 0, slotName, jsxBucket = createProjectionJSXNode(slotName)), 
                        !1 === child || jsxBucket.children.push(child);
                    }
                    for (let i = projections.length - 2; i >= 0; i -= 2) {
                        projections.splice(i, 1);
                    }
                    descend(projections, !0);
                }
            }(jsxNode.children, host);
        } else {
            const lookupKey = jsxNode.key;
            const lookupKeysAreEqual = lookupKey === getKey(host);
            const vNodeComponentHash = getComponentHash(host, container.$getObjectById$);
            if (lookupKeysAreEqual ? null != vNodeComponentHash && (insertNewInlineComponent(), 
            host = vNewNode) : (vNewNode = retrieveChildWithKey(null, lookupKey), vNewNode ? vnode_insertBefore(journal, vParent, vNewNode, vCurrent) : insertNewInlineComponent(), 
            host = vNewNode), host) {
                let componentHost = host;
                for (;componentHost && (!vnode_isVirtualVNode(componentHost) || null === vnode_getProp(componentHost, "q:renderFn", null)); ) {
                    componentHost = vnode_getParent(componentHost);
                }
                const jsxOutput = executeComponent(container, host, componentHost || container.rootVNode, component, jsxNode.props);
                asyncQueue.push(jsxOutput, host);
            }
        }
    }
    function insertNewComponent(host, componentQRL, jsxProps) {
        host && clearAllEffects(container, host), vnode_insertBefore(journal, vParent, vNewNode = vnode_newVirtual(), vCurrent && getInsertBefore());
        const jsxNode = jsxValue;
        isDev && vnode_setProp(vNewNode, DEBUG_TYPE, "C"), container.setHostProp(vNewNode, "q:renderFn", componentQRL), 
        container.setHostProp(vNewNode, "q:props", jsxProps), container.setHostProp(vNewNode, "q:key", jsxNode.key);
    }
    function insertNewInlineComponent() {
        vnode_insertBefore(journal, vParent, vNewNode = vnode_newVirtual(), vCurrent && getInsertBefore());
        const jsxNode = jsxValue;
        isDev && vnode_setProp(vNewNode, DEBUG_TYPE, "I"), vnode_setProp(vNewNode, "q:props", jsxNode.props), 
        jsxNode.key && vnode_setProp(vNewNode, "q:key", jsxNode.key);
    }
    function expectText(text) {
        if (null !== vCurrent) {
            if (3 === vnode_getType(vCurrent)) {
                return text !== vnode_getText(vCurrent) ? void vnode_setText(journal, vCurrent, text) : void 0;
            }
        }
        vnode_insertBefore(journal, vParent, vNewNode = vnode_newText(container.document.createTextNode(text), text), vCurrent);
    }
};

function getKey(vNode) {
    return null == vNode ? null : vnode_getProp(vNode, "q:key", null);
}

function getComponentHash(vNode, getObject) {
    if (null == vNode) {
        return null;
    }
    const qrl = vnode_getProp(vNode, "q:renderFn", getObject);
    return qrl ? qrl.$hash$ : null;
}

function Projection() {}

function propsDiffer(src, dst) {
    if (!src || !dst) {
        return !0;
    }
    let srcKeys = removePropsKeys(Object.keys(src), [ "children", "q:brefs" ]);
    let dstKeys = removePropsKeys(Object.keys(dst), [ "children", "q:brefs" ]);
    if (srcKeys.length !== dstKeys.length) {
        return !0;
    }
    srcKeys = srcKeys.sort(), dstKeys = dstKeys.sort();
    for (let idx = 0; idx < srcKeys.length; idx++) {
        const srcKey = srcKeys[idx];
        const dstKey = dstKeys[idx];
        if (srcKey !== dstKey || src[srcKey] !== dst[dstKey]) {
            return !0;
        }
    }
    return !1;
}

function removePropsKeys(keys, propKeys) {
    for (let i = propKeys.length - 1; i >= 0; i--) {
        const propIdx = keys.indexOf(propKeys[i]);
        -1 !== propIdx && keys.splice(propIdx, 1);
    }
    return keys;
}

function cleanup(container, vNode) {
    let vCursor = vNode;
    if (vnode_isTextVNode(vNode)) {
        return void markVNodeAsDeleted(vCursor);
    }
    let vParent = null;
    for (;;) {
        const type = vCursor[0];
        if (3 & type) {
            if (clearAllEffects(container, vCursor), markVNodeAsDeleted(vCursor), 2 & type) {
                const seq = container.getHostProp(vCursor, "q:seq");
                if (seq) {
                    for (let i = 0; i < seq.length; i++) {
                        const obj = seq[i];
                        if (isTask(obj)) {
                            const task = obj;
                            clearAllEffects(container, task), 1 & task.$flags$ ? container.$scheduler$(48, task) : cleanupTask(task);
                        }
                    }
                }
            }
            if (2 & type && null !== vnode_getProp(vCursor, "q:renderFn", null)) {
                const attrs = vnode_getProps(vCursor);
                for (let i = 0; i < attrs.length; i += 2) {
                    if (isSlotProp(attrs[i])) {
                        const value = attrs[i + 1];
                        if (value) {
                            attrs[i + 1] = null;
                            const projection = "string" == typeof value ? vnode_locate(container.rootVNode, value) : value;
                            let projectionChild = vnode_getFirstChild(projection);
                            for (;projectionChild; ) {
                                cleanup(container, projectionChild), projectionChild = vnode_getNextSibling(projectionChild);
                            }
                            cleanupStaleUnclaimedProjection(container.$journal$, projection);
                        }
                    }
                }
            }
            if (vnode_isProjection(vCursor)) {
                if (vCursor === vNode) {
                    const vFirstChild = vnode_getFirstChild(vCursor);
                    if (vFirstChild) {
                        return void vnode_walkVNode(vFirstChild);
                    }
                }
            } else {
                const vFirstChild = vnode_getFirstChild(vCursor);
                if (vFirstChild) {
                    vCursor = vFirstChild;
                    continue;
                }
            }
        } else {
            4 & type && markVNodeAsDeleted(vCursor);
        }
        if (vCursor === vNode) {
            return;
        }
        const vNextSibling = vnode_getNextSibling(vCursor);
        if (vNextSibling) {
            vCursor = vNextSibling;
        } else {
            for (vParent = vnode_getParent(vCursor); vParent; ) {
                if (vParent === vNode) {
                    return;
                }
                const vNextParentSibling = vnode_getNextSibling(vParent);
                if (vNextParentSibling) {
                    vCursor = vNextParentSibling;
                    break;
                }
                vParent = vnode_getParent(vParent);
            }
            if (null == vParent) {
                return;
            }
        }
    }
}

function cleanupStaleUnclaimedProjection(journal, projection) {
    const projectionParent = vnode_getParent(projection);
    if (projectionParent) {
        1 & projectionParent[0] && vnode_getElementName(projectionParent) === QTemplate && vnode_remove(journal, projectionParent, projection, !0);
    }
}

function markVNodeAsDeleted(vCursor) {
    vCursor[0] |= 32;
}

const HANDLER_PREFIX = ":";

let count = 0;

const implicit$FirstArg = fn => function(first, ...rest) {
    return fn.call(null, dollar(first), ...rest);
};

const createSignal$1 = value => new SignalImpl(null, value);

const createComputedSignal = qrl => (throwIfQRLNotResolved(qrl), new ComputedSignalImpl(null, qrl));

const createSerializerSignal = arg => (throwIfQRLNotResolved(arg), new SerializerSignalImpl(null, arg));

const createSignal = createSignal$1;

const createComputed$ = /*#__PURE__*/ implicit$FirstArg(createComputedSignal);

const createSerializer$ = implicit$FirstArg(createSerializerSignal);

const useResourceQrl = (qrl, opts) => {
    const {val, set, i, iCtx} = useSequentialScope();
    if (null != val) {
        return val;
    }
    assertQrl(qrl);
    const container = iCtx.$container$;
    const resource = createResourceReturn(container, opts);
    const task = new Task(12, i, iCtx.$hostElement$, qrl, resource, null);
    return container.$scheduler$(3, task), set(resource), resource;
};

const Resource = props => _jsxSorted(Fragment, null, null, getResourceValueAsPromise(props), 0, null);

function getResourceValueAsPromise(props) {
    const resource = props.value;
    if (isResourceReturn(resource)) {
        if (!isServerPlatform()) {
            const state = resource._state;
            if ("pending" === state && props.onPending) {
                return Promise.resolve().then(useBindInvokeContext(props.onPending));
            }
            if ("rejected" === state && props.onRejected) {
                return Promise.resolve(resource._error).then(useBindInvokeContext(props.onRejected));
            }
            {
                const resolvedValue = untrack((() => resource._resolved));
                if (void 0 !== resolvedValue) {
                    return Promise.resolve(resolvedValue).then(useBindInvokeContext(props.onResolved));
                }
            }
        }
        const value = resource.value;
        return value ? value.then(useBindInvokeContext(props.onResolved), useBindInvokeContext(props.onRejected)) : Promise.resolve(void 0);
    }
    return isPromise(resource) ? resource.then(useBindInvokeContext(props.onResolved), useBindInvokeContext(props.onRejected)) : isSignal(resource) ? Promise.resolve(resource.value).then(useBindInvokeContext(props.onResolved), useBindInvokeContext(props.onRejected)) : Promise.resolve(resource).then(useBindInvokeContext(props.onResolved), useBindInvokeContext(props.onRejected));
}

const _createResourceReturn = opts => ({
    __brand: "resource",
    value: void 0,
    loading: !isServerPlatform(),
    _resolved: void 0,
    _error: void 0,
    _state: "pending",
    _timeout: opts?.timeout ?? -1,
    _cache: 0
});

const createResourceReturn = (container, opts, initialPromise) => {
    const result = _createResourceReturn(opts);
    return result.value = initialPromise, createStore(container, result, 1);
};

const isResourceReturn = obj => isObject(obj) && "resource" === (getStoreTarget(obj) || obj).__brand;

const runResource = (task, container, host) => {
    task.$flags$ &= -9, cleanupTask(task);
    const iCtx = newInvokeContext(container.$locale$, host, void 0, "qResource");
    iCtx.$container$ = container;
    const taskFn = task.$qrl$.getFn(iCtx, (() => clearAllEffects(container, task)));
    const resource = task.$state$;
    assertDefined(resource, 'useResource: when running a resource, "task.resource" must be a defined.', task);
    const cleanups = [];
    task.$destroy$ = noSerialize((() => {
        cleanups.forEach((fn => {
            try {
                fn();
            } catch (err) {
                container.handleError(err, host);
            }
        })), done = !0;
    }));
    const resourceTarget = unwrapStore(resource);
    const opts = {
        track: (obj, prop) => {
            const ctx = newInvokeContext();
            return ctx.$effectSubscriber$ = getSubscriber(task, ":"), ctx.$container$ = container, 
            invoke(ctx, (() => isFunction(obj) ? obj() : prop ? obj[prop] : isSignal(obj) ? obj.value : obj));
        },
        cleanup(fn) {
            "function" == typeof fn && cleanups.push(fn);
        },
        cache(policy) {
            let milliseconds = 0;
            milliseconds = "immutable" === policy ? 1 / 0 : policy, resource._cache = milliseconds;
        },
        previous: resourceTarget._resolved
    };
    let resolve;
    let reject;
    let done = !1;
    const setState = (resolved, value) => !done && (done = !0, resolved ? (done = !0, 
    resource.loading = !1, resource._state = "resolved", resource._resolved = value, 
    resource._error = void 0, resolve(value)) : (done = !0, resource.loading = !1, resource._state = "rejected", 
    resource._error = value, reject(value)), !0);
    cleanups.push((() => {
        if (!0 === untrack((() => resource.loading))) {
            const value = untrack((() => resource._resolved));
            setState(!0, value);
        }
    })), invoke(iCtx, (() => {
        resource._state = "pending", resource.loading = !isServerPlatform();
        (resource.value = new Promise(((r, re) => {
            resolve = r, reject = re;
        }))).catch(ignoreErrorToPreventNodeFromCrashing);
    }));
    const promise = safeCall((() => Promise.resolve(taskFn(opts))), (value => {
        setState(!0, value);
    }), (err => {
        if (isPromise(err)) {
            return err.then((() => runResource(task, container, host)));
        }
        setState(!1, err);
    }));
    const timeout = resourceTarget._timeout;
    return timeout > 0 ? Promise.race([ promise, delay(timeout).then((() => {
        setState(!1, new Error("timeout")) && cleanupTask(task);
    })) ]) : promise;
};

const ignoreErrorToPreventNodeFromCrashing = () => {};

const aVNodePath = [];

const bVNodePath = [];

const vnode_documentPosition = (a, b, rootVNode) => {
    if (a === b) {
        return 0;
    }
    let aDepth = -1;
    let bDepth = -1;
    for (;a; ) {
        a = (aVNodePath[++aDepth] = a)[1] || rootVNode && vnode_getProp(a, "q:sparent", (id => vnode_locate(rootVNode, id)));
    }
    for (;b; ) {
        b = (bVNodePath[++bDepth] = b)[1] || rootVNode && vnode_getProp(b, "q:sparent", (id => vnode_locate(rootVNode, id)));
    }
    for (;aDepth >= 0 && bDepth >= 0; ) {
        if ((a = aVNodePath[aDepth]) !== (b = bVNodePath[bDepth])) {
            let cursor = b;
            do {
                if (cursor = vnode_getNextSibling(cursor), cursor === a) {
                    return 1;
                }
            } while (cursor);
            cursor = b;
            do {
                if (cursor = vnode_getPreviousSibling(cursor), cursor === a) {
                    return -1;
                }
            } while (cursor);
            return rootVNode && vnode_getProp(b, "q:sparent", (id => vnode_locate(rootVNode, id))) ? -1 : 1;
        }
        aDepth--, bDepth--;
    }
    return aDepth < bDepth ? -1 : 1;
};

const aSsrNodePath = [];

const bSsrNodePath = [];

const ssrNodeDocumentPosition = (a, b) => {
    if (a === b) {
        return 0;
    }
    let aDepth = -1;
    let bDepth = -1;
    for (;a; ) {
        a = (aSsrNodePath[++aDepth] = a).currentComponentNode;
    }
    for (;b; ) {
        b = (bSsrNodePath[++bDepth] = b).currentComponentNode;
    }
    for (;aDepth >= 0 && bDepth >= 0; ) {
        if ((a = aSsrNodePath[aDepth]) !== (b = bSsrNodePath[bDepth])) {
            return 1;
        }
        aDepth--, bDepth--;
    }
    return aDepth < bDepth ? -1 : 1;
};

const DEBUG = !1;

const getPromise = chore => chore.$promise$ ||= new Promise((resolve => {
    chore.$resolve$ = resolve;
}));

const createScheduler = (container, scheduleDrain, journalFlush) => {
    const choreQueue = [];
    const qrlRuns = [];
    let currentChore = null;
    let drainScheduled = !1;
    return function schedule(type, hostOrTask = null, targetOrQrl = null, payload = null) {
        const isServer = !isDomContainer(container);
        const isComponentSsr = isServer && 6 === type;
        const runLater = 255 !== type && !isComponentSsr && 2 !== type;
        const isTask = 3 === type || 32 === type || 48 === type;
        const isClientOnly = 16 === type || 4 === type || 5 === type;
        if (isServer && isClientOnly) {
            return;
        }
        isTask && (hostOrTask.$flags$ |= 8);
        let chore = {
            $type$: type,
            $idx$: isTask ? hostOrTask.$index$ : "string" == typeof targetOrQrl ? targetOrQrl : 0,
            $host$: isTask ? hostOrTask.$el$ : hostOrTask,
            $target$: targetOrQrl,
            $payload$: isTask ? hostOrTask : payload,
            $resolve$: null,
            $promise$: null,
            $returnValue$: null,
            $executed$: !1
        };
        chore = function(sortedArray, value, rootVNode) {
            const idx = function(sortedArray, value, rootVNode) {
                let bottom = 0;
                let top = sortedArray.length;
                for (;bottom < top; ) {
                    const middle = bottom + (top - bottom >> 1);
                    const comp = choreComparator(value, sortedArray[middle], rootVNode);
                    if (comp < 0) {
                        top = middle;
                    } else {
                        if (!(comp > 0)) {
                            return middle;
                        }
                        bottom = middle + 1;
                    }
                }
                return ~bottom;
            }(sortedArray, value, rootVNode);
            if (idx < 0) {
                return sortedArray.splice(~idx, 0, value), value;
            }
            const existing = sortedArray[idx];
            4 === existing.$type$ && (existing.$payload$ = value.$payload$);
            existing.$executed$ && (existing.$executed$ = !1);
            return existing;
        }(choreQueue, chore, container.rootVNode || null), !drainScheduled && runLater && (drainScheduled = !0, 
        schedule(16), scheduleDrain()?.catch?.((() => {})));
        return runLater ? getPromise(chore) : drainUpTo(chore, isServer);
    };
    function drainUpTo(runUptoChore, isServer) {
        let maxRetries = 5e3;
        for (;choreQueue.length; ) {
            if (maxRetries-- < 0) {
                throw new Error("drainUpTo: max retries reached");
            }
            if (currentChore) {
                return getPromise(currentChore).then((() => drainUpTo(runUptoChore, isServer))).catch((e => {
                    container.handleError(e, currentChore?.$host$);
                }));
            }
            const nextChore = choreQueue[0];
            if (nextChore.$executed$) {
                if (choreQueue.shift(), nextChore === runUptoChore) {
                    break;
                }
            } else {
                vNodeAlreadyDeleted(nextChore) && 48 !== nextChore.$type$ ? choreQueue.shift() : executeChore(nextChore, isServer);
            }
        }
        return runUptoChore.$returnValue$;
    }
    function executeChore(chore, isServer) {
        const host = chore.$host$;
        assertEqual(currentChore, null, "Chore already running."), currentChore = chore;
        let returnValue = null;
        try {
            switch (chore.$type$) {
              case 255:
                isServer && (drainScheduled = !1);
                break;

              case 16:
                returnValue = journalFlush(), drainScheduled = !1;
                break;

              case 6:
                returnValue = safeCall((() => executeComponent(container, host, host, chore.$target$, chore.$payload$)), (jsx => {
                    if (isServer) {
                        return jsx;
                    }
                    {
                        const styleScopedId = container.getHostProp(host, "q:sstyle");
                        return retryOnPromise((() => vnode_diff(container, jsx, host, addComponentStylePrefix(styleScopedId))));
                    }
                }), (err => container.handleError(err, host)));
                break;

              case 2:
                {
                    const fn = chore.$target$.getFn();
                    const result = retryOnPromise((() => fn(...chore.$payload$)));
                    if (isPromise(result)) {
                        const handled = result.finally((() => {
                            qrlRuns.splice(qrlRuns.indexOf(handled), 1);
                        })).catch((error => {
                            container.handleError(error, chore.$host$);
                        }));
                        return qrlRuns.push(handled), chore.$returnValue$ = handled, chore.$resolve$?.(handled), 
                        currentChore = null, void (chore.$executed$ = !0);
                    }
                    returnValue = null;
                }
                break;

              case 3:
              case 32:
                {
                    const payload = chore.$payload$;
                    if (4 & payload.$flags$) {
                        const result = runResource(payload, container, host);
                        returnValue = isServer ? result : null;
                    } else {
                        returnValue = runTask(payload, container, host);
                    }
                }
                break;

              case 48:
                cleanupTask(chore.$payload$);
                break;

              case 4:
                {
                    const parentVirtualNode = chore.$target$;
                    let jsx = chore.$payload$;
                    isSignal(jsx) && (jsx = jsx.value), returnValue = retryOnPromise((() => vnode_diff(container, jsx, parentVirtualNode, null)));
                }
                break;

              case 5:
                {
                    const virtualNode = chore.$host$;
                    const payload = chore.$payload$;
                    let value = payload.$value$;
                    isSignal(value) && (value = value.value);
                    const isConst = payload.$isConst$;
                    const journal = container.$journal$;
                    const property = chore.$idx$;
                    const serializedValue = serializeAttribute(property, value, payload.$scopedStyleIdPrefix$);
                    if (isConst) {
                        journal.push(2, virtualNode[6], property, serializedValue);
                    } else {
                        vnode_setAttr(journal, virtualNode, property, serializedValue);
                    }
                }
                break;

              case 1:
                {
                    const target = chore.$target$;
                    returnValue = target.resolved ? null : target.resolve();
                }
                break;

              case 7:
                {
                    const target = chore.$target$;
                    const forceRunEffects = target.$forceRunEffects$;
                    if (target.$forceRunEffects$ = !1, !target.$effects$?.size) {
                        break;
                    }
                    returnValue = retryOnPromise((() => {
                        (target.$computeIfNeeded$() || forceRunEffects) && triggerEffects(container, target, target.$effects$);
                    }));
                }
            }
        } catch (e) {
            returnValue = Promise.reject(e);
        }
        const after = (value, error) => {
            currentChore = null, chore.$executed$ = !0, error ? container.handleError(error, host) : (chore.$returnValue$ = value, 
            chore.$resolve$?.(value));
        };
        isPromise(returnValue) ? (chore.$promise$ = returnValue.then(after, (error => after(void 0, error))), 
        chore.$resolve$?.(chore.$promise$), chore.$resolve$ = void 0) : after(returnValue);
    }
    function choreComparator(a, b, rootVNode) {
        const macroTypeDiff = (240 & a.$type$) - (240 & b.$type$);
        if (0 !== macroTypeDiff) {
            return macroTypeDiff;
        }
        const aHost = a.$host$;
        const bHost = b.$host$;
        if (aHost !== bHost && null !== aHost && null !== bHost) {
            if (vnode_isVNode(aHost) && vnode_isVNode(bHost)) {
                const hostDiff = vnode_documentPosition(aHost, bHost, rootVNode);
                if (0 !== hostDiff) {
                    return hostDiff;
                }
            } else {
                assertFalse(vnode_isVNode(aHost), "expected aHost to be SSRNode but it is a VNode"), 
                assertFalse(vnode_isVNode(bHost), "expected bHost to be SSRNode but it is a VNode");
                const errorMessage = `SERVER: during HTML streaming, re-running tasks on a different host is not allowed.\n          You are attempting to change a state that has already been streamed to the client.\n          This can lead to inconsistencies between Server-Side Rendering (SSR) and Client-Side Rendering (CSR).\n          Problematic Node: ${aHost.toString()}`;
                logWarn(errorMessage);
                const hostDiff = ssrNodeDocumentPosition(aHost, bHost);
                if (0 !== hostDiff) {
                    return hostDiff;
                }
            }
        }
        const microTypeDiff = (15 & a.$type$) - (15 & b.$type$);
        if (0 !== microTypeDiff) {
            return microTypeDiff;
        }
        const idxDiff = toNumber(a.$idx$) - toNumber(b.$idx$);
        return 0 !== idxDiff ? idxDiff : a.$target$ !== b.$target$ || a.$payload$ !== b.$payload$ || b === currentChore ? 1 : 0;
    }
};

const toNumber = value => "number" == typeof value ? value : -1;

function vNodeAlreadyDeleted(chore) {
    return !!(chore.$host$ && vnode_isVNode(chore.$host$) && 32 & chore.$host$[0]);
}

function debugChoreTypeToString(type) {
    return {
        1: "QRL_RESOLVE",
        2: "RUN_QRL",
        3: "TASK",
        4: "NODE_DIFF",
        5: "NODE_PROP",
        6: "COMPONENT",
        7: "RECOMPUTE_SIGNAL",
        16: "JOURNAL_FLUSH",
        32: "VISIBLE",
        48: "CLEANUP_VISIBLE",
        255: "WAIT_FOR_ALL"
    }[type] || "UNKNOWN: " + type;
}

function debugChoreToString(chore) {
    const type = debugChoreTypeToString(chore.$type$);
    const host = String(chore.$host$).replaceAll(/\n.*/gim, "");
    const qrlTarget = chore.$target$?.$symbol$;
    return `Chore(${type} ${1 === chore.$type$ || 2 === chore.$type$ ? qrlTarget : host} ${chore.$idx$})`;
}

function debugTrace(action, arg, currentChore, queue) {
    const lines = [ "===========================\nScheduler: " + action ];
    arg && !("$type$" in arg) && lines.push("      arg: " + String(arg).replaceAll(/\n.*/gim, "")), 
    queue && queue.forEach((chore => {
        lines.push(`     ${chore === arg ? ">>>" : "   "} > ` + (chore === currentChore ? "[running] " : "") + debugChoreToString(chore));
    })), console.log(lines.join("\n") + "\n");
}

class _SharedContainer {
    $version$;
    $scheduler$;
    $storeProxyMap$;
    $locale$;
    $getObjectById$;
    $serverData$;
    $currentUniqueId$=0;
    $instanceHash$=null;
    $buildBase$=null;
    constructor(scheduleDrain, journalFlush, serverData, locale) {
        this.$serverData$ = serverData, this.$locale$ = locale, this.$version$ = version, 
        this.$storeProxyMap$ = new WeakMap, this.$getObjectById$ = () => {
            throw Error("Not implemented");
        }, this.$scheduler$ = createScheduler(this, scheduleDrain, journalFlush);
    }
    trackSignalValue(signal, subscriber, property, data) {
        return trackSignalAndAssignHost(signal, subscriber, property, this, data);
    }
    serializationCtxFactory(NodeConstructor, DomRefConstructor, symbolToChunkResolver, writer, prepVNodeData) {
        return createSerializationContext(NodeConstructor, DomRefConstructor, symbolToChunkResolver, this.getHostProp.bind(this), this.setHostProp.bind(this), this.$storeProxyMap$, writer, prepVNodeData);
    }
}

const VNodeDataSeparator = {
    REFERENCE_CH: "~",
    REFERENCE: 126,
    ADVANCE_1_CH: "!",
    ADVANCE_1: 33,
    ADVANCE_2_CH: '"',
    ADVANCE_2: 34,
    ADVANCE_4_CH: "#",
    ADVANCE_4: 35,
    ADVANCE_8_CH: "$",
    ADVANCE_8: 36,
    ADVANCE_16_CH: "%",
    ADVANCE_16: 37,
    ADVANCE_32_CH: "&",
    ADVANCE_32: 38,
    ADVANCE_64_CH: "'",
    ADVANCE_64: 39,
    ADVANCE_128_CH: "(",
    ADVANCE_128: 40,
    ADVANCE_256_CH: ")",
    ADVANCE_256: 41,
    ADVANCE_512_CH: "*",
    ADVANCE_512: 42,
    ADVANCE_1024_CH: "+",
    ADVANCE_1024: 43,
    ADVANCE_2048_CH: ",",
    ADVANCE_2048: 44,
    ADVANCE_4096_CH: "-",
    ADVANCE_4096: 45,
    ADVANCE_8192_CH: ".",
    ADVANCE_8192: 46
};

const VNodeDataChar = {
    OPEN: 123,
    OPEN_CHAR: "{",
    CLOSE: 125,
    CLOSE_CHAR: "}",
    SCOPED_STYLE: 59,
    SCOPED_STYLE_CHAR: ";",
    RENDER_FN: 60,
    RENDER_FN_CHAR: "<",
    ID: 61,
    ID_CHAR: "=",
    PROPS: 62,
    PROPS_CHAR: ">",
    SLOT_PARENT: 63,
    SLOT_PARENT_CHAR: "?",
    KEY: 64,
    KEY_CHAR: "@",
    SEQ: 91,
    SEQ_CHAR: "[",
    DON_T_USE: 92,
    DON_T_USE_CHAR: "\\",
    CONTEXT: 93,
    CONTEXT_CHAR: "]",
    SEQ_IDX: 94,
    SEQ_IDX_CHAR: "^",
    BACK_REFS: 96,
    BACK_REFS_CHAR: "`",
    SEPARATOR: 124,
    SEPARATOR_CHAR: "|",
    SLOT: 126,
    SLOT_CHAR: "~"
};

function processVNodeData$1(document) {
    const vNodeDataMap = document.qVNodeData || (document.qVNodeData = new WeakMap);
    const prototype = document.body;
    const getAttribute = prototype.getAttribute;
    const hasAttribute = prototype.hasAttribute;
    const getNodeType = ((prototype, name) => {
        let getter;
        for (;prototype && !(getter = Object.getOwnPropertyDescriptor(prototype, name)?.get); ) {
            prototype = Object.getPrototypeOf(prototype);
        }
        return getter || function() {
            return this[name];
        };
    })(prototype, "nodeType");
    const attachVnodeDataAndRefs = element => {
        Array.from(element.querySelectorAll('script[type="qwik/vnode"]')).forEach((script => {
            script.setAttribute("type", "x-qwik/vnode");
            const qContainerElement = script.closest("[q\\:container]");
            qContainerElement.qVnodeData = script.textContent, qContainerElement.qVNodeRefs = new Map;
        })), element.querySelectorAll("[q\\:shadowroot]").forEach((parent => {
            const shadowRoot = parent.shadowRoot;
            shadowRoot && attachVnodeDataAndRefs(shadowRoot);
        }));
    };
    attachVnodeDataAndRefs(document);
    const getFastNodeType = node => {
        const nodeType = getNodeType.call(node);
        if (1 === nodeType) {
            if (null === getAttribute.call(node, "q:container")) {
                if (hasAttribute.call(node, "q:shadowroot")) {
                    return 6;
                }
                return hasAttribute.call(node, ":") ? 2 : 0;
            }
            return 3;
        }
        if (8 === nodeType) {
            const nodeValue = node.nodeValue || "";
            if (nodeValue.startsWith("q:container-island")) {
                return 65;
            }
            if (nodeValue.startsWith("q:ignore")) {
                return 16;
            }
            if (nodeValue.startsWith("q:container")) {
                return 5;
            }
            if (nodeValue.startsWith("/q:container-island")) {
                return 128;
            }
            if (nodeValue.startsWith("/q:ignore")) {
                return 32;
            }
            if (nodeValue.startsWith("/q:container")) {
                return 8;
            }
        }
        return 0;
    };
    const isSeparator = ch => VNodeDataSeparator.ADVANCE_1 <= ch && ch <= VNodeDataSeparator.ADVANCE_8192;
    const findVDataSectionEnd = (vData, start, end) => {
        let depth = 0;
        for (;start < end; ) {
            const ch = vData.charCodeAt(start);
            if (0 === depth && isSeparator(ch)) {
                break;
            }
            ch === VNodeDataChar.OPEN ? depth++ : ch === VNodeDataChar.CLOSE && depth--, start++;
        }
        return start;
    };
    const nextSibling = node => {
        for (;node && (node = node.nextSibling) && 0 === getFastNodeType(node); ) {}
        return node;
    };
    const firstChild = node => {
        for (;node && (node = node.firstChild) && 0 === getFastNodeType(node); ) {}
        return node;
    };
    const walkContainer = (walker, containerNode, node, exitNode, vData, qVNodeRefs) => {
        const vData_length = vData.length;
        let elementIdx = 0;
        let vNodeElementIndex = -1;
        let vData_start = 0;
        let vData_end = 0;
        let ch = 0;
        let needsToStoreRef = -1;
        let nextNode = null;
        const howManyElementsToSkip = () => {
            let elementsToSkip = 0;
            for (;isSeparator(ch = vData.charCodeAt(vData_start)) && (elementsToSkip += 1 << ch - VNodeDataSeparator.ADVANCE_1, 
            vData_start++, !(vData_start >= vData_length)); ) {}
            return elementsToSkip;
        };
        do {
            if (node === exitNode) {
                return;
            }
            nextNode = null;
            const nodeType = node == containerNode ? 2 : getFastNodeType(node);
            if (3 === nodeType) {
                const container = node;
                let cursor = node;
                for (;cursor && !(nextNode = nextSibling(cursor)); ) {
                    cursor = cursor.parentNode;
                }
                walkContainer(walker, container, node, nextNode, container.qVnodeData || "", container.qVNodeRefs);
            } else if (16 === nodeType) {
                let islandNode = node;
                do {
                    if (islandNode = walker.nextNode(), !islandNode) {
                        throw new Error(`Island inside \x3c!--${node?.nodeValue}--\x3e not found!`);
                    }
                } while (65 !== getFastNodeType(islandNode));
                nextNode = null;
            } else if (128 === nodeType) {
                nextNode = node;
                do {
                    if (nextNode = walker.nextNode(), !nextNode) {
                        throw new Error("Ignore block not closed!");
                    }
                } while (32 !== getFastNodeType(nextNode));
                nextNode = null;
            } else if (5 === nodeType) {
                nextNode = node;
                do {
                    if (nextNode = nextSibling(nextNode), !nextNode) {
                        throw new Error(`\x3c!--${node?.nodeValue}--\x3e not closed!`);
                    }
                } while (8 !== getFastNodeType(nextNode));
                walkContainer(walker, node, node, nextNode, "", null);
            } else if (6 === nodeType) {
                nextNode = nextSibling(node);
                const shadowRootContainer = node;
                const shadowRoot = shadowRootContainer?.shadowRoot;
                shadowRoot && walkContainer(document.createTreeWalker(shadowRoot, 129), null, firstChild(shadowRoot), null, "", null);
            }
            if (!(2 & ~nodeType)) {
                if (vNodeElementIndex < elementIdx) {
                    if (-1 === vNodeElementIndex && (vNodeElementIndex = 0), vData_start = vData_end, 
                    vData_start < vData_length) {
                        vNodeElementIndex += howManyElementsToSkip();
                        ch === VNodeDataSeparator.REFERENCE && (needsToStoreRef = vNodeElementIndex, vData_start++, 
                        ch = vData_start < vData_length ? vData.charCodeAt(vData_end) : VNodeDataSeparator.ADVANCE_1), 
                        vData_end = findVDataSectionEnd(vData, vData_start, vData_length);
                    } else {
                        vNodeElementIndex = Number.MAX_SAFE_INTEGER;
                    }
                }
                if (elementIdx === vNodeElementIndex) {
                    needsToStoreRef === elementIdx && qVNodeRefs.set(elementIdx, node);
                    const instructions = vData.substring(vData_start, vData_end);
                    vNodeDataMap.set(node, instructions);
                }
                elementIdx++;
            }
        } while (node = nextNode || walker.nextNode());
    };
    const walker = document.createTreeWalker(document, 129);
    walkContainer(walker, null, walker.firstChild(), null, "", null);
}

function getDomContainer(element) {
    const qContainerElement = _getQContainerElement(element);
    if (!qContainerElement) {
        throw qError(41);
    }
    return getDomContainerFromQContainerElement(qContainerElement);
}

function getDomContainerFromQContainerElement(qContainerElement) {
    let container = qContainerElement.qContainer;
    return container || (container = new DomContainer(qContainerElement)), container;
}

function _getQContainerElement(element) {
    return (Array.isArray(element) ? vnode_getDomParent(element) : element).closest(QContainerSelector);
}

const isDomContainer = container => container instanceof DomContainer;

class DomContainer extends _SharedContainer {
    element;
    qContainer;
    qManifestHash;
    rootVNode;
    document;
    $journal$;
    renderDone=null;
    $rawStateData$;
    $storeProxyMap$=new WeakMap;
    $qFuncs$;
    $instanceHash$;
    vNodeLocate=id => vnode_locate(this.rootVNode, id);
    $stateData$;
    $styleIds$=null;
    $renderCount$=0;
    constructor(element) {
        if (super((() => this.scheduleRender()), (() => vnode_applyJournal(this.$journal$)), {}, element.getAttribute("q:locale")), 
        this.qContainer = element.getAttribute("q:container"), !this.qContainer) {
            throw qError(42);
        }
        this.$journal$ = [ 3, element.ownerDocument ], this.document = element.ownerDocument, 
        this.element = element, this.$buildBase$ = element.getAttribute("q:base"), this.$instanceHash$ = element.getAttribute("q:instance"), 
        this.qManifestHash = element.getAttribute("q:manifest-hash"), this.rootVNode = vnode_newUnMaterializedElement(this.element), 
        this.$rawStateData$ = [], this.$stateData$ = [];
        const document = this.element.ownerDocument;
        document.qVNodeData || processVNodeData$1(document), this.$qFuncs$ = getQFuncs(document, this.$instanceHash$) || EMPTY_ARRAY, 
        this.$setServerData$(), element.setAttribute("q:container", "resumed"), element.qContainer = this;
        const qwikStates = element.querySelectorAll('script[type="qwik/state"]');
        if (0 !== qwikStates.length) {
            this.$rawStateData$ = JSON.parse(qwikStates[qwikStates.length - 1].textContent), 
            this.$stateData$ = wrapDeserializerProxy(this, this.$rawStateData$);
        }
    }
    $setRawState$(id, vParent) {
        this.$stateData$[id] = vParent;
    }
    parseQRL(qrl) {
        return inflateQRL(this, parseQRL(qrl));
    }
    handleError(err, host) {
        const errorStore = host && this.resolveContext(host, ERROR_CONTEXT);
        if (!errorStore) {
            throw err;
        }
        errorStore.error = err;
    }
    setContext(host, context, value) {
        let ctx = this.getHostProp(host, "q:ctx");
        ctx || this.setHostProp(host, "q:ctx", ctx = []), mapArray_set(ctx, context.id, value, 0);
    }
    resolveContext(host, contextId) {
        for (;host; ) {
            const ctx = this.getHostProp(host, "q:ctx");
            if (ctx) {
                const value = mapArray_get(ctx, contextId.id, 0);
                if (value) {
                    return value;
                }
            }
            host = this.getParentHost(host);
        }
    }
    getParentHost(host) {
        let vNode = vnode_getParent(host);
        for (;vNode; ) {
            if (vnode_isVirtualVNode(vNode)) {
                if (null !== vnode_getProp(vNode, "q:renderFn", null)) {
                    return vNode;
                }
                vNode = vnode_getParent(vNode) || vnode_getProp(vNode, "q:sparent", this.vNodeLocate);
            } else {
                vNode = vnode_getParent(vNode);
            }
        }
        return null;
    }
    setHostProp(host, name, value) {
        vnode_setProp(host, name, value);
    }
    getHostProp(host, name) {
        const vNode = host;
        let getObjectById = null;
        switch (name) {
          case "q:seq":
          case "q:props":
          case "q:renderFn":
          case "q:ctx":
          case "q:brefs":
            getObjectById = this.$getObjectById$;
            break;

          case "q:seqIdx":
          case ":onIdx":
            getObjectById = parseInt;
        }
        return vnode_getProp(vNode, name, getObjectById);
    }
    scheduleRender() {
        return this.$renderCount$++, this.renderDone ||= getPlatform().nextTick((() => this.processChores())), 
        this.renderDone.finally((() => emitEvent("qrender", {
            instanceHash: this.$instanceHash$,
            renderCount: this.$renderCount$
        })));
    }
    processChores() {
        let renderCount = this.$renderCount$;
        const result = this.$scheduler$(255);
        if (isPromise(result)) {
            return result.then((async () => {
                for (;renderCount !== this.$renderCount$; ) {
                    renderCount = this.$renderCount$, await this.$scheduler$(255);
                }
                this.renderDone = null;
            }));
        }
        renderCount === this.$renderCount$ ? this.renderDone = null : this.processChores();
    }
    ensureProjectionResolved(vNode) {
        if (!(16 & vNode[0])) {
            vNode[0] |= 16;
            const props = vnode_getProps(vNode);
            for (let i = 0; i < props.length; i += 2) {
                if (isSlotProp(props[i])) {
                    const value = props[i + 1];
                    "string" == typeof value && (props[i + 1] = this.vNodeLocate(value));
                }
            }
        }
    }
    $getObjectById$=id => ("string" == typeof id && (id = parseFloat(id)), assertTrue(id < this.$rawStateData$.length / 2, `Invalid reference: ${id} >= ${this.$rawStateData$.length / 2}`), 
    this.$stateData$[id]);
    getSyncFn(id) {
        const fn = this.$qFuncs$[id];
        return assertTrue("function" == typeof fn, "Invalid reference: " + id), fn;
    }
    $appendStyle$(content, styleId, host, scoped) {
        if (scoped) {
            const scopedStyleIdsString = this.getHostProp(host, "q:sstyle");
            const scopedStyleIds = new Set(convertScopedStyleIdsToArray(scopedStyleIdsString));
            scopedStyleIds.add(styleId), this.setHostProp(host, "q:sstyle", convertStyleIdsToString(scopedStyleIds));
        }
        if (null == this.$styleIds$ && (this.$styleIds$ = new Set, this.element.querySelectorAll(QStyleSelector).forEach((style => {
            this.$styleIds$.add(style.getAttribute(QStyle));
        }))), !this.$styleIds$.has(styleId)) {
            this.$styleIds$.add(styleId);
            const styleElement = this.document.createElement("style");
            styleElement.setAttribute(QStyle, styleId), styleElement.textContent = content, 
            this.$journal$.push(5, this.document.head, null, styleElement);
        }
    }
    $setServerData$() {
        const containerAttributes = {};
        const attrs = this.element.attributes;
        if (attrs) {
            for (let index = 0; index < attrs.length; index++) {
                const attr = attrs[index];
                ":" !== attr.name && (containerAttributes[attr.name] = attr.value);
            }
        }
        this.$serverData$ = {
            containerAttributes
        };
    }
}

let _context;

const tryGetInvokeContext = () => {
    if (!_context) {
        const context = "undefined" != typeof document && document && document.__q_context__;
        if (!context) {
            return;
        }
        return isArray(context) ? document.__q_context__ = newInvokeContextFromTuple(context) : context;
    }
    return _context;
};

const getInvokeContext = () => {
    const ctx = tryGetInvokeContext();
    if (!ctx) {
        throw qError(14);
    }
    return ctx;
};

const useInvokeContext = () => {
    const ctx = tryGetInvokeContext();
    if (!ctx || "qRender" !== ctx.$event$) {
        throw qError(20);
    }
    return assertDefined(ctx.$hostElement$, "invoke: $hostElement$ must be defined", ctx), 
    assertDefined(ctx.$effectSubscriber$, "invoke: $effectSubscriber$ must be defined", ctx), 
    ctx;
};

function useBindInvokeContext(fn) {
    if (null == fn) {
        return fn;
    }
    const ctx = getInvokeContext();
    return function(...args) {
        return invokeApply.call(this, ctx, fn, args);
    };
}

function invoke(context, fn, ...args) {
    return invokeApply.call(this, context, fn, args);
}

function invokeApply(context, fn, args) {
    const previousContext = _context;
    let returnValue;
    try {
        _context = context, returnValue = fn.apply(this, args);
    } finally {
        _context = previousContext;
    }
    return returnValue;
}

const newInvokeContextFromTuple = ([element, event, url]) => {
    const domContainer = getDomContainer(element);
    const hostElement = vnode_locate(domContainer.rootVNode, element);
    const locale = domContainer.$locale$;
    return locale && setLocale(locale), newInvokeContext(locale, hostElement, element, event, url);
};

const newInvokeContext = (locale, hostElement, element, event, url) => {
    const ctx = {
        $url$: url,
        $i$: 0,
        $hostElement$: hostElement,
        $element$: element,
        $event$: event,
        $qrl$: void 0,
        $effectSubscriber$: void 0,
        $locale$: locale || ("object" == typeof event && event && "locale" in event ? event.locale : void 0),
        $container$: void 0
    };
    return seal(), ctx;
};

const untrack = fn => invoke(void 0, fn);

const trackInvocation = /*#__PURE__*/ newInvokeContext(void 0, void 0, void 0, "qRender");

const trackSignal = (fn, subscriber, property, container, data) => {
    const previousSubscriber = trackInvocation.$effectSubscriber$;
    const previousContainer = trackInvocation.$container$;
    try {
        return trackInvocation.$effectSubscriber$ = getSubscriber(subscriber, property, data), 
        trackInvocation.$container$ = container, invoke(trackInvocation, fn);
    } finally {
        trackInvocation.$effectSubscriber$ = previousSubscriber, trackInvocation.$container$ = previousContainer;
    }
};

const trackSignalAndAssignHost = (value, host, property, container, data) => (value instanceof WrappedSignal && value.$hostElement$ !== host && host && (value.$hostElement$ = host), 
trackSignal((() => value.value), host, property, container, data));

const _getContextElement = () => {
    const iCtx = tryGetInvokeContext();
    if (iCtx) {
        const hostElement = iCtx.$hostElement$;
        let element = null;
        return null != hostElement && (vnode_isVNode(hostElement) ? vnode_isElementVNode(hostElement) && (element = vnode_getNode(hostElement)) : element = hostElement), 
        element ?? iCtx.$qrl$?.$setContainer$(void 0);
    }
};

const _getContextEvent = () => {
    const iCtx = tryGetInvokeContext();
    if (iCtx) {
        return iCtx.$event$;
    }
};

const _jsxBranch = input => input;

const _waitUntilRendered = elm => {
    const containerEl = _getQContainerElement(elm);
    if (!containerEl) {
        return Promise.resolve();
    }
    const container = containerEl.qContainer;
    return container?.renderDone ?? Promise.resolve();
};

const queueQRL = (...args) => {
    const [runQrl] = useLexicalScope();
    const context = getInvokeContext();
    const hostElement = context.$hostElement$;
    if (!hostElement) {
        return;
    }
    const scheduler = getDomContainer(context.$element$).$scheduler$;
    if (!scheduler) {
        throw qError(1);
    }
    return scheduler(2, hostElement, runQrl, args);
};

function isAsyncGenerator(value) {
    return !!value[Symbol.asyncIterator];
}

const applyInlineComponent = (ssr, componentHost, inlineComponentFunction, jsx) => {
    const host = ssr.getLastNode();
    return executeComponent(ssr, host, componentHost, inlineComponentFunction, jsx.props);
};

const applyQwikComponentBody = (ssr, jsx, component) => {
    const host = ssr.getLastNode();
    const [componentQrl] = component[SERIALIZABLE_STATE];
    const srcProps = jsx.props;
    srcProps && srcProps.children && delete srcProps.children;
    const scheduler = ssr.$scheduler$;
    return host.setProp("q:renderFn", componentQrl), host.setProp("q:props", srcProps), 
    null !== jsx.key && host.setProp("q:key", jsx.key), scheduler(6, host, componentQrl, srcProps);
};

class ParentComponentData {
    $scopedStyle$;
    $componentFrame$;
    constructor($scopedStyle$, $componentFrame$) {
        this.$scopedStyle$ = $scopedStyle$, this.$componentFrame$ = $componentFrame$;
    }
}

async function _walkJSX(ssr, value, options) {
    const stack = [ value ];
    const enqueue = value => stack.push(value);
    await (async () => {
        for (;stack.length; ) {
            const value = stack.pop();
            if (value instanceof ParentComponentData) {
                options.currentStyleScoped = value.$scopedStyle$, options.parentComponentFrame = value.$componentFrame$;
            } else if ("function" != typeof value) {
                processJSXNode(ssr, enqueue, value, {
                    styleScoped: options.currentStyleScoped,
                    parentComponentFrame: options.parentComponentFrame
                });
            } else {
                if (value === Promise) {
                    stack.push(await stack.pop());
                    continue;
                }
                await value.apply(ssr);
            }
        }
    })();
}

function processJSXNode(ssr, enqueue, value, options) {
    if (null == value) {
        ssr.textNode("");
    } else if ("boolean" == typeof value) {
        ssr.textNode("");
    } else if ("number" == typeof value) {
        ssr.textNode(String(value));
    } else if ("string" == typeof value) {
        ssr.textNode(value);
    } else if ("object" == typeof value) {
        if (Array.isArray(value)) {
            for (let i = value.length - 1; i >= 0; i--) {
                enqueue(value[i]);
            }
        } else if (isSignal(value)) {
            ssr.openFragment(isDev ? [ DEBUG_TYPE, "S" ] : EMPTY_ARRAY);
            const signalNode = ssr.getLastNode();
            enqueue(ssr.closeFragment), enqueue(trackSignalAndAssignHost(value, signalNode, ".", ssr));
        } else if (isPromise(value)) {
            ssr.openFragment(isDev ? [ DEBUG_TYPE, "A" ] : EMPTY_ARRAY), enqueue(ssr.closeFragment), 
            enqueue(value), enqueue(Promise), enqueue((() => ssr.commentNode("qkssr-f")));
        } else if (isAsyncGenerator(value)) {
            enqueue((async () => {
                for await (const chunk of value) {
                    await _walkJSX(ssr, chunk, {
                        currentStyleScoped: options.styleScoped,
                        parentComponentFrame: options.parentComponentFrame
                    }), ssr.commentNode("qkssr-f");
                }
            }));
        } else {
            const jsx = value;
            const type = jsx.type;
            if ("string" == typeof type) {
                appendClassIfScopedStyleExists(jsx, options.styleScoped);
                let qwikInspectorAttrValue = null;
                isDev && jsx.dev && "head" !== jsx.type && (qwikInspectorAttrValue = getFileLocationFromJsx(jsx.dev));
                const innerHTML = ssr.openElement(type, varPropsToSsrAttrs(jsx.varProps, jsx.constProps, ssr.serializationCtx, options.styleScoped, jsx.key), constPropsToSsrAttrs(jsx.constProps, jsx.varProps, ssr.serializationCtx, options.styleScoped), qwikInspectorAttrValue);
                innerHTML && ssr.htmlNode(innerHTML), enqueue(ssr.closeElement), "head" === type ? (enqueue(ssr.additionalHeadNodes), 
                enqueue(ssr.emitQwikLoaderAtTopIfNeeded)) : "body" === type && enqueue(ssr.additionalBodyNodes);
                const children = jsx.children;
                null != children && enqueue(children);
            } else if (isFunction(type)) {
                if (type === Fragment) {
                    let attrs = null != jsx.key ? [ "q:key", jsx.key ] : EMPTY_ARRAY;
                    isDev && (attrs = [ DEBUG_TYPE, "F", ...attrs ]), ssr.openFragment(attrs), ssr.addCurrentElementFrameAsComponentChild(), 
                    enqueue(ssr.closeFragment);
                    const children = jsx.children;
                    null != children && enqueue(children);
                } else if (type === Slot) {
                    const componentFrame = options.parentComponentFrame || ssr.unclaimedProjectionComponentFrameQueue.shift();
                    if (componentFrame) {
                        const projectionAttrs = isDev ? [ DEBUG_TYPE, "P" ] : [];
                        projectionAttrs.push("q:sparent", componentFrame.componentNode.id || ""), ssr.openProjection(projectionAttrs);
                        const host = componentFrame.componentNode;
                        const node = ssr.getLastNode();
                        const slotName = getSlotName(host, jsx, ssr);
                        projectionAttrs.push(QSlot, slotName), enqueue(new ParentComponentData(options.styleScoped, options.parentComponentFrame)), 
                        enqueue(ssr.closeProjection);
                        const slotDefaultChildren = jsx.children || null;
                        const slotChildren = componentFrame.consumeChildrenForSlot(node, slotName) || slotDefaultChildren;
                        slotDefaultChildren && slotChildren !== slotDefaultChildren && ssr.addUnclaimedProjection(componentFrame, "", slotDefaultChildren), 
                        enqueue(slotChildren), enqueue(new ParentComponentData(componentFrame.projectionScopedStyle, componentFrame.projectionComponentFrame));
                    } else {
                        ssr.openFragment(isDev ? [ DEBUG_TYPE, "P" ] : EMPTY_ARRAY), ssr.closeFragment();
                    }
                } else if (type === SSRComment) {
                    ssr.commentNode(directGetPropsProxyProp(jsx, "data") || "");
                } else if (type === SSRStream) {
                    ssr.commentNode("qkssr-f");
                    const generator = jsx.children;
                    let value;
                    value = isFunction(generator) ? generator({
                        async write(chunk) {
                            await _walkJSX(ssr, chunk, {
                                currentStyleScoped: options.styleScoped,
                                parentComponentFrame: options.parentComponentFrame
                            }), ssr.commentNode("qkssr-f");
                        }
                    }) : generator, enqueue(value), isPromise(value) && enqueue(Promise);
                } else if (type === SSRRaw) {
                    ssr.htmlNode(directGetPropsProxyProp(jsx, "data"));
                } else if (isQwikComponent(type)) {
                    ssr.openComponent(isDev ? [ DEBUG_TYPE, "C" ] : []);
                    const host = ssr.getLastNode();
                    const componentFrame = ssr.getParentComponentFrame();
                    componentFrame.distributeChildrenIntoSlots(jsx.children, options.styleScoped, options.parentComponentFrame);
                    const jsxOutput = applyQwikComponentBody(ssr, jsx, type);
                    const compStyleComponentId = addComponentStylePrefix(host.getProp("q:sstyle"));
                    enqueue(new ParentComponentData(options.styleScoped, options.parentComponentFrame)), 
                    enqueue(ssr.closeComponent), enqueue(jsxOutput), isPromise(jsxOutput) && enqueue(Promise), 
                    enqueue(new ParentComponentData(compStyleComponentId, componentFrame));
                } else {
                    const inlineComponentProps = [ "q:key", jsx.key ];
                    ssr.openFragment(isDev ? [ DEBUG_TYPE, "I", ...inlineComponentProps ] : inlineComponentProps), 
                    enqueue(ssr.closeFragment);
                    const component = ssr.getComponentFrame(0);
                    const jsxOutput = applyInlineComponent(ssr, component && component.componentNode, type, jsx);
                    enqueue(jsxOutput), isPromise(jsxOutput) && enqueue(Promise);
                }
            }
        }
    }
}

function varPropsToSsrAttrs(varProps, constProps, serializationCtx, styleScopedId, key) {
    return toSsrAttrs(varProps, constProps, serializationCtx, !0, styleScopedId, key);
}

function constPropsToSsrAttrs(constProps, varProps, serializationCtx, styleScopedId) {
    return toSsrAttrs(constProps, varProps, serializationCtx, !1, styleScopedId);
}

function toSsrAttrs(record, anotherRecord, serializationCtx, pushMergedEventProps, styleScopedId, key) {
    if (null == record) {
        return null;
    }
    const ssrAttrs = [];
    for (const key in record) {
        let value = record[key];
        if (isJsxPropertyAnEventName(key)) {
            if (anotherRecord) {
                const anotherValue = getEventProp(anotherRecord, key);
                if (anotherValue) {
                    if (!pushMergedEventProps) {
                        continue;
                    }
                    value = getMergedEventPropValues(value, anotherValue);
                }
            }
            const eventValue = setEvent(serializationCtx, key, value);
            eventValue && ssrAttrs.push(jsxEventToHtmlAttribute(key), eventValue);
        } else {
            isSignal(value) ? isClassAttr(key) ? ssrAttrs.push(key, [ value, styleScopedId ]) : ssrAttrs.push(key, value) : (isPreventDefault(key) && addPreventDefaultEventToSerializationContext(serializationCtx, key), 
            value = serializeAttribute(key, value, styleScopedId), ssrAttrs.push(key, value));
        }
    }
    return null != key && ssrAttrs.push("q:key", key), ssrAttrs;
}

function getMergedEventPropValues(value, anotherValue) {
    let mergedValue = value;
    return Array.isArray(value) && Array.isArray(anotherValue) ? mergedValue = value.concat(anotherValue) : Array.isArray(mergedValue) ? mergedValue.push(anotherValue) : Array.isArray(anotherValue) ? (mergedValue = anotherValue, 
    mergedValue.push(value)) : mergedValue = [ value, anotherValue ], mergedValue;
}

function getEventProp(record, propKey) {
    const eventProp = propKey.toLowerCase();
    for (const prop in record) {
        if (prop.toLowerCase() === eventProp) {
            return record[prop];
        }
    }
    return null;
}

function setEvent(serializationCtx, key, rawValue) {
    let value = null;
    const qrls = rawValue;
    const appendToValue = valueToAppend => {
        value = (null == value ? "" : value + "\n") + valueToAppend;
    };
    const getQrlString = qrl => (qrl.$symbol$.startsWith("_") || !qrl.$captureRef$ && !qrl.$capture$ || (qrl = createQRL(null, "_run", queueQRL, null, null, [ qrl ])), 
    qrlToString(serializationCtx, qrl));
    if (Array.isArray(qrls)) {
        for (let i = 0; i <= qrls.length; i++) {
            const qrl = qrls[i];
            if (isQrl$1(qrl)) {
                appendToValue(getQrlString(qrl)), addQwikEventToSerializationContext(serializationCtx, key, qrl);
            } else if (null != qrl) {
                const nestedValue = setEvent(serializationCtx, key, qrl);
                nestedValue && appendToValue(nestedValue);
            }
        }
    } else {
        isQrl$1(qrls) && (value = getQrlString(qrls), addQwikEventToSerializationContext(serializationCtx, key, qrls));
    }
    return value;
}

function addQwikEventToSerializationContext(serializationCtx, key, qrl) {
    const eventName = getEventNameFromJsxEvent(key);
    eventName && (serializationCtx.$eventNames$.add(eventName), serializationCtx.$eventQrls$.add(qrl));
}

function addPreventDefaultEventToSerializationContext(serializationCtx, key) {
    const eventName = key.substring(15);
    eventName && serializationCtx.$eventNames$.add(eventName);
}

function getSlotName(host, jsx, ssr) {
    const constProps = jsx.constProps;
    if (constProps && "object" == typeof constProps && "name" in constProps) {
        const constValue = constProps.name;
        if (constValue instanceof WrappedSignal) {
            return trackSignalAndAssignHost(constValue, host, ":", ssr);
        }
    }
    return directGetPropsProxyProp(jsx, "name") || "";
}

function appendQwikInspectorAttribute(jsx, qwikInspectorAttrValue) {
    !qwikInspectorAttrValue || jsx.constProps && qwikInspectorAttr in jsx.constProps || ((jsx.constProps ||= {})[qwikInspectorAttr] = qwikInspectorAttrValue);
}

function appendClassIfScopedStyleExists(jsx, styleScoped) {
    !(null != directGetPropsProxyProp(jsx, "class")) && styleScoped && (jsx.constProps || (jsx.constProps = {}), 
    jsx.constProps.class = "");
}

const getValueProp = p0 => p0.value;

const getProp = (p0, p1) => p0[p1];

const getWrapped = args => new WrappedSignal(null, 1 === args.length ? getValueProp : getProp, args, null);

const _wrapProp = (...args) => {
    const obj = args[0];
    const prop = args.length < 2 ? "value" : args[1];
    if (!isObject(obj)) {
        return obj[prop];
    }
    if (isSignal(obj)) {
        return assertEqual(prop, "value", "Left side is a signal, prop must be value"), 
        obj instanceof WrappedSignal && 2 & obj.flags ? obj : getWrapped(args);
    }
    if (isPropsProxy(obj)) {
        const constProps = obj[_CONST_PROPS];
        if (constProps && prop in constProps) {
            return constProps[prop];
        }
    } else {
        const target = getStoreTarget(obj);
        if (target) {
            const value = target[prop];
            return isSignal(value) ? value : getWrapped(args);
        }
    }
    return getWrapped(args);
};

const _wrapStore = (obj, prop) => {
    const value = getStoreTarget(obj)[prop];
    return isSignal(value) ? value : new WrappedSignal(null, getProp, [ obj, prop ], null, 1);
};

const _wrapSignal = (obj, prop) => {
    const r = _wrapProp(obj, prop);
    return r === _IMMUTABLE ? obj[prop] : r;
};

const _fnSignal = (fn, args, fnStr) => new WrappedSignal(null, fn, args, fnStr || null);

function isStringifiable(value) {
    return null === value || "string" == typeof value || "number" == typeof value || "boolean" == typeof value;
}

const _jsxSorted = (type, varProps, constProps, children, flags, key) => {
    const processed = null == key ? null : String(key);
    const node = new JSXNodeImpl(type, varProps || {}, constProps || null, children, flags, processed);
    return seal(), node;
};

const _jsxSplit = (type, varProps, constProps, children, flags, key, dev) => {
    let sortedProps;
    return sortedProps = varProps ? Object.fromEntries(untrack((() => Object.entries(varProps))).filter((entry => {
        const attr = entry[0];
        return "children" === attr ? (children ??= entry[1], !1) : "key" === attr ? (key = entry[1], 
        !1) : !constProps || !(attr in constProps) || /^on[A-Z].*\$$/.test(attr);
    })).sort((([a], [b]) => a < b ? -1 : 1))) : "string" == typeof type ? EMPTY_OBJ : {}, 
    constProps && "children" in constProps && (children = constProps.children, constProps.children = void 0), 
    _jsxSorted(type, sortedProps, constProps, children, flags, key, dev);
};

const _jsxC = (type, mutable, _flags, key) => jsx(type, mutable, key);

const _jsxS = (type, mutable, immutable, _flags, key) => jsx(type, {
    ...immutable,
    ...mutable
}, key);

const _jsxQ = (type, mutable, immutable, children, _flags, key) => jsx(type, {
    ...immutable,
    ...mutable,
    children
}, key);

const jsx = (type, props, key) => _jsxSplit(type, props, null, null, 0, key || null);

const flattenArray = (array, dst) => {
    dst || (dst = []);
    for (const item of array) {
        isArray(item) ? flattenArray(item, dst) : dst.push(item);
    }
    return dst;
};

function h(type, props, ...children) {
    const normalizedProps = {
        children: arguments.length > 2 ? flattenArray(children) : null
    };
    let key = null;
    for (const i in props) {
        "key" == i ? key = props[i] : normalizedProps[i] = props[i];
    }
    return "string" == typeof type && !key && "dangerouslySetInnerHTML" in normalizedProps && (key = "innerhtml"), 
    _jsxSplit(type, props, null, normalizedProps.children, 0, key);
}

const isPropsProxy = obj => obj && void 0 !== obj[_VAR_PROPS];

class JSXNodeImpl {
    type;
    varProps;
    constProps;
    children;
    flags;
    key;
    dev;
    constructor(type, varProps, constProps, children, flags, key = null) {
        this.type = type, this.varProps = varProps, this.constProps = constProps, this.children = children, 
        this.flags = flags, this.key = key;
    }
    _proxy=null;
    get props() {
        return this._proxy || (this._proxy = createPropsProxy(this.varProps, this.constProps, this.children)), 
        this._proxy;
    }
}

const Virtual = props => props.children;

const RenderOnce = (props, key) => new JSXNodeImpl(Virtual, EMPTY_OBJ, null, props.children, 2, key);

const isJSXNode = n => n instanceof JSXNodeImpl;

const Fragment = props => props.children;

const jsxDEV = (type, props, key, _isStatic, opts) => {
    const processed = null == key ? null : String(key);
    const children = untrack((() => {
        const c = props.children;
        return "string" == typeof type && delete props.children, c;
    }));
    isString(type) && "className" in props && (props.class = props.className, delete props.className);
    const node = new JSXNodeImpl(type, props, null, children, 0, processed);
    return node.dev = {
        stack: (new Error).stack,
        ...opts
    }, seal(), node;
};

function createPropsProxy(varProps, constProps, children) {
    return new Proxy({}, new PropsProxyHandler(varProps, constProps, children));
}

class PropsProxyHandler {
    $varProps$;
    $constProps$;
    $children$;
    constructor($varProps$, $constProps$, $children$) {
        this.$varProps$ = $varProps$, this.$constProps$ = $constProps$, this.$children$ = $children$;
    }
    get(_, prop) {
        if (prop === _CONST_PROPS) {
            return this.$constProps$;
        }
        if (prop === _VAR_PROPS) {
            return this.$varProps$;
        }
        if (null != this.$children$ && "children" === prop) {
            return this.$children$;
        }
        const value = this.$constProps$ && prop in this.$constProps$ ? this.$constProps$[prop] : this.$varProps$[prop];
        return value instanceof WrappedSignal && 2 & value.$flags$ ? value.value : value;
    }
    set(_, prop, value) {
        return prop === _CONST_PROPS ? (this.$constProps$ = value, !0) : prop === _VAR_PROPS ? (this.$varProps$ = value, 
        !0) : (this.$constProps$ && prop in this.$constProps$ ? this.$constProps$[prop] = value : this.$varProps$[prop] = value, 
        !0);
    }
    deleteProperty(_, prop) {
        if ("string" != typeof prop) {
            return !1;
        }
        let didDelete = delete this.$varProps$[prop];
        return this.$constProps$ && (didDelete = delete this.$constProps$[prop] || didDelete), 
        null != this.$children$ && "children" === prop && (this.$children$ = null), didDelete;
    }
    has(_, prop) {
        return "children" === prop && null != this.$children$ || prop === _CONST_PROPS || prop === _VAR_PROPS || prop in this.$varProps$ || !!this.$constProps$ && prop in this.$constProps$;
    }
    getOwnPropertyDescriptor(_, p) {
        return {
            configurable: !0,
            enumerable: !0,
            value: "children" === p && null != this.$children$ ? this.$children$ : this.$constProps$ && p in this.$constProps$ ? this.$constProps$[p] : this.$varProps$[p]
        };
    }
    ownKeys() {
        const out = Object.keys(this.$varProps$);
        if (null != this.$children$ && -1 === out.indexOf("children") && out.push("children"), 
        this.$constProps$) {
            for (const key in this.$constProps$) {
                -1 === out.indexOf(key) && out.push(key);
            }
        }
        return out;
    }
}

const directGetPropsProxyProp = (jsx, prop) => jsx.constProps && prop in jsx.constProps ? jsx.constProps[prop] : jsx.varProps[prop];

const stringifyPath = [];

function qwikDebugToString(value) {
    if (null === value) {
        return "null";
    }
    if (void 0 === value) {
        return "undefined";
    }
    if ("string" == typeof value) {
        return '"' + value + '"';
    }
    if ("number" == typeof value || "boolean" == typeof value) {
        return String(value);
    }
    if (isTask(value)) {
        return `Task(${qwikDebugToString(value.$qrl$)})`;
    }
    if (isQrl(value)) {
        return `Qrl(${value.$symbol$})`;
    }
    if ("object" == typeof value || "function" == typeof value) {
        if (stringifyPath.includes(value)) {
            return "*";
        }
        try {
            if (stringifyPath.push(value), Array.isArray(value)) {
                return vnode_isVNode(value) ? "(" + vnode_getProp(value, DEBUG_TYPE, null) + ")" : value.map(qwikDebugToString);
            }
            if (isSignal(value)) {
                return value instanceof WrappedSignal ? "WrappedSignal" : value instanceof ComputedSignalImpl ? "ComputedSignal" : "Signal";
            }
            if (isStore(value)) {
                return "Store";
            }
            if (isJSXNode(value)) {
                return jsxToString(value);
            }
        } finally {
            stringifyPath.pop();
        }
    }
    return value;
}

const pad = (text, prefix) => String(text).split("\n").map(((line, idx) => (idx ? prefix : "") + line)).join("\n");

const jsxToString = value => {
    if (isJSXNode(value)) {
        let str = "<" + value.type;
        if (value.props) {
            for (const [key, val] of Object.entries(value.props)) {
                str += " " + key + "=" + qwikDebugToString(val);
            }
            const children = value.children;
            null != children ? (str += ">", Array.isArray(children) ? children.forEach((child => {
                str += jsxToString(child);
            })) : str += jsxToString(children), str += "</" + value.type + ">") : str += "/>";
        }
        return str;
    }
    return String(value);
};

const mergeMaps = (map1, map2) => {
    for (const [k, v] of map2) {
        map1.set(k, v);
    }
    return map1;
};

const vnode_newElement = (element, elementName) => {
    assertEqual(fastNodeType(element), 1, "Expecting element node.");
    const vnode = VNodeArray.createElement(-247, null, null, null, null, null, element, elementName);
    return assertTrue(vnode_isElementVNode(vnode), "Incorrect format of ElementVNode."), 
    assertFalse(vnode_isTextVNode(vnode), "Incorrect format of ElementVNode."), assertFalse(vnode_isVirtualVNode(vnode), "Incorrect format of ElementVNode."), 
    element.vNode = new WeakRef(vnode), vnode;
};

const vnode_newUnMaterializedElement = element => {
    assertEqual(fastNodeType(element), 1, "Expecting element node.");
    const vnode = VNodeArray.createElement(-255, null, null, null, void 0, void 0, element, void 0);
    return assertTrue(vnode_isElementVNode(vnode), "Incorrect format of ElementVNode."), 
    assertFalse(vnode_isTextVNode(vnode), "Incorrect format of ElementVNode."), assertFalse(vnode_isVirtualVNode(vnode), "Incorrect format of ElementVNode."), 
    element.vNode = new WeakRef(vnode), vnode;
};

const vnode_newSharedText = (previousTextNode, sharedTextNode, textContent) => {
    sharedTextNode && assertEqual(fastNodeType(sharedTextNode), 3, "Expecting element node.");
    const vnode = VNodeArray.createText(-252, null, previousTextNode, null, sharedTextNode, textContent);
    return assertFalse(vnode_isElementVNode(vnode), "Incorrect format of TextVNode."), 
    assertTrue(vnode_isTextVNode(vnode), "Incorrect format of TextVNode."), assertFalse(vnode_isVirtualVNode(vnode), "Incorrect format of TextVNode."), 
    vnode;
};

const vnode_newText = (textNode, textContent) => {
    const vnode = VNodeArray.createText(-244, null, null, null, textNode, textContent);
    return assertEqual(fastNodeType(textNode), 3, "Expecting element node."), assertFalse(vnode_isElementVNode(vnode), "Incorrect format of TextVNode."), 
    assertTrue(vnode_isTextVNode(vnode), "Incorrect format of TextVNode."), assertFalse(vnode_isVirtualVNode(vnode), "Incorrect format of TextVNode."), 
    vnode;
};

const vnode_newVirtual = () => {
    const vnode = VNodeArray.createVirtual(-254, null, null, null, null, null);
    return assertFalse(vnode_isElementVNode(vnode), "Incorrect format of TextVNode."), 
    assertFalse(vnode_isTextVNode(vnode), "Incorrect format of TextVNode."), assertTrue(vnode_isVirtualVNode(vnode), "Incorrect format of TextVNode."), 
    vnode;
};

const vnode_isVNode = vNode => vNode instanceof VNodeArray;

const vnode_isElementVNode = vNode => {
    assertDefined(vNode, "Missing vNode");
    return !(1 & ~vNode[0]);
};

const vnode_isElementOrTextVNode = vNode => {
    assertDefined(vNode, "Missing vNode");
    return !!(5 & vNode[0]);
};

const vnode_isMaterialized = vNode => {
    assertDefined(vNode, "Missing vNode");
    return !(1 & ~vNode[0]) && void 0 !== vNode[4] && void 0 !== vNode[5];
};

const vnode_isTextVNode = vNode => {
    assertDefined(vNode, "Missing vNode");
    return !(4 & ~vNode[0]);
};

const vnode_isVirtualVNode = vNode => {
    assertDefined(vNode, "Missing vNode");
    return !(2 & ~vNode[0]);
};

const vnode_isProjection = vNode => {
    assertDefined(vNode, "Missing vNode");
    return !(2 & ~vNode[0]) && null !== vnode_getProp(vNode, QSlot, null);
};

const ensureTextVNode = vNode => (assertTrue(vnode_isTextVNode(vNode), "Expecting TextVNode was: " + vnode_getNodeTypeName(vNode)), 
vNode);

const ensureElementOrVirtualVNode = vNode => {
    assertDefined(vNode, "Missing vNode"), assertTrue(!!(3 & vNode[0]), "Expecting ElementVNode or VirtualVNode was: " + vnode_getNodeTypeName(vNode));
};

const ensureElementVNode = vNode => (assertTrue(vnode_isElementVNode(vNode), "Expecting ElementVNode was: " + vnode_getNodeTypeName(vNode)), 
vNode);

const vnode_getNodeTypeName = vNode => {
    if (vNode) {
        switch (7 & vNode[0]) {
          case 1:
            return "Element";

          case 2:
            return "Virtual";

          case 4:
            return "Text";
        }
    }
    return "<unknown>";
};

const vnode_ensureElementInflated = vnode => {
    if (1 == (15 & vnode[0])) {
        const elementVNode = vnode;
        elementVNode[0] ^= 8;
        const element = elementVNode[6];
        const attributes = element.attributes;
        const props = vnode_getProps(elementVNode);
        for (let idx = 0; idx < attributes.length; idx++) {
            const attr = attributes[idx];
            const key = attr.name;
            if (":" === key || !key) {
                break;
            }
            if (key.startsWith("q:container")) {
                "html" === attr.value ? mapArray_set(props, dangerouslySetInnerHTML, element.innerHTML, 0) : "text" === attr.value && "value" in element && mapArray_set(props, "value", element.value, 0);
            } else if (!key.startsWith("on:")) {
                mapArray_set(props, key, attr.value, 0);
            }
        }
    }
};

function vnode_walkVNode(vNode) {
    let vCursor = vNode;
    if (vnode_isTextVNode(vNode)) {
        return;
    }
    let vParent = null;
    for (;;) {
        const vFirstChild = vnode_getFirstChild(vCursor);
        if (vFirstChild) {
            vCursor = vFirstChild;
            continue;
        }
        if (vCursor === vNode) {
            return;
        }
        const vNextSibling = vnode_getNextSibling(vCursor);
        if (vNextSibling) {
            vCursor = vNextSibling;
        } else {
            for (vParent = vnode_getParent(vCursor); vParent; ) {
                if (vParent === vNode) {
                    return;
                }
                const vNextParentSibling = vnode_getNextSibling(vParent);
                if (vNextParentSibling) {
                    vCursor = vNextParentSibling;
                    break;
                }
                vParent = vnode_getParent(vParent);
            }
            if (null == vParent) {
                return;
            }
        }
    }
}

function vnode_getDOMChildNodes(journal, root, isVNode = !1, childNodes = []) {
    if (vnode_isElementOrTextVNode(root)) {
        return vnode_isTextVNode(root) && vnode_ensureTextInflated(journal, root), childNodes.push(isVNode ? root : vnode_getNode(root)), 
        childNodes;
    }
    let vNode = vnode_getFirstChild(root);
    for (;vNode; ) {
        vnode_isElementVNode(vNode) ? childNodes.push(isVNode ? vNode : vnode_getNode(vNode)) : vnode_isTextVNode(vNode) ? (vnode_ensureTextInflated(journal, vNode), 
        childNodes.push(isVNode ? vNode : vnode_getNode(vNode))) : vnode_getDOMChildNodes(journal, vNode, !!isVNode, childNodes), 
        vNode = vnode_getNextSibling(vNode);
    }
    return childNodes;
}

const vnode_getDomSibling = (vNode, nextDirection, descend) => {
    const childProp = nextDirection ? 4 : 5;
    const siblingProp = nextDirection ? 3 : 2;
    let cursor = vNode;
    for (;descend && cursor && vnode_isVirtualVNode(cursor); ) {
        const child = cursor[childProp];
        if (!child) {
            break;
        }
        if (5 & child[0]) {
            return child;
        }
        cursor = child;
    }
    for (;cursor; ) {
        let sibling = cursor[siblingProp];
        if (sibling && 5 & sibling[0]) {
            return sibling;
        }
        if (!sibling) {
            let virtual = cursor[1];
            if (virtual && !vnode_isVirtualVNode(virtual)) {
                return null;
            }
            for (;virtual && !(sibling = virtual[siblingProp]); ) {
                if (virtual = virtual[1], virtual && !vnode_isVirtualVNode(virtual)) {
                    return null;
                }
            }
            if (!sibling) {
                return null;
            }
            if (vnode_isTextVNode(sibling) && virtual && vnode_isElementVNode(virtual)) {
                return null;
            }
        }
        for (;sibling; ) {
            if (cursor = sibling, 5 & cursor[0] && vnode_getNode(cursor)) {
                return cursor;
            }
            sibling = cursor[childProp];
        }
    }
    return null;
};

const vnode_ensureInflatedIfText = (journal, vNode) => {
    vnode_isTextVNode(vNode) && vnode_ensureTextInflated(journal, vNode);
};

const vnode_ensureTextInflated = (journal, vnode) => {
    const textVNode = ensureTextVNode(vnode);
    if (!(8 & textVNode[0])) {
        const parentNode = vnode_getDomParent(vnode);
        const sharedTextNode = textVNode[4];
        const doc = parentNode.ownerDocument;
        let cursor = vnode_getDomSibling(vnode, !1, !0);
        const insertBeforeNode = sharedTextNode || vnode_getDomSibling(vnode, !0, !0)?.[6] || null;
        let lastPreviousTextNode = insertBeforeNode;
        for (;cursor && vnode_isTextVNode(cursor); ) {
            if (!(8 & cursor[0])) {
                const textNode = doc.createTextNode(cursor[5]);
                journal.push(5, parentNode, lastPreviousTextNode, textNode), lastPreviousTextNode = textNode, 
                cursor[4] = textNode, cursor[0] |= 8;
            }
            cursor = vnode_getDomSibling(cursor, !1, !0);
        }
        for (cursor = vnode; cursor && vnode_isTextVNode(cursor); ) {
            const next = vnode_getDomSibling(cursor, !0, !0);
            const isLastNode = !next || !vnode_isTextVNode(next);
            if (!(8 & cursor[0])) {
                if (isLastNode && sharedTextNode) {
                    journal.push(1, sharedTextNode, cursor[5]);
                } else {
                    const textNode = doc.createTextNode(cursor[5]);
                    journal.push(5, parentNode, insertBeforeNode, textNode), cursor[4] = textNode;
                }
                cursor[0] |= 8;
            }
            cursor = next;
        }
    }
};

const vnode_locate = (rootVNode, id) => {
    ensureElementVNode(rootVNode);
    let vNode = rootVNode;
    const containerElement = rootVNode[6];
    const {qVNodeRefs} = containerElement;
    let elementOffset = -1;
    let refElement;
    if ("string" == typeof id) {
        assertDefined(qVNodeRefs, "Missing qVNodeRefs."), elementOffset = parseInt(id), 
        refElement = qVNodeRefs.get(elementOffset);
    } else {
        refElement = id;
        const vNode = refElement.vNode?.deref();
        if (vNode) {
            return vNode;
        }
    }
    if (assertDefined(refElement, "Missing refElement."), vnode_isVNode(refElement)) {
        vNode = refElement;
    } else {
        assertTrue(containerElement.contains(refElement), "Couldn't find the element inside the container while locating the VNode.");
        let parent = refElement;
        const elementPath = [ refElement ];
        for (;parent && parent !== containerElement; ) {
            parent = parent.parentElement, elementPath.push(parent);
        }
        for (let i = elementPath.length - 2; i >= 0; i--) {
            vNode = vnode_getVNodeForChildNode(vNode, elementPath[i]);
        }
        -1 != elementOffset && qVNodeRefs.set(elementOffset, vNode);
    }
    if ("string" == typeof id) {
        const idLength = id.length;
        let idx = indexOfAlphanumeric(id, idLength);
        let childIdx = 0;
        for (;idx < idLength; ) {
            const ch = id.charCodeAt(idx);
            childIdx *= 26, ch >= 97 ? childIdx += ch - 97 : (childIdx += ch - 65, vNode = vnode_getChildWithIdx(vNode, childIdx), 
            childIdx = 0), idx++;
        }
    }
    return vNode;
};

const vnode_getChildWithIdx = (vNode, childIdx) => {
    let child = vnode_getFirstChild(vNode);
    for (assertDefined(child, "Missing child."); child[0] >>> 8 !== childIdx; ) {
        child = vnode_getNextSibling(child), assertDefined(child, "Missing child.");
    }
    return child;
};

const vNodeStack = [];

const vnode_getVNodeForChildNode = (vNode, childElement) => {
    ensureElementVNode(vNode);
    let child = vnode_getFirstChild(vNode);
    for (assertDefined(child, "Missing child."); child && child[6] !== childElement; ) {
        if (vnode_isVirtualVNode(child)) {
            const next = vnode_getNextSibling(child);
            const firstChild = vnode_getFirstChild(child);
            firstChild ? (next && vNodeStack.push(next), child = firstChild) : child = next || (vNodeStack.length ? vNodeStack.pop() : null);
        } else {
            const next = vnode_getNextSibling(child);
            child = next || (next || vNodeStack.pop());
        }
        assertDefined(child, "Missing child.");
    }
    for (;vNodeStack.length; ) {
        vNodeStack.pop();
    }
    return ensureElementVNode(child), assertEqual(child[6], childElement, "Child not found."), 
    child;
};

const indexOfAlphanumeric = (id, length) => {
    let idx = 0;
    for (;idx < length; ) {
        if (!(id.charCodeAt(idx) <= 57)) {
            return idx;
        }
        idx++;
    }
    return length;
};

const parseBoolean = value => "false" !== value && Boolean(value);

const isBooleanAttr = (element, key) => ("allowfullscreen" == key || "async" == key || "autofocus" == key || "autoplay" == key || "checked" == key || "controls" == key || "default" == key || "defer" == key || "disabled" == key || "formnovalidate" == key || "inert" == key || "ismap" == key || "itemscope" == key || "loop" == key || "multiple" == key || "muted" == key || "nomodule" == key || "novalidate" == key || "open" == key || "playsinline" == key || "readonly" == key || "required" == key || "reversed" == key || "selected" == key) && key in element;

const vnode_applyJournal = journal => {
    let idx = 0;
    const length = journal.length;
    for (;idx < length; ) {
        switch (journal[idx++]) {
          case 1:
            journal[idx++].nodeValue = journal[idx++];
            break;

          case 2:
            const element = journal[idx++];
            let key = journal[idx++];
            "className" === key && (key = "class");
            const value = journal[idx++];
            isBooleanAttr(element, key) ? element[key] = parseBoolean(value) : "value" === key && key in element ? element.value = String(value) : key === dangerouslySetInnerHTML ? (element.innerHTML = value, 
            element.setAttribute("q:container", "html")) : null == value || !1 === value ? element.removeAttribute(key) : element.setAttribute(key, String(value));
            break;

          case 3:
            const document = journal[idx++];
            const head = document.head;
            const styles = document.querySelectorAll(QStylesAllSelector);
            for (let i = 0; i < styles.length; i++) {
                head.appendChild(styles[i]);
            }
            break;

          case 4:
            const removeParent = journal[idx++];
            let nodeToRemove;
            for (;idx < length && "number" != typeof (nodeToRemove = journal[idx]); ) {
                removeParent.removeChild(nodeToRemove), idx++;
            }
            break;

          case 5:
            const insertParent = journal[idx++];
            const insertBefore = journal[idx++];
            let newChild;
            for (;idx < length && "number" != typeof (newChild = journal[idx]); ) {
                insertParent.insertBefore(newChild, insertBefore), idx++;
            }
        }
    }
    journal.length = 0;
};

const vnode_insertBefore = (journal, parent, newChild, insertBefore) => {
    ensureElementOrVirtualVNode(parent), vnode_isElementVNode(parent) && ensureMaterialized(parent);
    const newChildCurrentParent = newChild[1];
    if (newChild === insertBefore) {
        if (newChildCurrentParent) {
            return;
        }
        insertBefore = null;
    }
    newChildCurrentParent && (newChild[2] || newChild[3] || newChildCurrentParent !== parent) && vnode_remove(journal, newChildCurrentParent, newChild, !1);
    let adjustedInsertBefore = null;
    null == insertBefore ? vnode_isVirtualVNode(parent) && (adjustedInsertBefore = vnode_getDomSibling(parent, !0, !1)) : adjustedInsertBefore = vnode_isVirtualVNode(insertBefore) ? vnode_getDomSibling(insertBefore, !0, !0) : insertBefore, 
    adjustedInsertBefore && vnode_ensureInflatedIfText(journal, adjustedInsertBefore);
    const domParentVNode = vnode_getDomParentVNode(parent);
    const parentNode = domParentVNode && domParentVNode[6];
    if (parentNode) {
        const domChildren = vnode_getDomChildrenWithCorrectNamespacesToInsert(journal, domParentVNode, newChild);
        domChildren.length && journal.push(5, parentNode, vnode_getNode(adjustedInsertBefore), ...domChildren);
    }
    const vNext = insertBefore;
    const vPrevious = vNext ? vNext[2] : parent[5];
    vNext ? vNext[2] = newChild : parent[5] = newChild, vPrevious ? vPrevious[3] = newChild : parent[4] = newChild, 
    newChild[2] = vPrevious, newChild[3] = vNext, newChild[1] = parent;
};

const vnode_getDomParent = vnode => (vnode = vnode_getDomParentVNode(vnode)) && vnode[6];

const vnode_getDomParentVNode = vnode => {
    for (;vnode && !vnode_isElementVNode(vnode); ) {
        vnode = vnode[1];
    }
    return vnode;
};

const vnode_remove = (journal, vParent, vToRemove, removeDOM) => {
    if (assertEqual(vParent, vnode_getParent(vToRemove), "Parent mismatch."), vnode_isTextVNode(vToRemove) && vnode_ensureTextInflated(journal, vToRemove), 
    removeDOM) {
        const domParent = vnode_getDomParent(vParent);
        if (vnode_getAttr(vParent, dangerouslySetInnerHTML)) {
            return;
        }
        const children = vnode_getDOMChildNodes(journal, vToRemove);
        domParent && children.length && journal.push(4, domParent, ...children);
    }
    const vPrevious = vToRemove[2];
    const vNext = vToRemove[3];
    vPrevious ? vPrevious[3] = vNext : vParent[4] = vNext, vNext ? vNext[2] = vPrevious : vParent[5] = vPrevious, 
    vToRemove[2] = null, vToRemove[3] = null;
};

const vnode_truncate = (journal, vParent, vDelete) => {
    assertDefined(vDelete, "Missing vDelete.");
    const parent = vnode_getDomParent(vParent);
    const children = vnode_getDOMChildNodes(journal, vDelete);
    parent && children.length && journal.push(4, parent, ...children);
    const vPrevious = vDelete[2];
    vPrevious ? vPrevious[3] = null : vParent[4] = null, vParent[5] = vPrevious;
};

const vnode_getElementName = vnode => {
    const elementVNode = ensureElementVNode(vnode);
    let elementName = elementVNode[7];
    if (void 0 === elementName) {
        const element = elementVNode[6];
        const nodeName = fastNodeName(element).toLowerCase();
        elementName = elementVNode[7] = nodeName, elementVNode[0] |= vnode_getElementNamespaceFlags(element);
    }
    return elementName;
};

const vnode_getText = vnode => {
    const textVNode = ensureTextVNode(vnode);
    let text = textVNode[5];
    return void 0 === text && (text = textVNode[5] = textVNode[4].nodeValue), text;
};

const vnode_setText = (journal, textVNode, text) => {
    vnode_ensureTextInflated(journal, textVNode);
    journal.push(1, textVNode[4], textVNode[5] = text);
};

const vnode_getFirstChild = vnode => {
    if (vnode_isTextVNode(vnode)) {
        return null;
    }
    let vFirstChild = vnode[4];
    return void 0 === vFirstChild && (vFirstChild = ensureMaterialized(vnode)), vFirstChild;
};

const vnode_materialize = vNode => {
    const element = vNode[6];
    const firstChild = fastFirstChild(element);
    const vNodeData = element.ownerDocument?.qVNodeData?.get(element);
    return materialize(vNode, element, firstChild, vNodeData);
};

const materialize = (vNode, element, firstChild, vNodeData) => {
    if (vNodeData) {
        if (vNodeData.charCodeAt(0) === VNodeDataChar.SEPARATOR) {
            const elementVNodeDataStartIdx = 1;
            let elementVNodeDataEndIdx = 1;
            for (;vNodeData.charCodeAt(elementVNodeDataEndIdx) !== VNodeDataChar.SEPARATOR; ) {
                elementVNodeDataEndIdx++;
            }
            const elementVNodeData = vNodeData.substring(elementVNodeDataStartIdx, elementVNodeDataEndIdx);
            vNodeData = vNodeData.substring(elementVNodeDataEndIdx + 1);
            const vFirstChild = materializeFromDOM(vNode, firstChild, elementVNodeData);
            if (!vNodeData) {
                return vFirstChild;
            }
        }
        return materializeFromVNodeData(vNode, vNodeData, element, firstChild);
    }
    return materializeFromDOM(vNode, firstChild);
};

const ensureMaterialized = vnode => {
    const vParent = ensureElementVNode(vnode);
    let vFirstChild = vParent[4];
    if (void 0 === vFirstChild) {
        vFirstChild = vParent[1] && shouldIgnoreChildren(vParent[6]) ? vParent[4] = vParent[5] = null : vnode_materialize(vParent);
    }
    return assertTrue(void 0 !== vParent[4], "Did not materialize."), assertTrue(void 0 !== vParent[5], "Did not materialize."), 
    vFirstChild;
};

let _fastHasAttribute = null;

const shouldIgnoreChildren = node => (_fastHasAttribute || (_fastHasAttribute = node.hasAttribute), 
_fastHasAttribute.call(node, "q:container"));

let _fastNodeType = null;

const fastNodeType = node => (_fastNodeType || (_fastNodeType = fastGetter(node, "nodeType")), 
_fastNodeType.call(node));

const fastIsTextOrElement = node => {
    const type = fastNodeType(node);
    return 3 === type || 1 === type;
};

let _fastNextSibling = null;

const fastNextSibling = node => {
    for (_fastNextSibling || (_fastNextSibling = fastGetter(node, "nextSibling")), _fastFirstChild || (_fastFirstChild = fastGetter(node, "firstChild")); node; ) {
        if (null !== (node = _fastNextSibling.call(node))) {
            const type = fastNodeType(node);
            if (3 === type || 1 === type) {
                break;
            }
            if (8 === type) {
                const nodeValue = node.nodeValue;
                if (nodeValue?.startsWith(QIgnore)) {
                    return getNodeAfterCommentNode(node, QContainerIsland, _fastNextSibling, _fastFirstChild);
                }
                if (node.nodeValue?.startsWith(QContainerIslandEnd)) {
                    return getNodeAfterCommentNode(node, QIgnoreEnd, _fastNextSibling, _fastFirstChild);
                }
                if (nodeValue?.startsWith("q:container")) {
                    for (;node && (node = _fastNextSibling.call(node)) && (8 !== fastNodeType(node) || !node.nodeValue?.startsWith("/q:container")); ) {}
                }
            }
        }
    }
    return node;
};

function getNodeAfterCommentNode(node, commentValue, nextSibling, firstChild) {
    for (;node; ) {
        if (node.nodeValue?.startsWith(commentValue)) {
            return node = nextSibling.call(node) || null;
        }
        let nextNode = firstChild.call(node);
        nextNode || (nextNode = nextSibling.call(node)), nextNode || (nextNode = fastParentNode(node), 
        nextNode && (nextNode = nextSibling.call(nextNode))), node = nextNode;
    }
    return null;
}

let _fastParentNode = null;

const fastParentNode = node => (_fastParentNode || (_fastParentNode = fastGetter(node, "parentNode")), 
_fastParentNode.call(node));

let _fastFirstChild = null;

const fastFirstChild = node => {
    for (_fastFirstChild || (_fastFirstChild = fastGetter(node, "firstChild")), node = node && _fastFirstChild.call(node); node && !fastIsTextOrElement(node); ) {
        node = fastNextSibling(node);
    }
    return node;
};

let _fastNamespaceURI = null;

const fastNamespaceURI = element => (_fastNamespaceURI || (_fastNamespaceURI = fastGetter(element, "namespaceURI")), 
_fastNamespaceURI.call(element));

let _fastNodeName = null;

const fastNodeName = element => (_fastNodeName || (_fastNodeName = fastGetter(element, "nodeName")), 
_fastNodeName.call(element));

const fastGetter = (prototype, name) => {
    let getter;
    for (;prototype && !(getter = Object.getOwnPropertyDescriptor(prototype, name)?.get); ) {
        prototype = Object.getPrototypeOf(prototype);
    }
    return getter || function() {
        return this[name];
    };
};

const isQStyleElement = node => isElement(node) && "STYLE" === node.nodeName && (node.hasAttribute("q:sstyle") || node.hasAttribute(QStyle));

const materializeFromDOM = (vParent, firstChild, vData) => {
    let vFirstChild = null;
    const skipStyleElements = () => {
        for (;isQStyleElement(child); ) {
            child = fastNextSibling(child);
        }
    };
    let child = firstChild;
    skipStyleElements();
    let vChild = null;
    for (;child; ) {
        const nodeType = fastNodeType(child);
        let vNextChild = null;
        3 === nodeType ? vNextChild = vnode_newText(child, child.textContent ?? void 0) : 1 === nodeType && (vNextChild = vnode_newUnMaterializedElement(child)), 
        vNextChild && (vNextChild[1] = vParent, vChild && (vChild[3] = vNextChild), vNextChild[2] = vChild, 
        vChild = vNextChild), vFirstChild || (vParent[4] = vFirstChild = vChild), child = fastNextSibling(child), 
        skipStyleElements();
    }
    if (vParent[5] = vChild || null, vParent[4] = vFirstChild, vData) {
        let container = null;
        processVNodeData(vData, ((peek, consumeValue) => {
            if (peek() === VNodeDataChar.ID) {
                container || (container = getDomContainer(vParent[6]));
                const id = consumeValue();
                container.$setRawState$(parseInt(id), vParent), isDev && vnode_setAttr(null, vParent, "q:id", id);
            } else {
                peek() === VNodeDataChar.BACK_REFS ? (container || (container = getDomContainer(vParent[6])), 
                setEffectBackRefFromVNodeData(vParent, consumeValue(), container)) : consumeValue();
            }
        }));
    }
    return vFirstChild;
};

function setEffectBackRefFromVNodeData(vParent, value, container) {
    const deserializedSubMap = container.$getObjectById$(value);
    if (vParent[_EFFECT_BACK_REF]) {
        mergeMaps(vParent[_EFFECT_BACK_REF], deserializedSubMap);
    } else {
        Object.defineProperty(vParent, _EFFECT_BACK_REF, {
            value: deserializedSubMap
        });
    }
}

const processVNodeData = (vData, callback) => {
    let nextToConsumeIdx = 0;
    let ch = 0;
    let peekCh = 0;
    const peek = () => 0 !== peekCh ? peekCh : peekCh = nextToConsumeIdx < vData.length ? vData.charCodeAt(nextToConsumeIdx) : 0;
    const consume = () => (ch = peek(), peekCh = 0, nextToConsumeIdx++, ch);
    const consumeValue = () => {
        consume();
        const start = nextToConsumeIdx;
        for (;peek() <= 58 && 0 !== peekCh || 95 === peekCh || peekCh >= 65 && peekCh <= 90 || peekCh >= 97 && peekCh <= 122; ) {
            consume();
        }
        return vData.substring(start, nextToConsumeIdx);
    };
    for (;0 !== peek(); ) {
        callback(peek, consumeValue, consume, nextToConsumeIdx);
    }
};

const vnode_getNextSibling = vnode => vnode[3];

const vnode_getPreviousSibling = vnode => vnode[2];

const vnode_getAttrKeys = vnode => {
    if (3 & vnode[0]) {
        vnode_ensureElementInflated(vnode);
        const keys = [];
        const props = vnode_getProps(vnode);
        for (let i = 0; i < props.length; i += 2) {
            const key = props[i];
            key.startsWith(":") || keys.push(key);
        }
        return keys;
    }
    return [];
};

const vnode_setAttr = (journal, vnode, key, value) => {
    const type = vnode[0];
    if (3 & type) {
        vnode_ensureElementInflated(vnode);
        const props = vnode_getProps(vnode);
        const idx = mapApp_findIndx(props, key, 0);
        if (idx >= 0) {
            if (props[idx + 1] != value && 1 & type) {
                journal && journal.push(2, vnode[6], key, value);
            }
            null == value ? props.splice(idx, 2) : props[idx + 1] = value;
        } else if (null != value && (props.splice(~idx, 0, key, value), 1 & type)) {
            journal && journal.push(2, vnode[6], key, value);
        }
    }
};

const vnode_getAttr = (vnode, key) => {
    if (3 & vnode[0]) {
        vnode_ensureElementInflated(vnode);
        const props = vnode_getProps(vnode);
        return mapArray_get(props, key, 0);
    }
    return null;
};

const vnode_getProp = (vnode, key, getObject) => {
    const type = vnode[0];
    if (3 & type) {
        1 & type && vnode_ensureElementInflated(vnode);
        const props = vnode_getProps(vnode);
        const idx = mapApp_findIndx(props, key, 0);
        if (idx >= 0) {
            let value = props[idx + 1];
            return "string" == typeof value && getObject && (props[idx + 1] = value = getObject(value)), 
            value;
        }
    }
    return null;
};

const vnode_setProp = (vnode, key, value) => {
    ensureElementOrVirtualVNode(vnode);
    const props = vnode_getProps(vnode);
    const idx = mapApp_findIndx(props, key, 0);
    idx >= 0 ? props[idx + 1] = value : null != value && props.splice(~idx, 0, key, value);
};

const vnode_getPropStartIndex = vnode => {
    const type = 7 & vnode[0];
    if (1 === type) {
        return 8;
    }
    if (2 === type) {
        return 6;
    }
    throw qError(43, [ type ]);
};

const vnode_getProps = vnode => vnode[vnode_getPropStartIndex(vnode)];

const vnode_getParent = vnode => vnode[1] || null;

const vnode_getNode = vnode => null === vnode || vnode_isVirtualVNode(vnode) ? null : vnode_isElementVNode(vnode) ? vnode[6] : (assertTrue(vnode_isTextVNode(vnode), "Expecting Text Node."), 
vnode[4]);

function vnode_toString(depth = 20, offset = "", materialize = !1, siblings = !1) {
    let vnode = this;
    if (0 === depth) {
        return "...";
    }
    if (null === vnode) {
        return "null";
    }
    if (void 0 === vnode) {
        return "undefined";
    }
    const strings = [];
    do {
        if (vnode_isTextVNode(vnode)) {
            strings.push(qwikDebugToString(vnode_getText(vnode)));
        } else if (vnode_isVirtualVNode(vnode)) {
            const attrs = [ "[" + String(vnode[0] >>> 8) + "]" ];
            vnode_getAttrKeys(vnode).forEach((key => {
                if (key !== DEBUG_TYPE) {
                    const value = vnode_getAttr(vnode, key);
                    attrs.push(" " + key + "=" + qwikDebugToString(value));
                }
            }));
            const name = VirtualTypeName[vnode_getAttr(vnode, DEBUG_TYPE) || "V"] || VirtualTypeName.V;
            strings.push("<" + name + attrs.join("") + ">");
            const child = vnode_getFirstChild(vnode);
            child && strings.push("  " + vnode_toString.call(child, depth - 1, offset + "  ", !0, !0)), 
            strings.push("</" + name + ">");
        } else if (vnode_isElementVNode(vnode)) {
            const tag = vnode_getElementName(vnode);
            const attrs = [];
            const keys = vnode_getAttrKeys(vnode);
            keys.forEach((key => {
                const value = vnode_getAttr(vnode, key);
                attrs.push(" " + key + "=" + qwikDebugToString(value));
            }));
            const node = vnode_getNode(vnode);
            if (node) {
                const vnodeData = node.ownerDocument.qVNodeData?.get(node);
                vnodeData && attrs.push(" q:vnodeData=" + qwikDebugToString(vnodeData));
            }
            const domAttrs = node.attributes;
            for (let i = 0; i < domAttrs.length; i++) {
                const attr = domAttrs[i];
                -1 === keys.indexOf(attr.name) && attrs.push(" " + attr.name + (attr.value ? "=" + qwikDebugToString(attr.value) : ""));
            }
            if (strings.push("<" + tag + attrs.join("") + ">"), vnode_isMaterialized(vnode) || materialize) {
                const child = vnode_getFirstChild(vnode);
                child && strings.push("  " + vnode_toString.call(child, depth - 1, offset + "  ", !0, !0));
            } else {
                strings.push("  \x3c!-- not materialized --!>");
            }
            strings.push("</" + tag + ">");
        }
        vnode = siblings && vnode_getNextSibling(vnode) || null;
    } while (vnode);
    return strings.join("\n" + offset);
}

const isNumber = ch => 48 <= ch && ch <= 57;

const isLowercase = ch => 97 <= ch && ch <= 122;

const stack = [];

function materializeFromVNodeData(vParent, vData, element, child) {
    let idx = 0;
    let vFirst = null;
    let vLast = null;
    let previousTextNode = null;
    const addVNode = node => {
        node[0] = 255 & node[0] | idx << 8, idx++, vLast && (vLast[3] = node), node[2] = vLast, 
        node[1] = vParent, vFirst || (vParent[4] = vFirst = node), vLast = node;
    };
    let textIdx = 0;
    let combinedText = null;
    let container = null;
    return processVNodeData(vData, ((peek, consumeValue, consume, nextToConsumeIdx) => {
        if (isNumber(peek())) {
            for (;!isElement(child); ) {
                if (!(child = fastNextSibling(child))) {
                    throw qError(44, [ vData, peek(), nextToConsumeIdx ]);
                }
            }
            for (;isQStyleElement(child); ) {
                child = fastNextSibling(child);
            }
            combinedText = null, previousTextNode = null;
            let value = 0;
            for (;isNumber(peek()); ) {
                value *= 10, value += consume() - 48;
            }
            for (;value--; ) {
                addVNode(vnode_newUnMaterializedElement(child)), child = fastNextSibling(child);
            }
        } else if (peek() === VNodeDataChar.SCOPED_STYLE) {
            vnode_setAttr(null, vParent, "q:sstyle", consumeValue());
        } else if (peek() === VNodeDataChar.RENDER_FN) {
            vnode_setAttr(null, vParent, "q:renderFn", consumeValue());
        } else if (peek() === VNodeDataChar.ID) {
            container || (container = getDomContainer(element));
            const id = consumeValue();
            container.$setRawState$(parseInt(id), vParent), isDev && vnode_setAttr(null, vParent, "q:id", id);
        } else if (peek() === VNodeDataChar.PROPS) {
            vnode_setAttr(null, vParent, "q:props", consumeValue());
        } else if (peek() === VNodeDataChar.KEY) {
            vnode_setAttr(null, vParent, "q:key", consumeValue());
        } else if (peek() === VNodeDataChar.SEQ) {
            vnode_setAttr(null, vParent, "q:seq", consumeValue());
        } else if (peek() === VNodeDataChar.SEQ_IDX) {
            vnode_setAttr(null, vParent, "q:seqIdx", consumeValue());
        } else if (peek() === VNodeDataChar.BACK_REFS) {
            container || (container = getDomContainer(element)), setEffectBackRefFromVNodeData(vParent, consumeValue(), container);
        } else if (peek() === VNodeDataChar.SLOT_PARENT) {
            vnode_setProp(vParent, "q:sparent", consumeValue());
        } else if (peek() === VNodeDataChar.CONTEXT) {
            vnode_setAttr(null, vParent, "q:ctx", consumeValue());
        } else if (peek() === VNodeDataChar.OPEN) {
            consume(), addVNode(vnode_newVirtual()), stack.push(vParent, vFirst, vLast, previousTextNode, idx), 
            idx = 0, vParent = vLast, vFirst = vLast = null;
        } else if (peek() === VNodeDataChar.SEPARATOR) {
            const key = consumeValue();
            const value = consumeValue();
            vnode_setAttr(null, vParent, key, value);
        } else if (peek() === VNodeDataChar.CLOSE) {
            consume(), vParent[5] = vLast, idx = stack.pop(), previousTextNode = stack.pop(), 
            vLast = stack.pop(), vFirst = stack.pop(), vParent = stack.pop();
        } else if (peek() === VNodeDataChar.SLOT) {
            vnode_setAttr(null, vParent, QSlot, consumeValue());
        } else {
            const textNode = child && 3 === fastNodeType(child) ? child : null;
            null === combinedText && (combinedText = textNode ? textNode.nodeValue : null, textIdx = 0);
            let length = 0;
            for (;isLowercase(peek()); ) {
                length += consume() - 97, length *= 26;
            }
            length += consume() - 65;
            const text = null === combinedText ? "" : combinedText.substring(textIdx, textIdx + length);
            addVNode(previousTextNode = vnode_newSharedText(previousTextNode, textNode, text)), 
            textIdx += length;
        }
    })), vParent[5] = vLast, vFirst;
}

const vnode_getType = vnode => {
    const type = vnode[0];
    if (1 & type) {
        return 1;
    }
    if (2 & type) {
        return 11;
    }
    if (4 & type) {
        return 3;
    }
    throw qError(43, [ type ]);
};

const isElement = node => node && "object" == typeof node && 1 === fastNodeType(node);

const vnode_getProjectionParentComponent = (vHost, rootVNode) => {
    let projectionDepth = 1;
    for (;projectionDepth--; ) {
        for (;vHost && (!vnode_isVirtualVNode(vHost) || null === vnode_getProp(vHost, "q:renderFn", null)); ) {
            const qSlotParent = vnode_getProp(vHost, "q:sparent", (id => vnode_locate(rootVNode, id)));
            const vProjectionParent = vnode_isVirtualVNode(vHost) && qSlotParent;
            vProjectionParent && projectionDepth++, vHost = vProjectionParent || vnode_getParent(vHost);
        }
        projectionDepth > 0 && (vHost = vnode_getParent(vHost));
    }
    return vHost;
};

const VNodeArray = class VNode extends Array {
    static createElement(flags, parent, previousSibling, nextSibling, firstChild, lastChild, element, elementName) {
        return new VNode(flags, parent, previousSibling, nextSibling, firstChild, lastChild, element, elementName, []);
    }
    static createText(flags, parent, previousSibling, nextSibling, textNode, text) {
        return new VNode(flags, parent, previousSibling, nextSibling, textNode, text);
    }
    static createVirtual(flags, parent, previousSibling, nextSibling, firstChild, lastChild) {
        return new VNode(flags, parent, previousSibling, nextSibling, firstChild, lastChild, []);
    }
    constructor(flags, parent, previousSibling, nextSibling, ...rest) {
        super(flags, parent, previousSibling, nextSibling, ...rest), isDev && (this.toString = vnode_toString);
    }
};

const deserializedProxyMap = new WeakMap;

const isDeserializerProxy = value => "object" == typeof value && null !== value && SERIALIZER_PROXY_UNWRAP in value;

const SERIALIZER_PROXY_UNWRAP = Symbol("UNWRAP");

const wrapDeserializerProxy = (container, data) => {
    if (!Array.isArray(data) || vnode_isVNode(data) || isDeserializerProxy(data)) {
        return data;
    }
    let proxy = deserializedProxyMap.get(data);
    if (!proxy) {
        const target = Array(data.length / 2).fill(void 0);
        proxy = new Proxy(target, new DeserializationHandler(container, data)), deserializedProxyMap.set(data, proxy);
    }
    return proxy;
};

class DeserializationHandler {
    $container$;
    $data$;
    $length$;
    constructor($container$, $data$) {
        this.$container$ = $container$, this.$data$ = $data$, this.$length$ = this.$data$.length / 2;
    }
    get(target, property, receiver) {
        if (property === SERIALIZER_PROXY_UNWRAP) {
            return target;
        }
        const i = "number" == typeof property ? property : "string" == typeof property ? parseInt(property, 10) : NaN;
        if (Number.isNaN(i) || i < 0 || i >= this.$length$) {
            return Reflect.get(target, property, receiver);
        }
        const idx = 2 * i;
        const typeId = this.$data$[idx];
        const value = this.$data$[idx + 1];
        if (void 0 === typeId) {
            return value;
        }
        const container = this.$container$;
        let propValue = allocate(container, typeId, value);
        return typeId >= 12 && (propValue = inflate(container, propValue, typeId, value)), 
        Reflect.set(target, property, propValue), this.$data$[idx] = void 0, this.$data$[idx + 1] = propValue, 
        propValue;
    }
    has(target, property) {
        return property === SERIALIZER_PROXY_UNWRAP || Object.prototype.hasOwnProperty.call(target, property);
    }
    set(target, property, value, receiver) {
        if (property === SERIALIZER_PROXY_UNWRAP) {
            return !1;
        }
        const out = Reflect.set(target, property, value, receiver);
        const i = "number" == typeof property ? property : parseInt(property, 10);
        if (Number.isNaN(i) || i < 0 || i >= this.$data$.length / 2) {
            return out;
        }
        const idx = 2 * i;
        return this.$data$[idx] = void 0, this.$data$[idx + 1] = value, !0;
    }
}

const _eagerDeserializeArray = (container, data) => {
    const out = Array(data.length / 2);
    for (let i = 0; i < data.length; i += 2) {
        out[i / 2] = deserializeData(container, data[i], data[i + 1]);
    }
    return out;
};

const resolvers = new WeakMap;

const inflate = (container, target, typeId, data) => {
    if (void 0 === typeId) {
        return target;
    }
    switch (13 !== typeId && Array.isArray(data) && (data = _eagerDeserializeArray(container, data)), 
    typeId) {
      case 13:
        for (let i = 0; i < data.length; i += 4) {
            const key = deserializeData(container, data[i], data[i + 1]);
            const valType = data[i + 2];
            const valData = data[i + 3];
            0 === valType || valType >= 12 ? Object.defineProperty(target, key, {
                get() {
                    const value = deserializeData(container, valType, valData);
                    return target[key] = value, value;
                },
                set(value) {
                    Object.defineProperty(target, key, {
                        value,
                        writable: !0,
                        enumerable: !0,
                        configurable: !0
                    });
                },
                enumerable: !0,
                configurable: !0
            }) : target[key] = deserializeData(container, valType, valData);
        }
        break;

      case 18:
        inflateQRL(container, target);
        break;

      case 19:
        const task = target;
        const v = data;
        task.$qrl$ = inflateQRL(container, v[0]), task.$flags$ = v[1], task.$index$ = v[2], 
        task.$el$ = v[3], task[_EFFECT_BACK_REF] = v[4], task.$state$ = v[5];
        break;

      case 20:
        const [resolved, result, effects] = data;
        const resource = target;
        resolved ? (resource.value = Promise.resolve(result), resource._resolved = result, 
        resource._state = "resolved") : (resource.value = Promise.reject(result), resource._error = result, 
        resource._state = "rejected"), getStoreHandler(target).$effects$ = effects;
        break;

      case 21:
        target[SERIALIZABLE_STATE][0] = data[0];
        break;

      case 26:
      case 27:
        {
            const [value, flags, effects] = data;
            const store = getOrCreateStore(value, flags, container);
            getStoreHandler(store).$effects$ = effects, target = store;
            break;
        }

      case 22:
        {
            const signal = target;
            const d = data;
            signal.$untrackedValue$ = d[0], signal.$effects$ = new Set(d.slice(1));
            break;
        }

      case 23:
        {
            const signal = target;
            const d = data;
            signal.$func$ = container.getSyncFn(d[0]), signal.$args$ = d[1], signal[_EFFECT_BACK_REF] = d[2], 
            signal.$untrackedValue$ = d[3], signal.$flags$ = d[4], signal.$hostElement$ = d[5], 
            signal.$effects$ = new Set(d.slice(6));
            break;
        }

      case 25:
      case 24:
        {
            const computed = target;
            const d = data;
            computed.$computeQrl$ = d[0], computed.$effects$ = new Set(d[1]);
            d.length > 2 ? (computed.$untrackedValue$ = d[2], 25 === typeId && (computed.$flags$ |= 1)) : (computed.$flags$ |= 1, 
            computed.$computeQrl$.resolve(), container.$scheduler$?.(1, null, computed.$computeQrl$));
            break;
        }

      case 12:
        {
            const d = data;
            target.message = d[0];
            const second = d[1];
            if (second && Array.isArray(second)) {
                for (let i = 0; i < second.length; i++) {
                    target[second[i++]] = second[i];
                }
                target.stack = d[2];
            } else {
                target.stack = second;
            }
            break;
        }

      case 28:
        {
            const formData = target;
            const d = data;
            for (let i = 0; i < d.length; i++) {
                formData.append(d[i++], d[i]);
            }
            break;
        }

      case 29:
        {
            const jsx = target;
            const [type, varProps, constProps, children, flags, key] = data;
            jsx.type = type, jsx.varProps = varProps, jsx.constProps = constProps, jsx.children = children, 
            jsx.flags = flags, jsx.key = key;
            break;
        }

      case 15:
        {
            const set = target;
            const d = data;
            for (let i = 0; i < d.length; i++) {
                set.add(d[i]);
            }
            break;
        }

      case 16:
        {
            const map = target;
            const d = data;
            for (let i = 0; i < d.length; i++) {
                map.set(d[i++], d[i]);
            }
            break;
        }

      case 14:
        {
            const promise = target;
            const [resolved, result] = data;
            const [resolve, reject] = resolvers.get(promise);
            resolved ? resolve(result) : reject(result);
            break;
        }

      case 17:
        const bytes = target;
        const buf = atob(data);
        let i = 0;
        for (const s of buf) {
            bytes[i++] = s.charCodeAt(0);
        }
        break;

      case 30:
        const propsProxy = target;
        propsProxy[_VAR_PROPS] = 0 === data ? {} : data[0], propsProxy[_CONST_PROPS] = data[1];
        break;

      case 31:
        {
            const effectData = target;
            effectData.data.$scopedStyleIdPrefix$ = data[0], effectData.data.$isConst$ = data[1];
            break;
        }

      default:
        throw qError(33, [ typeId ]);
    }
    return target;
};

const _constants = [ void 0, null, !0, !1, "", EMPTY_ARRAY, EMPTY_OBJ, NEEDS_COMPUTATION, STORE_ALL_PROPS, Slot, Fragment, NaN, 1 / 0, -1 / 0, Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER - 1, Number.MIN_SAFE_INTEGER ];

const allocate = (container, typeId, value) => {
    if (void 0 === value) {
        return typeId;
    }
    switch (typeId) {
      case 0:
        return container.$getObjectById$(value);

      case 1:
        return _constants[value];

      case 2:
      case 3:
        return value;

      case 4:
        return wrapDeserializerProxy(container, value);

      case 13:
        return {};

      case 18:
        return parseQRL(container.$getObjectById$(value));

      case 19:
        return new Task(-1, -1, null, null, null, null);

      case 20:
        {
            const res = createResourceReturn(container, void 0, void 0);
            return res.loading = !1, res;
        }

      case 5:
        return new URL(value);

      case 6:
        return new Date(value);

      case 7:
        const idx = value.lastIndexOf("/");
        return new RegExp(value.slice(1, idx), value.slice(idx + 1));

      case 12:
        return new Error;

      case 21:
        return componentQrl(null);

      case 22:
        return new SignalImpl(container, 0);

      case 23:
        return new WrappedSignal(container, null, null, null);

      case 24:
        return new ComputedSignalImpl(container, null);

      case 25:
        return new SerializerSignalImpl(container, null);

      case 26:
      case 27:
        return null;

      case 11:
        return new URLSearchParams(value);

      case 28:
        return new FormData;

      case 29:
        return new JSXNodeImpl(null, null, null, null, -1, null);

      case 10:
        return BigInt(value);

      case 15:
        return new Set;

      case 16:
        return new Map;

      case 14:
        let resolve;
        let reject;
        const promise = new Promise(((res, rej) => {
            resolve = res, reject = rej;
        }));
        return resolvers.set(promise, [ resolve, reject ]), promise.catch((() => {})), promise;

      case 17:
        const encodedLength = value.length;
        const rest = 3 & encodedLength;
        return new Uint8Array(3 * (encodedLength >>> 2) + (rest ? rest - 1 : 0));

      case 30:
        return createPropsProxy(null, null);

      case 8:
        return retrieveVNodeOrDocument(container, value);

      case 9:
        const vNode = retrieveVNodeOrDocument(container, value);
        if (vnode_isVNode(vNode)) {
            return vnode_getNode(vNode);
        }
        throw qError(34, [ typeof vNode ]);

      case 31:
        return new SubscriptionData({});

      default:
        throw qError(35, [ typeId ]);
    }
};

function retrieveVNodeOrDocument(container, value) {
    return value ? container.rootVNode ? vnode_locate(container.rootVNode, value) : void 0 : container.element?.ownerDocument;
}

function parseQRL(qrl) {
    const hashIdx = qrl.indexOf("#");
    const captureStart = qrl.indexOf("[", hashIdx);
    const captureEnd = qrl.indexOf("]", captureStart);
    const chunk = qrl.slice(0, hashIdx > -1 ? hashIdx : captureStart);
    const symbol = captureStart > -1 ? qrl.slice(hashIdx + 1, captureStart) : qrl.slice(hashIdx + 1);
    const captureIds = captureStart > -1 && captureEnd > -1 ? qrl.slice(captureStart + 1, captureEnd).split(" ").filter((v => v.length)).map((s => parseInt(s, 10))) : null;
    let qrlRef = null;
    if (chunk === QRL_RUNTIME_CHUNK) {
        const backChannel = globalThis[QRL_RUNTIME_CHUNK];
        assertDefined(backChannel, "Missing QRL_RUNTIME_CHUNK"), qrlRef = backChannel.get(symbol);
    }
    return createQRL(chunk, symbol, qrlRef, null, captureIds, null);
}

function inflateQRL(container, qrl) {
    const captureIds = qrl.$capture$;
    return qrl.$captureRef$ = captureIds ? captureIds.map((id => container.$getObjectById$(id))) : null, 
    container.element && qrl.$setContainer$(container.element), qrl;
}

let isDomRef = () => !1;

const createSerializationContext = (NodeConstructor, DomRefConstructor, symbolToChunkResolver, getProp, setProp, storeProxyMap, writer, prepVNodeData) => {
    if (!writer) {
        const buffer = [];
        writer = {
            write: text => buffer.push(text),
            toString: () => buffer.join("")
        };
    }
    const map = new Map;
    const syncFnMap = new Map;
    const syncFns = [];
    const roots = [];
    const $wasSeen$ = obj => map.get(obj);
    const $seen$ = obj => map.set(obj, -1);
    const $addRoot$ = obj => {
        let id = map.get(obj);
        return "number" == typeof id && -1 !== id || (id = roots.length, map.set(obj, id), 
        roots.push(obj)), id;
    };
    const isSsrNode = NodeConstructor ? obj => obj instanceof NodeConstructor : () => !1;
    return isDomRef = DomRefConstructor ? obj => obj instanceof DomRefConstructor : () => !1, 
    {
        $serialize$() {
            serialize(this);
        },
        $isSsrNode$: isSsrNode,
        $isDomRef$: isDomRef,
        $symbolToChunkResolver$: symbolToChunkResolver,
        $wasSeen$,
        $roots$: roots,
        $seen$,
        $hasRootId$: obj => {
            const id = map.get(obj);
            return void 0 === id || -1 === id ? void 0 : id;
        },
        $addRoot$,
        $getRootId$: obj => {
            const id = map.get(obj);
            if (!id || -1 === id) {
                throw qError(36, [ obj ]);
            }
            return id;
        },
        $syncFns$: syncFns,
        $addSyncFn$: (funcStr, argCount, fn) => {
            const isFullFn = null == funcStr;
            isFullFn && (funcStr = fn.serialized || fn.toString());
            let id = syncFnMap.get(funcStr);
            if (void 0 === id) {
                if (id = syncFns.length, syncFnMap.set(funcStr, id), isFullFn) {
                    syncFns.push(funcStr);
                } else {
                    let code = "(";
                    for (let i = 0; i < argCount; i++) {
                        code += (0 == i ? "p" : ",p") + i;
                    }
                    syncFns.push(code += ")=>" + funcStr);
                }
            }
            return id;
        },
        $writer$: writer,
        $breakCircularDepsAndAwaitPromises$: async function() {
            const discoveredValues = [];
            const promises = [];
            const visit = obj => {
                if ("function" == typeof obj) {
                    if (isQrl$1(obj)) {
                        obj.$captureRef$ && discoveredValues.push(...obj.$captureRef$);
                    } else if (isQwikComponent(obj)) {
                        const [qrl] = obj[SERIALIZABLE_STATE];
                        discoveredValues.push(qrl);
                    }
                } else if ("object" != typeof obj || null === obj || obj instanceof URL || obj instanceof Date || obj instanceof RegExp || obj instanceof Uint8Array || obj instanceof URLSearchParams || vnode_isVNode(obj) || "undefined" != typeof FormData && obj instanceof FormData || fastSkipSerialize(obj)) {} else if (obj instanceof Error) {
                    discoveredValues.push(...Object.values(obj));
                } else if (isStore(obj)) {
                    const target = getStoreTarget(obj);
                    const effects = getStoreHandler(obj).$effects$;
                    discoveredValues.push(target, effects);
                    for (const prop in target) {
                        const propValue = target[prop];
                        storeProxyMap.has(propValue) && discoveredValues.push(prop, storeProxyMap.get(propValue));
                    }
                } else if (obj instanceof Set) {
                    discoveredValues.push(...obj.values());
                } else if (obj instanceof Map) {
                    obj.forEach(((v, k) => {
                        discoveredValues.push(k, v);
                    }));
                } else if (obj instanceof SignalImpl) {
                    const toSerialize = obj instanceof ComputedSignalImpl && !(obj instanceof SerializerSignalImpl) && (1 & obj.$flags$ || fastSkipSerialize(obj)) ? NEEDS_COMPUTATION : obj.$untrackedValue$;
                    toSerialize !== NEEDS_COMPUTATION && (obj instanceof SerializerSignalImpl ? promises.push(obj.$computeQrl$.resolve().then((arg => {
                        let data;
                        arg.serialize ? data = arg.serialize(toSerialize) : SerializerSymbol in toSerialize && (data = toSerialize[SerializerSymbol](toSerialize)), 
                        void 0 === data && (data = NEEDS_COMPUTATION), serializationResults.set(obj, data), 
                        discoveredValues.push(data);
                    }))) : discoveredValues.push(toSerialize)), obj.$effects$ && discoveredValues.push(obj.$effects$), 
                    obj instanceof WrappedSignal ? (discoverEffectBackRefs(obj[_EFFECT_BACK_REF], discoveredValues), 
                    obj.$args$ && discoveredValues.push(...obj.$args$), obj.$hostElement$ && discoveredValues.push(obj.$hostElement$)) : obj instanceof ComputedSignalImpl && (discoverEffectBackRefs(obj[_EFFECT_BACK_REF], discoveredValues), 
                    discoveredValues.push(obj.$computeQrl$));
                } else if (obj instanceof Task) {
                    discoveredValues.push(obj.$el$, obj.$qrl$, obj.$state$), discoverEffectBackRefs(obj[_EFFECT_BACK_REF], discoveredValues);
                } else if (isSsrNode(obj)) {
                    if (discoverValuesForVNodeData(obj.vnodeData, discoveredValues), obj.childrenVNodeData && obj.childrenVNodeData.length) {
                        for (const data of obj.childrenVNodeData) {
                            discoverValuesForVNodeData(data, discoveredValues);
                        }
                    }
                } else if (isDomRef(obj)) {
                    discoveredValues.push(obj.$ssrNode$.id);
                } else if (isJSXNode(obj)) {
                    discoveredValues.push(obj.type, obj.props, obj.constProps, obj.children);
                } else if (isQrl$1(obj)) {
                    obj.$captureRef$ && obj.$captureRef$.length && discoveredValues.push(...obj.$captureRef$);
                } else if (isPropsProxy(obj)) {
                    discoveredValues.push(obj[_VAR_PROPS], obj[_CONST_PROPS]);
                } else if (isPromise(obj)) {
                    obj.then((value => {
                        promiseResults.set(obj, [ !0, value ]), discoveredValues.push(value);
                    }), (error => {
                        promiseResults.set(obj, [ !1, error ]), discoveredValues.push(error);
                    })), promises.push(obj);
                } else if (obj instanceof SubscriptionData) {
                    discoveredValues.push(obj.data);
                } else if (Array.isArray(obj)) {
                    discoveredValues.push(...obj);
                } else if (isSerializerObj(obj)) {
                    const result = obj[SerializerSymbol](obj);
                    serializationResults.set(obj, result), discoveredValues.push(result);
                } else {
                    if (!isObjectLiteral(obj)) {
                        throw qError(37, [ obj ]);
                    }
                    Object.entries(obj).forEach((([key, value]) => {
                        discoveredValues.push(key, value);
                    }));
                }
            };
            for (const root of roots) {
                visit(root);
            }
            do {
                for (;discoveredValues.length; ) {
                    const obj = discoveredValues.pop();
                    if (!shouldTrackObj(obj) && !frameworkType(obj)) {
                        continue;
                    }
                    const id = $wasSeen$(obj);
                    void 0 === id ? ($seen$(obj), visit(obj)) : -1 === id && $addRoot$(obj);
                }
                await Promise.allSettled(promises), promises.length = 0;
            } while (discoveredValues.length);
        },
        $eventQrls$: new Set,
        $eventNames$: new Set,
        $resources$: new Set,
        $renderSymbols$: new Set,
        $storeProxyMap$: storeProxyMap,
        $getProp$: getProp,
        $setProp$: setProp,
        $prepVNodeData$: prepVNodeData
    };
};

const isSsrAttrs = value => Array.isArray(value) && value.length > 0;

const discoverValuesForVNodeData = (vnodeData, discoveredValues) => {
    for (const value of vnodeData) {
        if (isSsrAttrs(value)) {
            for (let i = 1; i < value.length; i += 2) {
                const attrValue = value[i];
                "string" != typeof attrValue && discoveredValues.push(attrValue);
            }
        }
    }
};

const discoverEffectBackRefs = (effectsBackRefs, discoveredValues) => {
    effectsBackRefs && discoveredValues.push(effectsBackRefs);
};

const promiseResults = new WeakMap;

const serializationResults = new WeakMap;

function serialize(serializationContext) {
    const {$writer$, $isSsrNode$, $isDomRef$, $setProp$, $storeProxyMap$} = serializationContext;
    let depth = -1;
    let writeType = !1;
    const output = (type, value) => {
        if (writeType ? $writer$.write(`${type},`) : writeType = !0, "number" == typeof value) {
            $writer$.write(value.toString());
        } else if ("string" == typeof value) {
            const s = JSON.stringify(value);
            let angleBracketIdx = -1;
            let lastIdx = 0;
            for (;-1 !== (angleBracketIdx = s.indexOf("</", lastIdx)); ) {
                $writer$.write(s.slice(lastIdx, angleBracketIdx)), $writer$.write("<\\/"), lastIdx = angleBracketIdx + 2;
            }
            $writer$.write(0 === lastIdx ? s : s.slice(lastIdx));
        } else {
            depth++, $writer$.write("[");
            let separator = !1;
            for (let i = 0; i < value.length; i++) {
                separator ? $writer$.write(",") : separator = !0, writeValue(value[i], i);
            }
            $writer$.write("]"), depth--;
        }
    };
    const writeValue = (value, idx) => {
        if (fastSkipSerialize(value)) {
            output(1, 0);
        } else if ("bigint" == typeof value) {
            output(10, value.toString());
        } else if ("boolean" == typeof value) {
            output(1, value ? 2 : 3);
        } else if ("function" == typeof value) {
            if (value === Slot) {
                output(1, 9);
            } else if (value === Fragment) {
                output(1, 10);
            } else if (isQrl$1(value)) {
                const qrl = qrlToString(serializationContext, value);
                const id = serializationContext.$addRoot$(qrl);
                output(18, id);
            } else if (isQwikComponent(value)) {
                const [qrl] = value[SERIALIZABLE_STATE];
                serializationContext.$renderSymbols$.add(qrl.$symbol$), output(21, [ qrl ]);
            } else {
                console.error("Cannot serialize function (ignoring for now): " + value.toString()), 
                output(1, 0);
            }
        } else if ("number" == typeof value) {
            Number.isNaN(value) ? output(1, 11) : Number.isFinite(value) ? value === Number.MAX_SAFE_INTEGER ? output(1, 14) : value === Number.MAX_SAFE_INTEGER - 1 ? output(1, 15) : value === Number.MIN_SAFE_INTEGER ? output(1, 16) : output(2, value) : output(1, value < 0 ? 13 : 12);
        } else if ("object" == typeof value) {
            value === EMPTY_ARRAY ? output(1, 5) : value === EMPTY_OBJ ? output(1, 6) : null === value ? output(1, 1) : (depth++, 
            writeObjectValue(value, idx), depth--);
        } else if ("string" == typeof value) {
            if (0 === value.length) {
                output(1, 4);
            } else {
                const seen = depth > 1 && serializationContext.$wasSeen$(value);
                "number" == typeof seen && seen >= 0 ? output(0, seen) : output(3, value);
            }
        } else if (void 0 === value) {
            output(1, 0);
        } else if (value === NEEDS_COMPUTATION) {
            output(1, 7);
        } else {
            if (value !== STORE_ALL_PROPS) {
                throw qError(37, [ typeof value ]);
            }
            output(1, 8);
        }
    };
    const writeObjectValue = (value, idx) => {
        const isRootObject = 2 === depth;
        if (depth > 2) {
            const seen = serializationContext.$wasSeen$(value);
            if ("number" == typeof seen && seen >= 0) {
                return void output(0, seen);
            }
        }
        if (isPropsProxy(value)) {
            const varProps = value[_VAR_PROPS];
            const constProps = value[_CONST_PROPS];
            const out = constProps ? [ varProps, constProps ] : Object.keys(varProps).length ? [ varProps ] : 0;
            output(30, out);
        } else if (value instanceof SubscriptionData) {
            output(31, [ value.data.$scopedStyleIdPrefix$, value.data.$isConst$ ]);
        } else if (isStore(value)) {
            if (isResource(value)) {
                serializationContext.$resources$.add(value);
                const res = promiseResults.get(value.value);
                if (!res) {
                    throw qError(38, [ "resource" ]);
                }
                output(20, [ ...res, getStoreHandler(value).$effects$ ]);
            } else {
                const storeHandler = getStoreHandler(value);
                const storeTarget = getStoreTarget(value);
                const flags = storeHandler.$flags$;
                const effects = storeHandler.$effects$;
                const innerStores = [];
                for (const prop in storeTarget) {
                    const propValue = storeTarget[prop];
                    if ($storeProxyMap$.has(propValue)) {
                        const innerStore = $storeProxyMap$.get(propValue);
                        innerStores.push(innerStore), serializationContext.$addRoot$(innerStore);
                    }
                }
                const out = [ storeTarget, flags, effects, ...innerStores ];
                for (;null == out[out.length - 1]; ) {
                    out.pop();
                }
                output(Array.isArray(storeTarget) ? 27 : 26, out);
            }
        } else if (isSerializerObj(value)) {
            let result = serializationResults.get(value);
            if (isPromise(result)) {
                const promiseResult = promiseResults.get(result);
                if (!promiseResult[0]) {
                    throw console.error(promiseResult[1]), qError(50);
                }
                result = promiseResult[1];
            }
            depth--, writeValue(result, idx), depth++;
        } else if (isObjectLiteral(value)) {
            if (Array.isArray(value)) {
                output(4, value);
            } else {
                const out = [];
                for (const key in value) {
                    Object.prototype.hasOwnProperty.call(value, key) && !fastSkipSerialize(value[key]) && out.push(key, value[key]);
                }
                output(13, out);
            }
        } else if ($isDomRef$(value)) {
            value.$ssrNode$.vnodeData[0] |= 16, output(9, value.$ssrNode$.id);
        } else if (value instanceof SignalImpl) {
            const isSerialized = value instanceof SerializerSignalImpl;
            const v = !isSerialized && value instanceof ComputedSignalImpl && (1 & value.$flags$ || fastSkipSerialize(value.$untrackedValue$)) ? NEEDS_COMPUTATION : value.$untrackedValue$;
            if (value instanceof WrappedSignal) {
                output(23, [ ...serializeWrappingFn(serializationContext, value), filterEffectBackRefs(value[_EFFECT_BACK_REF]), v, value.$flags$, value.$hostElement$, ...value.$effects$ || [] ]);
            } else if (value instanceof ComputedSignalImpl) {
                const out = [ value.$computeQrl$, value.$effects$ ];
                v !== NEEDS_COMPUTATION && out.push(isSerialized ? serializationResults.get(value) : v), 
                output(isSerialized ? 25 : 24, out);
            } else {
                output(22, [ v, ...value.$effects$ || [] ]);
            }
        } else if (value instanceof URL) {
            output(5, value.href);
        } else if (value instanceof Date) {
            output(6, Number.isNaN(value.valueOf()) ? "" : value.valueOf());
        } else if (value instanceof RegExp) {
            output(7, value.toString());
        } else if (value instanceof Error) {
            const out = [ value.message ];
            const extraProps = Object.entries(value).flat();
            extraProps.length && out.push(extraProps), out.push(value.stack), output(12, out);
        } else if ($isSsrNode$(value)) {
            if (isRootObject) {
                $setProp$(value, "q:id", String(idx)), output(8, value.id);
                const vNodeData = value.vnodeData;
                if (vNodeData && (serializationContext.$prepVNodeData$?.(vNodeData), vNodeData[0] |= 16), 
                value.childrenVNodeData) {
                    for (const vNodeData of value.childrenVNodeData) {
                        vNodeData[0] |= 16;
                    }
                }
            } else {
                serializationContext.$addRoot$(value), output(0, serializationContext.$roots$.length - 1);
            }
        } else if ("undefined" != typeof FormData && value instanceof FormData) {
            const array = [];
            value.forEach(((value, key) => {
                array.push(key, "string" == typeof value ? value : value.name);
            })), output(28, array);
        } else if (value instanceof URLSearchParams) {
            output(11, value.toString());
        } else if (value instanceof Set) {
            output(15, [ ...value.values() ]);
        } else if (value instanceof Map) {
            const combined = [];
            for (const [k, v] of value.entries()) {
                combined.push(k, v);
            }
            output(16, combined);
        } else if (isJSXNode(value)) {
            output(29, [ value.type, value.varProps, value.constProps, value.children, value.flags, value.key ]);
        } else if (value instanceof Task) {
            const out = [ value.$qrl$, value.$flags$, value.$index$, value.$el$, value[_EFFECT_BACK_REF], value.$state$ ];
            for (;null == out[out.length - 1]; ) {
                out.pop();
            }
            output(19, out);
        } else if (isPromise(value)) {
            const res = promiseResults.get(value);
            if (!res) {
                throw qError(38, [ "promise" ]);
            }
            output(14, res);
        } else if (value instanceof Uint8Array) {
            let buf = "";
            for (const c of value) {
                buf += String.fromCharCode(c);
            }
            const out = btoa(buf).replace(/=+$/, "");
            output(17, out);
        } else {
            if (!vnode_isVNode(value)) {
                throw qError(37, [ typeof value ]);
            }
            output(1, 0);
        }
    };
    writeValue(serializationContext.$roots$, -1);
}

function filterEffectBackRefs(effectBackRef) {
    let effectBackRefToSerialize = null;
    if (effectBackRef) {
        for (const [effectProp, effect] of effectBackRef) {
            effect[2] && (effectBackRefToSerialize ||= new Map, effectBackRefToSerialize.set(effectProp, effect));
        }
    }
    return effectBackRefToSerialize;
}

function serializeWrappingFn(serializationContext, value) {
    value.$funcStr$ && "{" === value.$funcStr$[0] && (value.$funcStr$ = `(${value.$funcStr$})`);
    return [ serializationContext.$addSyncFn$(value.$funcStr$, value.$args$.length, value.$func$), value.$args$ ];
}

function qrlToString(serializationContext, value) {
    let symbol = value.$symbol$;
    let chunk = value.$chunk$;
    const platform = getPlatform();
    if (platform) {
        const result = platform.chunkForSymbol(symbol, chunk, value.dev?.file);
        result && (chunk = result[1], symbol = result[0]);
    }
    if (isSyncQrl(value)) {
        chunk = "", symbol = String(serializationContext.$addSyncFn$(null, 0, value.resolved));
    } else {
        chunk || (chunk = serializationContext.$symbolToChunkResolver$(value.$hash$));
        {
            let backChannel = globalThis[QRL_RUNTIME_CHUNK];
            backChannel || (backChannel = globalThis[QRL_RUNTIME_CHUNK] = new Map), backChannel.set(value.$symbol$, value._devOnlySymbolRef), 
            chunk || (chunk = QRL_RUNTIME_CHUNK);
        }
        if (!chunk) {
            throw qError(31, [ value.$symbol$ ]);
        }
        chunk.startsWith("./") && (chunk = chunk.slice(2));
    }
    let qrlStringInline = `${chunk}#${symbol}`;
    if (Array.isArray(value.$captureRef$) && value.$captureRef$.length > 0) {
        let serializedReferences = "";
        for (let i = 0; i < value.$captureRef$.length; i++) {
            i > 0 && (serializedReferences += " "), serializedReferences += serializationContext.$addRoot$(value.$captureRef$[i]);
        }
        qrlStringInline += `[${serializedReferences}]`;
    } else {
        value.$capture$ && value.$capture$.length > 0 && (qrlStringInline += `[${value.$capture$.join(" ")}]`);
    }
    return qrlStringInline;
}

async function _serialize(data) {
    const serializationContext = createSerializationContext(null, null, (() => ""), (() => ""), (() => {}), new WeakMap);
    for (const root of data) {
        serializationContext.$addRoot$(root);
    }
    return await serializationContext.$breakCircularDepsAndAwaitPromises$(), serializationContext.$serialize$(), 
    serializationContext.$writer$.toString();
}

function _deserialize(rawStateData, element) {
    if (null == rawStateData) {
        return [];
    }
    const stateData = JSON.parse(rawStateData);
    if (!Array.isArray(stateData)) {
        return [];
    }
    let container;
    container = isNode(element) && isElement$1(element) ? _createDeserializeContainer(stateData, element) : _createDeserializeContainer(stateData);
    const output = [];
    for (let i = 0; i < stateData.length; i += 2) {
        output[i / 2] = deserializeData(container, stateData[i], stateData[i + 1]);
    }
    return output;
}

function deserializeData(container, typeId, value) {
    if (void 0 === typeId) {
        return value;
    }
    let propValue = allocate(container, typeId, value);
    return typeId >= 12 && (propValue = inflate(container, propValue, typeId, value)), 
    propValue;
}

function getObjectById(id, stateData) {
    return "string" == typeof id && (id = parseInt(id, 10)), assertTrue(id < stateData.length, `Invalid reference ${id} >= ${stateData.length}`), 
    stateData[id];
}

function _createDeserializeContainer(stateData, element) {
    let state;
    const container = {
        $getObjectById$: id => getObjectById(id, state),
        getSyncFn: () => () => {},
        $storeProxyMap$: new WeakMap,
        element: null
    };
    return state = wrapDeserializerProxy(container, stateData), container.$state$ = state, 
    element && (container.element = element), container;
}

function shouldTrackObj(obj) {
    return "object" == typeof obj && null !== obj || "string" == typeof obj && obj.length > 1;
}

function isObjectLiteral(obj) {
    const prototype = Object.getPrototypeOf(obj);
    return null == prototype || prototype === Object.prototype || prototype === Array.prototype;
}

function isResource(value) {
    return "__brand" in value && "resource" === value.__brand;
}

const frameworkType = obj => "object" == typeof obj && null !== obj && (obj instanceof SignalImpl || obj instanceof Task || isJSXNode(obj)) || isQrl$1(obj);

const canSerialize = (value, seen = new WeakSet) => {
    if (null == value || "string" == typeof value || "number" == typeof value || "boolean" == typeof value || "bigint" == typeof value) {
        return !0;
    }
    if ("object" == typeof value) {
        if (seen.has(value)) {
            return !0;
        }
        seen.add(value);
        const proto = Object.getPrototypeOf(value);
        if (isStore(value) && (value = getStoreTarget(value)), proto == Object.prototype) {
            for (const key in value) {
                if (!canSerialize(untrack((() => value[key])), seen)) {
                    return !1;
                }
            }
            return !0;
        }
        if (proto == Array.prototype) {
            for (let i = 0; i < value.length; i++) {
                if (!canSerialize(value[i], seen)) {
                    return !1;
                }
            }
            return !0;
        }
        if (isTask(value)) {
            return !0;
        }
        if (isPropsProxy(value)) {
            return !0;
        }
        if (isPromise(value)) {
            return !0;
        }
        if (isJSXNode(value)) {
            return !0;
        }
        if (value instanceof Error) {
            return !0;
        }
        if (value instanceof URL) {
            return !0;
        }
        if (value instanceof Date) {
            return !0;
        }
        if (value instanceof RegExp) {
            return !0;
        }
        if (value instanceof URLSearchParams) {
            return !0;
        }
        if (value instanceof FormData) {
            return !0;
        }
        if (value instanceof Set) {
            return !0;
        }
        if (value instanceof Map) {
            return !0;
        }
        if (value instanceof Uint8Array) {
            return !0;
        }
        if (isDomRef?.(value)) {
            return !0;
        }
    } else if ("function" == typeof value && (isQrl$1(value) || isQwikComponent(value))) {
        return !0;
    }
    return !1;
};

const QRL_RUNTIME_CHUNK = "mock-chunk";

const verifySerializable = (value, preMessage) => {
    const seen = new WeakSet;
    return _verifySerializable(value, seen, "_", preMessage);
};

const _verifySerializable = (value, seen, ctx, preMessage) => {
    const unwrapped = unwrapStore(value);
    if (null == unwrapped) {
        return value;
    }
    if (shouldSerialize(unwrapped)) {
        if ("object" == typeof unwrapped) {
            if (seen.has(unwrapped)) {
                return value;
            }
            seen.add(unwrapped);
        }
        if (isSignal(unwrapped)) {
            return value;
        }
        if (canSerialize(unwrapped)) {
            return value;
        }
        const typeObj = typeof unwrapped;
        switch (typeObj) {
          case "object":
            if (isPromise(unwrapped)) {
                return value;
            }
            if (isNode(unwrapped)) {
                return value;
            }
            if (isArray(unwrapped)) {
                let expectIndex = 0;
                return unwrapped.forEach(((v, i) => {
                    if (i !== expectIndex) {
                        throw qError(3, [ unwrapped ]);
                    }
                    _verifySerializable(v, seen, ctx + "[" + i + "]"), expectIndex = i + 1;
                })), value;
            }
            if (isSerializableObject(unwrapped)) {
                for (const [key, item] of Object.entries(unwrapped)) {
                    _verifySerializable(item, seen, ctx + "." + key);
                }
                return value;
            }
            break;

          case "boolean":
          case "string":
          case "number":
            return value;
        }
        let message;
        if (message = preMessage || "Value cannot be serialized", "_" !== ctx && (message += ` in ${ctx},`), 
        "object" === typeObj) {
            message += ` because it's an instance of "${value?.constructor.name}". You might need to use 'noSerialize()' or use an object literal instead. Check out https://qwik.dev/docs/advanced/dollar/`;
        } else if ("function" === typeObj) {
            const fnName = value.name;
            message += ` because it's a function named "${fnName}". You might need to convert it to a QRL using $(fn):\n\nconst ${fnName} = $(${String(value)});\n\nPlease check out https://qwik.dev/docs/advanced/qrl/ for more information.`;
        }
        throw qError(3, [ message ]);
    }
    return value;
};

const noSerializeSet = /*#__PURE__*/ new WeakSet;

const weakSerializeSet = /*#__PURE__*/ new WeakSet;

const shouldSerialize = obj => !isObject(obj) && !isFunction(obj) || !noSerializeSet.has(obj);

const fastSkipSerialize = obj => "object" == typeof obj && obj && (NoSerializeSymbol in obj || noSerializeSet.has(obj));

const noSerialize = input => (null != input && noSerializeSet.add(input), input);

const _weakSerialize = input => (weakSerializeSet.add(input), input);

const NoSerializeSymbol = Symbol("noSerialize");

const SerializerSymbol = Symbol("serialize");

const createQRL = (chunk, symbol, symbolRef, symbolFn, capture, captureRef) => {
    let _containerEl;
    const qrl = async function(...args) {
        const boundedFn = bindFnToContext.call(this, tryGetInvokeContext());
        return await boundedFn(...args);
    };
    const setContainer = el => (_containerEl || (_containerEl = el), _containerEl);
    function bindFnToContext(currentCtx, beforeFn) {
        const bound = (...args) => {
            if (!qrl.resolved) {
                return retryOnPromise((() => qrl.resolve())).then((fn => {
                    if (!isFunction(fn)) {
                        throw qError(10);
                    }
                    return bound(...args);
                }));
            }
            if (beforeFn && !1 === beforeFn()) {
                return;
            }
            const context = createOrReuseInvocationContext(currentCtx);
            const prevQrl = context.$qrl$;
            const prevEvent = context.$event$;
            context.$qrl$ = qrl, context.$event$ ||= this;
            try {
                return invoke.call(this, context, symbolRef, ...args);
            } finally {
                context.$qrl$ = prevQrl, context.$event$ = prevEvent;
            }
        };
        return bound;
    }
    const wrapFn = fn => "function" != typeof fn || !capture?.length && !captureRef?.length ? fn : function(...args) {
        let context = tryGetInvokeContext();
        if (context) {
            if (context.$qrl$?.$symbol$ === qrl.$symbol$) {
                return fn.apply(this, args);
            }
            const prevQrl = context.$qrl$;
            context.$qrl$ = qrl;
            try {
                return fn.apply(this, args);
            } finally {
                context.$qrl$ = prevQrl;
            }
        }
        return context = newInvokeContext(), context.$qrl$ = qrl, context.$event$ = this, 
        invoke.call(this, context, fn, ...args);
    };
    const resolve = async containerEl => {
        if (null !== symbolRef) {
            return symbolRef;
        }
        if (containerEl && setContainer(containerEl), "" === chunk) {
            assertDefined(_containerEl, "Sync QRL must have container element");
            const hash = _containerEl.getAttribute("q:instance");
            const qFuncs = getQFuncs(_containerEl.ownerDocument, hash);
            return qrl.resolved = symbolRef = qFuncs[Number(symbol)];
        }
        const start = now();
        const ctx = tryGetInvokeContext();
        if (null !== symbolFn) {
            symbolRef = symbolFn().then((module => qrl.resolved = wrapFn(symbolRef = module[symbol])));
        } else {
            const imported = getPlatform().importSymbol(_containerEl, chunk, symbol);
            symbolRef = maybeThen(imported, (ref => qrl.resolved = wrapFn(symbolRef = ref)));
        }
        return "object" == typeof symbolRef && isPromise(symbolRef) && symbolRef.then((() => emitUsedSymbol(symbol, ctx?.$element$, start)), (err => {
            throw console.error(`qrl ${symbol} failed to load`, err), symbolRef = null, err;
        })), symbolRef;
    };
    const createOrReuseInvocationContext = invoke => null == invoke ? newInvokeContext() : isArray(invoke) ? newInvokeContextFromTuple(invoke) : invoke;
    const hash = getSymbolHash(symbol);
    return Object.assign(qrl, {
        getSymbol: () => symbol,
        getHash: () => hash,
        getCaptured: () => captureRef,
        resolve,
        $resolveLazy$: containerEl => null !== symbolRef ? symbolRef : resolve(containerEl),
        $setContainer$: setContainer,
        $chunk$: chunk,
        $symbol$: symbol,
        $hash$: hash,
        getFn: bindFnToContext,
        $capture$: capture,
        $captureRef$: captureRef,
        dev: null,
        resolved: void 0
    }), symbolRef && (symbolRef = maybeThen(symbolRef, (resolved => qrl.resolved = wrapFn(symbolRef = resolved)))), 
    isDev && Object.defineProperty(qrl, "_devOnlySymbolRef", {
        get: () => symbolRef
    }), qrl;
};

const EMITTED = /*#__PURE__*/ new Set;

const emitUsedSymbol = (symbol, element, reqTime) => {
    EMITTED.has(symbol) || (EMITTED.add(symbol), emitEvent("qsymbol", {
        symbol,
        element,
        reqTime
    }));
};

const emitEvent = (eventName, detail) => {
    isServerPlatform() || "object" != typeof document || document.dispatchEvent(new CustomEvent(eventName, {
        bubbles: !1,
        detail
    }));
};

const now = () => isServerPlatform() ? 0 : "object" == typeof performance ? performance.now() : 0;

let runtimeSymbolId = 0;

const $ = expression => createQRL(null, "s" + runtimeSymbolId++, expression, null, null, null);

const dollar = $;

const eventQrl = qrl => qrl;

const sync$ = fn => createQRL("", "<sync>", fn, null, null, null);

const _qrlSync = function(fn, serializedFn) {
    return void 0 === serializedFn && (serializedFn = fn.toString()), fn.serialized = serializedFn, 
    createQRL("", "<sync>", fn, null, null, null);
};

const componentQrl = componentQrl => {
    function QwikComponent(props, key, flags = 0) {
        assertQrl(componentQrl), assertNumber(flags, "The Qwik Component was not invoked correctly");
        const finalKey = componentQrl.$hash$.slice(0, 4) + ":" + (key || "");
        const InnerCmp = () => {};
        return InnerCmp[SERIALIZABLE_STATE] = [ componentQrl ], _jsxSplit(InnerCmp, props, null, props.children, flags, finalKey);
    }
    return QwikComponent[SERIALIZABLE_STATE] = [ componentQrl ], QwikComponent;
};

const SERIALIZABLE_STATE = Symbol("serializable-data");

const isQwikComponent = component => "function" == typeof component && void 0 !== component[SERIALIZABLE_STATE];

const component$ = onMount => componentQrl(dollar(onMount));

const event$ = implicit$FirstArg(eventQrl);

const render = async (parent, jsxNode, opts = {}) => {
    if (isDocument(parent)) {
        let child = parent.firstChild;
        for (;child && !isElement$1(child); ) {
            child = child.nextSibling;
        }
        parent = child;
    }
    parent.setAttribute("q:container", "resumed");
    const container = getDomContainer(parent);
    container.$serverData$ = opts.serverData || {};
    const host = container.rootVNode;
    return container.$scheduler$(4, host, host, jsxNode), await container.$scheduler$(255), 
    {
        cleanup: () => {
            cleanup(container, container.rootVNode);
        }
    };
};

const useStore = (initialState, opts) => {
    const {val, set, iCtx} = useSequentialScope();
    if (null != val) {
        return val;
    }
    const value = isFunction(initialState) ? invoke(void 0, initialState) : initialState;
    if (!1 === opts?.reactive) {
        return set(value), value;
    }
    {
        const containerState = iCtx.$container$;
        const newStore = getOrCreateStore(value, opts?.deep ?? !0 ? 1 : 0, containerState);
        return set(newStore), newStore;
    }
};

const intToStr = nu => nu.toString(36);

const getNextUniqueIndex = container => intToStr(container.$currentUniqueId$++);

const useId = () => {
    const {val, set, iCtx} = useSequentialScope();
    if (null != val) {
        return val;
    }
    const containerBase = iCtx.$container$.$buildBase$ || "";
    const base = containerBase ? hashCode(containerBase).substring(0, 3) : "";
    const componentQrl = iCtx.$container$.getHostProp(iCtx.$hostElement$, "q:renderFn");
    let id = `${base}${componentQrl?.getHash().substring(0, 3) || ""}${getNextUniqueIndex(iCtx.$container$) || ""}`;
    let firstChar = id.charCodeAt(0);
    return firstChar >= 48 && firstChar <= 57 && (firstChar += 17, id = String.fromCharCode(firstChar) + id.substring(1)), 
    set(id);
};

function useServerData(key, defaultValue) {
    const ctx = tryGetInvokeContext();
    return ctx?.$container$?.$serverData$[key] ?? defaultValue;
}

const STYLE_CACHE = /*#__PURE__*/ new Map;

const getScopedStyles = (css, scopeId) => {
    let styleCss = STYLE_CACHE.get(scopeId);
    return styleCss || STYLE_CACHE.set(scopeId, styleCss = scopeStylesheet(css, scopeId)), 
    styleCss;
};

const scopeStylesheet = (css, scopeId) => {
    const end = css.length;
    const out = [];
    const stack = [];
    let idx = 0;
    let lastIdx = idx;
    let mode = rule;
    let lastCh = 0;
    for (;idx < end; ) {
        const chIdx = idx;
        let ch = css.charCodeAt(idx++);
        ch === BACKSLASH && (idx++, ch = A);
        const arcs = STATE_MACHINE[mode];
        for (let i = 0; i < arcs.length; i++) {
            const arc = arcs[i];
            const [expectLastCh, expectCh, newMode] = arc;
            if ((expectLastCh === lastCh || expectLastCh === ANY || expectLastCh === IDENT && isIdent(lastCh) || expectLastCh === WHITESPACE && isWhiteSpace(lastCh)) && (expectCh === ch || expectCh === ANY || expectCh === IDENT && isIdent(ch) || expectCh === NOT_IDENT && !isIdent(ch) && ch !== DOT || expectCh === WHITESPACE && isWhiteSpace(ch)) && (3 == arc.length || lookAhead(arc))) {
                if (arc.length > 3 && (ch = css.charCodeAt(idx - 1)), newMode === EXIT || newMode == EXIT_INSERT_SCOPE) {
                    if (newMode === EXIT_INSERT_SCOPE) {
                        if (mode !== starSelector || shouldNotInsertScoping()) {
                            if (!isChainedSelector(ch)) {
                                insertScopingSelector(idx - (expectCh == NOT_IDENT ? 1 : expectCh == CLOSE_PARENTHESIS ? 2 : 0));
                            }
                        } else {
                            isChainedSelector(ch) ? flush(idx - 2) : insertScopingSelector(idx - 2), lastIdx++;
                        }
                    }
                    expectCh === NOT_IDENT && (idx--, ch = lastCh);
                    do {
                        mode = stack.pop() || rule, mode === pseudoGlobal && (flush(idx - 1), lastIdx++);
                    } while (isSelfClosingRule(mode));
                } else {
                    stack.push(mode), mode === pseudoGlobal && newMode === rule ? (flush(idx - 8), lastIdx = idx) : newMode === pseudoElement && insertScopingSelector(chIdx), 
                    mode = newMode;
                }
                break;
            }
        }
        lastCh = ch;
    }
    return flush(idx), out.join("");
    function flush(idx) {
        out.push(css.substring(lastIdx, idx)), lastIdx = idx;
    }
    function insertScopingSelector(idx) {
        mode === pseudoGlobal || shouldNotInsertScoping() || (flush(idx), out.push(".", "⚡️", scopeId));
    }
    function lookAhead(arc) {
        let prefix = 0;
        if (css.charCodeAt(idx) === DASH) {
            for (let i = 1; i < 10; i++) {
                if (css.charCodeAt(idx + i) === DASH) {
                    prefix = i + 1;
                    break;
                }
            }
        }
        words: for (let arcIndx = 3; arcIndx < arc.length; arcIndx++) {
            const txt = arc[arcIndx];
            for (let i = 0; i < txt.length; i++) {
                if ((css.charCodeAt(idx + i + prefix) | LOWERCASE) !== txt.charCodeAt(i)) {
                    continue words;
                }
            }
            return idx += txt.length + prefix, !0;
        }
        return !1;
    }
    function shouldNotInsertScoping() {
        return -1 !== stack.indexOf(pseudoGlobal) || -1 !== stack.indexOf(atRuleSelector);
    }
};

const isIdent = ch => ch >= _0 && ch <= _9 || ch >= A && ch <= Z || ch >= a && ch <= z || ch >= 128 || ch === UNDERSCORE || ch === DASH;

const isChainedSelector = ch => ch === COLON || ch === DOT || ch === OPEN_BRACKET || ch === HASH || isIdent(ch);

const isSelfClosingRule = mode => mode === atRuleBlock || mode === atRuleSelector || mode === atRuleInert || mode === pseudoGlobal;

const isWhiteSpace = ch => ch === SPACE || ch === TAB || ch === NEWLINE || ch === CARRIAGE_RETURN;

const rule = 0;

const elementClassIdSelector = 1;

const starSelector = 2;

const pseudoClassWithSelector = 3;

const pseudoClass = 4;

const pseudoGlobal = 5;

const pseudoElement = 6;

const attrSelector = 7;

const inertParenthesis = 8;

const inertBlock = 9;

const atRuleSelector = 10;

const atRuleBlock = 11;

const atRuleInert = 12;

const body = 13;

const stringSingle = 14;

const stringDouble = 15;

const commentMultiline = 16;

const EXIT = 17;

const EXIT_INSERT_SCOPE = 18;

const ANY = 0;

const IDENT = 1;

const NOT_IDENT = 2;

const WHITESPACE = 3;

const TAB = 9;

const NEWLINE = 10;

const CARRIAGE_RETURN = 13;

const SPACE = 32;

const DOUBLE_QUOTE = 34;

const HASH = 35;

const SINGLE_QUOTE = 39;

const OPEN_PARENTHESIS = 40;

const CLOSE_PARENTHESIS = 41;

const STAR = 42;

const DASH = 45;

const DOT = 46;

const FORWARD_SLASH = 47;

const _0 = 48;

const _9 = 57;

const COLON = 58;

const SEMICOLON = 59;

const AT = 64;

const A = 65;

const Z = 90;

const OPEN_BRACKET = 91;

const CLOSE_BRACKET = 93;

const BACKSLASH = 92;

const UNDERSCORE = 95;

const LOWERCASE = 32;

const a = 97;

const z = 122;

const OPEN_BRACE = 123;

const CLOSE_BRACE = 125;

const STRINGS_COMMENTS = [ [ ANY, 39, 14 ], [ ANY, 34, 15 ], [ ANY, 47, 16, "*" ] ];

const STATE_MACHINE = [ [ [ ANY, 42, starSelector ], [ ANY, OPEN_BRACKET, 7 ], [ ANY, COLON, pseudoElement, ":", "before", "after", "first-letter", "first-line" ], [ ANY, COLON, pseudoGlobal, "global" ], [ ANY, COLON, 3, "has", "host-context", "not", "where", "is", "matches", "any" ], [ ANY, COLON, 4 ], [ ANY, IDENT, 1 ], [ ANY, DOT, 1 ], [ ANY, HASH, 1 ], [ ANY, 64, atRuleSelector, "keyframe" ], [ ANY, 64, atRuleBlock, "media", "supports", "container" ], [ ANY, 64, atRuleInert ], [ ANY, 123, 13 ], [ 47, 42, 16 ], [ ANY, 59, EXIT ], [ ANY, 125, EXIT ], [ ANY, CLOSE_PARENTHESIS, EXIT ], ...STRINGS_COMMENTS ], [ [ ANY, NOT_IDENT, EXIT_INSERT_SCOPE ] ], [ [ ANY, NOT_IDENT, EXIT_INSERT_SCOPE ] ], [ [ ANY, 40, rule ], [ ANY, NOT_IDENT, EXIT_INSERT_SCOPE ] ], [ [ ANY, 40, 8 ], [ ANY, NOT_IDENT, EXIT_INSERT_SCOPE ] ], [ [ ANY, 40, rule ], [ ANY, NOT_IDENT, EXIT ] ], [ [ ANY, NOT_IDENT, EXIT ] ], [ [ ANY, 93, EXIT_INSERT_SCOPE ], [ ANY, 39, 14 ], [ ANY, 34, 15 ] ], [ [ ANY, CLOSE_PARENTHESIS, EXIT ], ...STRINGS_COMMENTS ], [ [ ANY, 125, EXIT ], ...STRINGS_COMMENTS ], [ [ ANY, 125, EXIT ], [ WHITESPACE, IDENT, 1 ], [ ANY, COLON, pseudoGlobal, "global" ], [ ANY, 123, 13 ], ...STRINGS_COMMENTS ], [ [ ANY, 123, rule ], [ ANY, 59, EXIT ], ...STRINGS_COMMENTS ], [ [ ANY, 59, EXIT ], [ ANY, 123, 9 ], ...STRINGS_COMMENTS ], [ [ ANY, 125, EXIT ], [ ANY, 123, 13 ], [ ANY, 40, 8 ], ...STRINGS_COMMENTS ], [ [ ANY, 39, EXIT ] ], [ [ ANY, 34, EXIT ] ], [ [ 42, 47, EXIT ] ] ];

const useStylesQrl = styles => ({
    styleId: _useStyles(styles, (str => str), !1)
});

const useStyles$ = /*#__PURE__*/ implicit$FirstArg(useStylesQrl);

const useStylesScopedQrl = styles => ({
    scopeId: "⚡️" + _useStyles(styles, getScopedStyles, !0)
});

const useStylesScoped$ = /*#__PURE__*/ implicit$FirstArg(useStylesScopedQrl);

const _useStyles = (styleQrl, transform, scoped) => {
    assertQrl(styleQrl);
    const {val, set, iCtx, i} = useSequentialScope();
    if (val) {
        return val;
    }
    const styleId = styleKey(styleQrl, i);
    const host = iCtx.$hostElement$;
    set(styleId);
    const value = styleQrl.$resolveLazy$(iCtx.$element$);
    if (isPromise(value)) {
        throw value.then((val => iCtx.$container$.$appendStyle$(transform(val, styleId), styleId, host, scoped))), 
        value;
    }
    return iCtx.$container$.$appendStyle$(transform(value, styleId), styleId, host, scoped), 
    styleId;
};

const useOn = (event, eventQrl) => {
    _useOn(createEventName(event, "on"), eventQrl);
};

const useOnDocument = (event, eventQrl) => {
    _useOn(createEventName(event, "document:on"), eventQrl);
};

const useOnWindow = (event, eventQrl) => {
    _useOn(createEventName(event, "window:on"), eventQrl);
};

const createEventName = (event, eventScope) => {
    const map = name => {
        let prefix = eventScope;
        return "DOMContentLoaded" === name && (prefix += "-"), eventNameToJsxEvent(name, prefix);
    };
    return Array.isArray(event) ? event.map(map) : map(event);
};

const _useOn = (eventName, eventQrl) => {
    const {isAdded, addEvent} = useOnEventsSequentialScope();
    isAdded || eventQrl && (Array.isArray(eventName) ? eventName.forEach((event => addEvent(event, eventQrl))) : addEvent(eventName, eventQrl));
};

const useOnEventsSequentialScope = () => {
    const iCtx = useInvokeContext();
    const host = iCtx.$hostElement$;
    let onMap = iCtx.$container$.getHostProp(host, ":on");
    null === onMap && (onMap = {}, iCtx.$container$.setHostProp(host, ":on", onMap));
    let seqIdx = iCtx.$container$.getHostProp(host, ":onIdx");
    null === seqIdx && (seqIdx = 0), iCtx.$container$.setHostProp(host, ":onIdx", seqIdx + 1);
    let addedFlags = iCtx.$container$.getHostProp(host, ":onFlags");
    for (null === addedFlags && (addedFlags = [], iCtx.$container$.setHostProp(host, ":onFlags", addedFlags)); addedFlags.length <= seqIdx; ) {
        addedFlags.push(!1);
    }
    return {
        isAdded: addedFlags[seqIdx],
        addEvent: (eventName, eventQrl) => {
            addedFlags[seqIdx] = !0;
            let events = onMap[eventName];
            events || (onMap[eventName] = events = []), events.push(eventQrl);
        }
    };
};

const useSignal = initialState => useConstant((() => {
    const value = isFunction(initialState) && !isQwikComponent(initialState) ? invoke(void 0, initialState) : initialState;
    return createSignal(value);
}));

const useConstant = value => {
    const {val, set} = useSequentialScope();
    return null != val ? val : set(value = isFunction(value) && !isQwikComponent(value) ? value() : value);
};

const useComputedCommon = (qrl, Class) => {
    const {val, set} = useSequentialScope();
    if (val) {
        return val;
    }
    assertQrl(qrl);
    const signal = new Class(null, qrl);
    return set(signal), throwIfQRLNotResolved(qrl), signal;
};

const useComputedQrl = qrl => useComputedCommon(qrl, ComputedSignalImpl);

const useComputed$ = implicit$FirstArg(useComputedQrl);

const useSerializerQrl = qrl => useComputedCommon(qrl, SerializerSignalImpl);

const useSerializer$ = implicit$FirstArg(useSerializerQrl);

const useVisibleTaskQrl = (qrl, opts) => {
    const {val, set, i, iCtx} = useSequentialScope();
    const eagerness = opts?.strategy ?? "intersection-observer";
    if (val) {
        return void (isServerPlatform() && useRunTask(val, eagerness));
    }
    assertQrl(qrl);
    const task = new Task(1, i, iCtx.$hostElement$, qrl, void 0, null);
    set(task), useRunTask(task, eagerness), isServerPlatform() || (qrl.$resolveLazy$(iCtx.$element$), 
    iCtx.$container$.$scheduler$(32, task));
};

const useRunTask = (task, eagerness) => {
    "intersection-observer" === eagerness ? useOn("qvisible", getTaskHandlerQrl(task)) : "document-ready" === eagerness ? useOnDocument("qinit", getTaskHandlerQrl(task)) : "document-idle" === eagerness && useOnDocument("qidle", getTaskHandlerQrl(task));
};

const getTaskHandlerQrl = task => createQRL(null, "_task", scheduleTask, null, null, [ task ]);

const useResource$ = (generatorFn, opts) => useResourceQrl(dollar(generatorFn), opts);

const useTask$ = /*#__PURE__*/ implicit$FirstArg(useTaskQrl);

const useVisibleTask$ = /*#__PURE__*/ implicit$FirstArg(useVisibleTaskQrl);

const useErrorBoundary = () => {
    const error = useStore({
        error: void 0
    });
    return useContextProvider(ERROR_CONTEXT, error), error;
};

const PrefetchServiceWorker = opts => {
    if (isDev && !import.meta.env.TEST) {
        return _jsxSorted("script", null, {
            dangerouslySetInnerHTML: "\x3c!-- PrefetchServiceWorker is disabled in dev mode. --\x3e"
        }, null, 0, "prefetch-service-worker");
    }
    const serverData = useServerData("containerAttributes", {});
    const baseUrl = import.meta.env.BASE_URL || "/";
    const resolvedOpts = {
        base: serverData["q:base"],
        manifestHash: serverData["q:manifest-hash"],
        scope: "/",
        verbose: !1,
        path: "qwik-prefetch-service-worker.js",
        ...opts
    };
    resolvedOpts.path = opts?.path?.startsWith?.("/") ? opts.path : baseUrl + resolvedOpts.path;
    let code = PREFETCH_CODE.replace("URL", resolvedOpts.path).replace("SCOPE", resolvedOpts.scope);
    isDev || (code = code.replaceAll(/\s+/gm, ""));
    const props = {
        dangerouslySetInnerHTML: [ "(" + code + ")(", [ JSON.stringify(resolvedOpts.base), JSON.stringify(resolvedOpts.manifestHash), "navigator.serviceWorker", "window.qwikPrefetchSW||(window.qwikPrefetchSW=[])", resolvedOpts.verbose ].join(","), ");" ].join(""),
        nonce: resolvedOpts.nonce
    };
    return _jsxSorted("script", null, props, null, 0, "prefetch-service-worker");
};

const PREFETCH_CODE = /*#__PURE__*/ ((b, h, c, q, v) => {
    c.register("URL", {
        scope: "SCOPE"
    }).then(((sw, onReady) => {
        onReady = () => q.forEach(q.push = v => sw.active.postMessage(v)), sw.installing ? sw.installing.addEventListener("statechange", (e => "activated" == e.target.state && onReady())) : onReady();
    })), v && q.push([ "verbose" ]), document.addEventListener("qprefetch", (e => e.detail.bundles && q.push([ "prefetch", b, ...e.detail.bundles ])));
}).toString();

const PrefetchGraph = (opts = {}) => {
    if (isDev && !import.meta.env.TEST) {
        return _jsxSorted("script", null, {
            dangerouslySetInnerHTML: "\x3c!-- PrefetchGraph is disabled in dev mode. --\x3e"
        }, null, 0, "prefetch-graph");
    }
    const serverData = useServerData("containerAttributes", {});
    const resolvedOpts = {
        base: serverData["q:base"],
        manifestHash: serverData["q:manifest-hash"],
        scope: "/",
        verbose: !1,
        path: "qwik-prefetch-service-worker.js",
        ...opts
    };
    const args = JSON.stringify([ "graph-url", resolvedOpts.base, `q-bundle-graph-${resolvedOpts.manifestHash}.json` ]);
    return _jsxSorted("script", null, {
        dangerouslySetInnerHTML: `(window.qwikPrefetchSW||(window.qwikPrefetchSW=[])).push(${args})`,
        nonce: opts.nonce
    }, null, 0, "prefetch-graph");
};

export { $, Fragment, NoSerializeSymbol, PrefetchGraph, PrefetchServiceWorker, RenderOnce, Resource, SSRComment, SSRRaw, SSRStream, SSRStreamBlock, SerializerSymbol, SkipRender, Slot, _CONST_PROPS, DomContainer as _DomContainer, _EFFECT_BACK_REF, EMPTY_ARRAY as _EMPTY_ARRAY, SubscriptionData as _EffectData, _IMMUTABLE, _SharedContainer, _VAR_PROPS, _deserialize, _fnSignal, _getContextElement, _getContextEvent, getDomContainer as _getDomContainer, _getQContainerElement, isJSXNode as _isJSXNode, isStringifiable as _isStringifiable, _jsxBranch, _jsxC, _jsxQ, _jsxS, _jsxSorted, _jsxSplit, _noopQrl, _noopQrlDEV, _qrlSync, _regSymbol, _restProps, queueQRL as _run, _serialize, scheduleTask as _task, verifySerializable as _verifySerializable, _waitUntilRendered, _walkJSX, _weakSerialize, _wrapProp, _wrapSignal, _wrapStore, component$, componentQrl, createComputed$, createComputedSignal as createComputedQrl, createContextId, h as createElement, createSerializer$, createSerializerSignal as createSerializerQrl, createSignal, event$, eventQrl, getDomContainer, getLocale, getPlatform, h, implicit$FirstArg, inlinedQrl, inlinedQrlDEV, isSignal, jsx, jsxDEV, jsx as jsxs, noSerialize, qrl, qrlDEV, render, setPlatform, sync$, untrack, unwrapStore, useComputed$, useComputedQrl, useConstant, useContext, useContextProvider, useErrorBoundary, useId, useLexicalScope, useOn, useOnDocument, useOnWindow, useResource$, useResourceQrl, useSerializer$, useSerializerQrl, useServerData, useSignal, useStore, useStyles$, useStylesQrl, useStylesScoped$, useStylesScopedQrl, useTask$, useTaskQrl, useVisibleTask$, useVisibleTaskQrl, version, withLocale };
